import React, { useState, useEffect, createContext, useContext, useCallback, useRef, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { 
  Home, Users, Briefcase, Clock, FileText, Bell, Settings, LogOut, Menu, X,
  ChevronRight, ChevronDown, Plus, CheckCircle, AlertCircle, MapPin, Calendar, User,
  ClipboardList, BarChart3, Play, Square, RefreshCw, Search, Filter, Eye,
  Truck, Camera, Upload, Image, Wrench, Shield, FileImage, Trash2, Save,
  Building2, Sliders, Key, MessageSquare, Send, Car, Fuel, Video, HelpCircle, 
  ChevronLeft, Download, Edit3, Check, ArrowLeft, Edit2, UserCheck, Archive, Receipt, BookOpen, CalendarDays, Box, Sparkles
} from "lucide-react";
import PDFSignatureFieldPlacer from "./components/PDFSignatureFieldPlacer";
import PDFDocumentSigner from "./components/PDFDocumentSigner";
import { toast, Toaster } from "sonner";
import DashboardPage from "./pages/DashboardPage";
import SettingsPage from "./pages/SettingsPage";
import MessagesPage from "./pages/MessagesPage";
import InvoicingPage from "./pages/InvoicingPage";
import NotificationsPage from "./pages/NotificationsPage";
import RecordsPage from "./pages/RecordsPage";
import TimePage from "./pages/TimePage";
import MachinesPage from "./pages/MachinesPage";
import InventoryPage from "./pages/InventoryPage";
import TutorialsModal from "./components/TutorialsModal";
import CompaniesPage from "./pages/CompaniesPage";
import SuperAdminPage from "./pages/SuperAdminPage";
import AssistantPage from "./pages/AssistantPage";
import TutorialsAdminPage from "./pages/TutorialsAdminPage";
import QRCodesPage from "./pages/QRCodesPage";
import QRResolverPage from "./pages/QRResolverPage";
import WorkerTutorialsPage from "./pages/WorkerTutorialsPage";
import ClientPreviewPage from "./pages/ClientPreviewPage";
import ShiftProductionPage from "./pages/ShiftProductionPage";
import ShiftEquipmentPage from "./pages/ShiftEquipmentPage";
import FuelPodsPage from "./pages/FuelPodsPage";
import IncidentLogPage from "./pages/IncidentLogPage";
import PricingPage from "./pages/PricingPage";
import BuildWorkflowPage from "./pages/BuildWorkflowPage";
import { CompanyAIAssistantBubble } from "./components/CompanyAIAssistant";
import LoginTutorialsModal from "./components/LoginTutorialsModal";
import EmployeeRecordsPage from "./pages/EmployeeRecordsPage";
import MyRecordsPage from "./pages/MyRecordsPage";
import AnalyticsPage from "./pages/AnalyticsPage";
import TimesheetsPage from "./pages/TimesheetsPage";
import JobRecordsPanel from "./components/jobs/JobRecordsPanel";
import JobUploadsSection from "./components/jobs/JobUploadsSection";
import FormSignaturePad from "./components/FormSignaturePad";
import { AcknowledgementsTab, AcknowledgeSignModal } from "./components/forms/Acknowledgements";
import SiteCrewPanel from "./components/SiteCrewPanel";
import DocumentsView from "./components/jobs/DocumentsView";
import EquipmentScheduleSection from "./components/jobs/EquipmentScheduleSection";
import SWMSSignView from "./components/jobs/SWMSSignView";
import SWMSManageView from "./components/jobs/SWMSManageView";
import DailyRosterSection from "./components/jobs/DailyRosterSection";
import SigningDocumentsView from "./components/jobs/SigningDocumentsView";
import JobModal from "./components/jobs/JobModal";
import EmployeeWeeklySummaryModal from "./components/EmployeeWeeklySummaryModal";
import MissedSignoffsPromptModal from "./components/compliance/MissedSignoffsPromptModal";
import { useGeofenceAutoPrompt } from "./hooks/useGeofenceAutoPrompt";
import { DatePicker, DateTimePicker } from "./components/DateTimePicker";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ==================== CONTEXT ====================
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [currentCompany, setCurrentCompany] = useState(null);

  // Bulletproof: set X-Company-Id on EVERY axios request from localStorage,
  // so timing of state updates never matters. This is the safety net that
  // prevents the "empty dropdown until refresh" race condition forever.
  useEffect(() => {
    const interceptorId = axios.interceptors.request.use((config) => {
      const cid = localStorage.getItem("currentCompanyId");
      if (cid && !config.headers["X-Company-Id"]) {
        config.headers["X-Company-Id"] = cid;
      }
      return config;
    });
    return () => axios.interceptors.request.eject(interceptorId);
  }, []);

  // Set X-Company-Id IMMEDIATELY from localStorage so the first fetches after
  // a refresh have the right header (prevents empty dropdown race condition).
  useEffect(() => {
    const savedCompanyId = localStorage.getItem("currentCompanyId");
    if (savedCompanyId) {
      axios.defaults.headers.common["X-Company-Id"] = savedCompanyId;
    }
  }, []);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const res = await axios.get(`${API}/auth/me`);
      setUser(res.data);
      // Wait for companies to load BEFORE we drop the loading flag, so child
      // components don't fire data fetches without X-Company-Id set.
      await fetchCompanies();
    } catch (e) {
      logout();
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const res = await axios.get(`${API}/companies`);
      setCompanies(res.data);

      // Super admins manage all companies — don't auto-pick one.
      // They explicitly opt-in via "View Profile" which sets currentCompanyId in localStorage.
      const me = await axios.get(`${API}/auth/me`).then(r => r.data).catch(() => null);
      if (me?.role === "super_admin") {
        const savedCompanyId = localStorage.getItem("currentCompanyId");
        if (savedCompanyId) {
          const c = res.data.find(x => x.id === savedCompanyId);
          if (c) {
            setCurrentCompany(c);
            axios.defaults.headers.common["X-Company-Id"] = c.id;
          } else {
            localStorage.removeItem("currentCompanyId");
            delete axios.defaults.headers.common["X-Company-Id"];
          }
        }
        return;
      }

      // Set primary company or first company as current
      const primaryCompany = res.data.find(c => c.is_primary) || res.data[0];
      if (primaryCompany && !currentCompany) {
        setCurrentCompany(primaryCompany);
        localStorage.setItem("currentCompanyId", primaryCompany.id);
        axios.defaults.headers.common["X-Company-Id"] = primaryCompany.id;
      } else if (!primaryCompany) {
        const savedCompanyId = localStorage.getItem("currentCompanyId");
        if (savedCompanyId) {
          const savedCompany = res.data.find(c => c.id === savedCompanyId);
          if (savedCompany) {
            setCurrentCompany(savedCompany);
            axios.defaults.headers.common["X-Company-Id"] = savedCompanyId;
          }
        }
      }
    } catch (e) {
      console.error("Failed to fetch companies:", e);
    }
  };

  const switchCompany = async (companyId) => {
    try {
      const res = await axios.post(`${API}/companies/${companyId}/switch`);
      setCurrentCompany(res.data.company);
      localStorage.setItem("currentCompanyId", companyId);
      axios.defaults.headers.common["X-Company-Id"] = companyId;
      return res.data;
    } catch (e) {
      throw e;
    }
  };

  const login = async (email, password, companyCode) => {
    const res = await axios.post(`${API}/auth/login`, { email, password, company_code: companyCode || null });
    const { token: newToken, user: userData, company: loginCompany } = res.data;
    localStorage.setItem("token", newToken);
    axios.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    if (loginCompany) {
      setCurrentCompany(loginCompany);
      localStorage.setItem("currentCompanyId", loginCompany.id);
      axios.defaults.headers.common["X-Company-Id"] = loginCompany.id;
    }
    return userData;
  };

  const register = async (data) => {
    const res = await axios.post(`${API}/auth/register`, data);
    const { token: newToken, user: userData } = res.data;
    localStorage.setItem("token", newToken);
    axios.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("currentCompanyId");
    delete axios.defaults.headers.common["Authorization"];
    setToken(null);
    setUser(null);
    setCompanies([]);
    setCurrentCompany(null);
  };

  return (
    <AuthContext.Provider value={{ 
      user, token, login, register, logout, loading,
      companies, currentCompany, switchCompany, fetchCompanies 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

// ==================== COMPONENTS ====================

// Button Component
const Button = ({ children, variant = "primary", size = "default", className = "", disabled, ...props }) => {
  const baseClasses = "inline-flex items-center justify-center font-bold uppercase tracking-wide transition-all duration-200 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation select-none";
  
  const variants = {
    primary: "bg-orange-500 text-slate-900 hover:bg-orange-600 active:bg-orange-700",
    secondary: "bg-slate-900 text-white hover:bg-slate-800 active:bg-slate-700",
    outline: "border-2 border-slate-300 bg-transparent hover:bg-slate-100 active:bg-slate-200 text-slate-900",
    ghost: "hover:bg-slate-100 active:bg-slate-200 text-slate-700 hover:text-slate-900",
    destructive: "bg-red-600 text-white hover:bg-red-700 active:bg-red-800"
  };

  const sizes = {
    sm: "h-10 px-4 text-sm rounded-lg",
    default: "h-12 px-6 rounded-lg",
    lg: "h-14 px-8 text-lg rounded-lg",
    icon: "h-12 w-12 rounded-lg"
  };

  return (
    <button 
      className={`${baseClasses} ${variants[variant]} ${sizes[size]} ${className}`}
      disabled={disabled}
      {...props}
    >
      {children}
    </button>
  );
};

// Input Component
const Input = ({ label, error, className = "", testId, ...props }) => (
  <div className="flex flex-col gap-1.5">
    {label && <label className="text-sm font-bold uppercase text-slate-600">{label}</label>}
    <input
      className={`h-12 bg-white border-2 border-slate-200 focus:border-orange-500 focus:outline-none rounded-md px-4 text-base font-medium placeholder:text-slate-400 ${error ? 'border-red-500' : ''} ${className}`}
      data-testid={testId}
      {...props}
    />
    {error && <span className="text-sm text-red-600">{error}</span>}
  </div>
);

// Select Component
const Select = ({ label, options, error, className = "", testId, ...props }) => (
  <div className="flex flex-col gap-1.5">
    {label && <label className="text-sm font-bold uppercase text-slate-600">{label}</label>}
    <select
      className={`h-12 bg-white border-2 border-slate-200 focus:border-orange-500 focus:outline-none rounded-lg px-4 text-base font-medium touch-manipulation ${error ? 'border-red-500' : ''} ${className}`}
      style={{ fontSize: '16px' }} /* Prevents iOS zoom on focus */
      data-testid={testId}
      {...props}
    >
      {options.map(opt => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
    {error && <span className="text-sm text-red-600">{error}</span>}
  </div>
);

// Textarea Component
const Textarea = ({ label, error, className = "", testId, ...props }) => (
  <div className="flex flex-col gap-1.5">
    {label && <label className="text-sm font-bold uppercase text-slate-600">{label}</label>}
    <textarea
      className={`min-h-[100px] bg-white border-2 border-slate-200 focus:border-orange-500 focus:outline-none rounded-md p-4 text-base font-medium placeholder:text-slate-400 resize-y ${error ? 'border-red-500' : ''} ${className}`}
      data-testid={testId}
      {...props}
    />
    {error && <span className="text-sm text-red-600">{error}</span>}
  </div>
);

// Card Component
const Card = ({ children, className = "", interactive = false, ...props }) => (
  <div 
    className={`bg-white border-2 border-slate-200 rounded-lg shadow-none p-6 ${interactive ? 'hover:border-orange-500/50 transition-colors cursor-pointer' : ''} ${className}`}
    {...props}
  >
    {children}
  </div>
);

// Badge Component
const Badge = ({ children, variant = "default", className = "", ...rest }) => {
  const variants = {
    default: "bg-slate-200 text-slate-800",
    success: "bg-green-600 text-white",
    warning: "bg-amber-500 text-white",
    error: "bg-red-600 text-white",
    info: "bg-blue-600 text-white",
    orange: "bg-orange-500 text-slate-900"
  };

  return (
    <span {...rest} className={`inline-flex items-center px-3 py-1 rounded-md text-sm font-bold uppercase tracking-wide ${variants[variant]} ${className}`}>
      {children}
    </span>
  );
};

// Modal Component
const Modal = ({ isOpen, onClose, title, children, size = "default" }) => {
  if (!isOpen) return null;

  const sizes = {
    sm: "max-w-md",
    default: "max-w-lg",
    lg: "max-w-2xl",
    xl: "max-w-4xl",
    full: "max-w-6xl"
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/50 overflow-y-auto" 
      onClick={onClose}
    >
      <div 
        className={`w-full ${sizes[size]} bg-white rounded-t-2xl sm:rounded-lg border-2 border-slate-200 shadow-xl animate-fade-in max-h-[95vh] sm:max-h-[90vh] overflow-y-auto`}
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 sm:p-6 border-b-2 border-slate-200 sticky top-0 bg-white z-10">
          <h2 className="text-xl sm:text-2xl font-bold uppercase tracking-tight font-heading">{title}</h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-full transition-colors touch-manipulation">
            <X className="w-6 h-6" />
          </button>
        </div>
        <div className="p-4 sm:p-6 pb-8">{children}</div>
      </div>
    </div>
  );
};

// Tutorials Modal Component
// TutorialsModal extracted to /components/TutorialsModal.jsx

// Loading Spinner
const Spinner = ({ size = "default" }) => {
  const sizes = { sm: "w-4 h-4", default: "w-8 h-8", lg: "w-12 h-12" };
  return (
    <div className={`${sizes[size]} border-4 border-slate-200 border-t-orange-500 rounded-full animate-spin`} />
  );
};

// Helper to open/download files (handles base64 data URLs and regular URLs)
const openFile = (fileUrl, fileName = 'document') => {
  if (!fileUrl) {
    alert('No file available');
    return;
  }
  
  // If it's a base64 data URL, create a download link
  if (fileUrl.startsWith('data:')) {
    try {
      // Create a temporary anchor element to trigger download
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = fileName || 'document';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Error downloading file:', err);
      // Fallback: try opening in new window
      const newWindow = window.open('', '_blank');
      if (newWindow) {
        newWindow.document.write(`<iframe src="${fileUrl}" style="width:100%;height:100%;border:none;"></iframe>`);
      } else {
        alert('Please allow popups to view this document');
      }
    }
  } else {
    // Regular URL - just open it
    window.open(fileUrl, '_blank');
  }
};

// File Upload Component - Direct upload from device
const FileUploadInput = ({ label, accept = "*", onFileSelect, currentFile, onClear }) => {
  const [uploading, setUploading] = useState(false);
  
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async (event) => {
        const base64Data = event.target.result;
        try {
          const res = await axios.post(`${API}/upload/file`, {
            file_data: base64Data,
            file_name: file.name,
            file_type: file.type
          });
          onFileSelect(res.data.file_url, file.name);
        } catch (err) {
          alert("Failed to upload file. Please try again.");
        } finally {
          setUploading(false);
        }
      };
      reader.readAsDataURL(file);
    } catch (err) {
      setUploading(false);
      alert("Error reading file");
    }
  };
  
  return (
    <div className="flex flex-col gap-1.5">
      {label && <label className="text-sm font-bold uppercase text-slate-600">{label}</label>}
      {currentFile ? (
        <div className="flex items-center gap-3 p-3 bg-green-50 border-2 border-green-200 rounded-lg">
          <CheckCircle className="w-5 h-5 text-green-600" />
          <span className="text-sm text-green-700 flex-1 truncate">{currentFile}</span>
          <Button variant="ghost" size="sm" onClick={onClear}>
            <X className="w-4 h-4" />
          </Button>
        </div>
      ) : (
        <div className="relative">
          <input
            type="file"
            accept={accept}
            onChange={handleFileChange}
            disabled={uploading}
            className="block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-orange-50 file:text-orange-700 hover:file:bg-orange-100 disabled:opacity-50"
          />
          {uploading && (
            <div className="absolute inset-0 flex items-center justify-center bg-white/80 rounded-lg">
              <Spinner size="sm" />
              <span className="ml-2 text-sm text-slate-600">Uploading...</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ==================== LAYOUT COMPONENTS ====================

// Sidebar for Desktop
const Sidebar = ({ navItems, currentPath }) => {
  const { user, logout, companies, currentCompany, switchCompany } = useAuth();
  const navigate = useNavigate();
  const [showCompanyMenu, setShowCompanyMenu] = useState(false);

  const handleSwitchCompany = async (companyId) => {
    try {
      await switchCompany(companyId);
      setShowCompanyMenu(false);
      window.location.reload(); // Refresh to load new company data
    } catch (e) {
      console.error("Failed to switch company:", e);
    }
  };

  return (
    <aside className="bg-slate-900 text-white w-64 hidden md:flex flex-col h-screen fixed left-0 top-0 border-r border-slate-800">
      {/* Logo */}
      <div className="p-6 border-b border-slate-800">
        <h1 className="text-2xl font-bold uppercase tracking-tight font-heading text-orange-500">
          ShadowOps
        </h1>
        <p className="text-sm text-slate-400 mt-1">Construction Management</p>
      </div>

      {/* Company Switcher */}
      {companies.length > 0 && (
        <div className="p-4 border-b border-slate-800 relative">
          <button
            onClick={() => setShowCompanyMenu(!showCompanyMenu)}
            className="w-full flex items-center justify-between px-3 py-2 bg-slate-800 hover:bg-slate-700 rounded-md transition-colors"
            data-testid="company-switcher"
          >
            <div className="flex items-center gap-2 overflow-hidden">
              <Building2 className="w-4 h-4 text-orange-400 flex-shrink-0" />
              <span className="text-sm font-medium truncate">
                {currentCompany?.name || "Select Company"}
              </span>
            </div>
            <ChevronRight className={`w-4 h-4 transition-transform ${showCompanyMenu ? 'rotate-90' : ''}`} />
          </button>
          
          {showCompanyMenu && (
            <div className="absolute left-4 right-4 top-full mt-1 bg-slate-800 border border-slate-700 rounded-md shadow-lg z-50 max-h-60 overflow-y-auto">
              {companies.map(company => (
                <button
                  key={company.id}
                  onClick={() => handleSwitchCompany(company.id)}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-slate-700 transition-colors ${
                    currentCompany?.id === company.id ? 'bg-orange-500/20 text-orange-400' : 'text-slate-300'
                  }`}
                >
                  {currentCompany?.id === company.id && <CheckCircle className="w-4 h-4" />}
                  <span className="truncate">{company.name}</span>
                </button>
              ))}
              {(user?.role === 'admin' || user?.role === 'manager') && (
                <button
                  onClick={() => { setShowCompanyMenu(false); navigate('/companies'); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm text-orange-400 hover:bg-slate-700 border-t border-slate-700"
                >
                  <Plus className="w-4 h-4" />
                  Manage Companies
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* User Info */}
      <div className="p-4 border-b border-slate-800">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-orange-500 rounded-md flex items-center justify-center text-slate-900 font-bold">
            {user?.first_name?.[0]}{user?.last_name?.[0]}
          </div>
          <div>
            <p className="font-semibold">{user?.first_name} {user?.last_name}</p>
            <p className="text-xs text-slate-400 uppercase">{user?.role?.replace('_', ' ')}</p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {navItems.map(item => (
          <button
            key={item.path}
            onClick={() => navigate(item.path)}
            data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-md transition-all duration-200 ${
              currentPath === item.path 
                ? 'bg-orange-500 text-slate-900 font-bold' 
                : 'text-slate-300 hover:bg-slate-800 hover:text-white'
            }`}
          >
            <item.icon className="w-5 h-5" />
            <span className="font-medium">{item.label}</span>
          </button>
        ))}
      </nav>

      {/* Tutorials Button */}
      <div className="p-4 border-t border-slate-800">
        <button
          onClick={() => {
            const event = new CustomEvent('openTutorials');
            window.dispatchEvent(event);
          }}
          data-testid="sidebar-tutorials-btn"
          className="w-full flex items-center gap-3 px-4 py-3 rounded-md text-slate-300 hover:bg-orange-500/20 hover:text-orange-400 transition-colors"
        >
          <Video className="w-5 h-5" />
          <span className="font-medium">Tutorials</span>
        </button>
      </div>

      {/* Logout */}
      <div className="p-4 border-t border-slate-800">
        <button
          onClick={logout}
          data-testid="logout-btn"
          className="w-full flex items-center gap-3 px-4 py-3 rounded-md text-slate-300 hover:bg-red-600 hover:text-white transition-colors"
        >
          <LogOut className="w-5 h-5" />
          <span className="font-medium">Logout</span>
        </button>
      </div>
    </aside>
  );
};

// Mobile Bottom Navigation
const MobileNav = ({ navItems, currentPath }) => {
  const navigate = useNavigate();
  const [showFullNav, setShowFullNav] = useState(false);

  // Fixed bottom nav items - prioritized for workers
  // Removed quick clock button - users must go to Time page to clock in properly
  const bottomNavItems = [
    { path: '/time', label: 'Time', icon: Clock },
    { path: '/jobs', label: 'Jobs', icon: Briefcase },
    { path: '/timesheets', label: 'Timesheets', icon: ClipboardList },
    { path: '/messages', label: 'Messages', icon: MessageSquare },
    { path: 'menu', label: 'More', icon: Menu, isAction: true }
  ];

  return (
    <>
      <nav className="bg-white border-t-2 border-slate-200 fixed bottom-0 left-0 right-0 h-20 flex items-center justify-around z-30 md:hidden pb-safe shadow-lg">
        {bottomNavItems.map(item => (
          <button
            key={item.path}
            onClick={() => {
              if (item.path === 'menu') {
                setShowFullNav(true);
              } else {
                navigate(item.path);
              }
            }}
            data-testid={`mobile-nav-${item.label.toLowerCase().replace(' ', '-')}`}
            className={`flex flex-col items-center justify-center flex-1 h-full transition-colors ${
              item.isAction 
                ? item.color || 'text-slate-600'
                : currentPath === item.path 
                  ? 'text-orange-500' 
                  : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            <div className={`w-12 h-12 flex items-center justify-center rounded-xl transition-all ${
              currentPath === item.path 
                ? 'bg-orange-100' 
                : ''
            }`}>
              <item.icon className="w-6 h-6" />
            </div>
            <span className="text-xs font-semibold mt-1">{item.label}</span>
          </button>
        ))}
      </nav>

      {/* Full Navigation Drawer */}
      {showFullNav && (
        <div className="fixed inset-0 bg-black/50 z-50 md:hidden" onClick={() => setShowFullNav(false)}>
          <div 
            className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl max-h-[70vh] overflow-y-auto animate-slide-up"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b border-slate-200 sticky top-0 bg-white">
              <div className="w-12 h-1 bg-slate-300 rounded-full mx-auto mb-4" />
              <h3 className="text-lg font-bold uppercase text-center">Navigation</h3>
            </div>
            <div className="grid grid-cols-3 gap-2 p-4">
              {navItems.map(item => (
                <button
                  key={item.path}
                  onClick={() => {
                    navigate(item.path);
                    setShowFullNav(false);
                  }}
                  className={`flex flex-col items-center justify-center p-4 rounded-xl transition-all ${
                    currentPath === item.path 
                      ? 'bg-orange-100 text-orange-600' 
                      : 'bg-slate-50 text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  <item.icon className="w-6 h-6 mb-2" />
                  <span className="text-xs font-semibold">{item.label}</span>
                </button>
              ))}
            </div>
            <div className="p-4 border-t border-slate-200">
              <Button 
                variant="ghost" 
                className="w-full" 
                onClick={() => setShowFullNav(false)}
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Mobile Header
const MobileHeader = ({ title, onMenuClick }) => {
  const { user, logout } = useAuth();
  const [showMenu, setShowMenu] = useState(false);

  return (
    <header className="h-16 bg-white border-b-2 border-slate-200 flex items-center justify-between px-4 sticky top-0 z-40 md:hidden">
      <div className="flex items-center gap-3">
        <h1 className="text-xl font-bold uppercase tracking-tight font-heading text-orange-500">
          ShadowOps
        </h1>
      </div>
      <div className="flex items-center gap-2">
        <NotificationBell />
        <button 
          onClick={() => setShowMenu(!showMenu)}
          className="w-10 h-10 bg-orange-500 rounded-md flex items-center justify-center text-slate-900 font-bold"
        >
          {user?.first_name?.[0]}{user?.last_name?.[0]}
        </button>
      </div>
      
      {showMenu && (
        <div className="absolute top-full right-4 mt-2 bg-white border-2 border-slate-200 rounded-lg shadow-lg p-2 min-w-[200px]">
          <div className="px-4 py-2 border-b border-slate-100">
            <p className="font-semibold">{user?.first_name} {user?.last_name}</p>
            <p className="text-xs text-slate-500 uppercase">{user?.role?.replace('_', ' ')}</p>
          </div>
          <button
            onClick={logout}
            className="w-full flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-md mt-1"
          >
            <LogOut className="w-4 h-4" />
            Logout
          </button>
        </div>
      )}
    </header>
  );
};

// Notification Bell
const NotificationBell = () => {
  const { user } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [pendingAcksCount, setPendingAcksCount] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) return; // Don't fetch if not logged in
    fetchUnreadCount();
    fetchPendingAcks();
    const interval = setInterval(() => { fetchUnreadCount(); fetchPendingAcks(); }, 30000);
    
    // For admin/manager/office_staff, also check for long shifts periodically
    if (['admin', 'manager', 'office_staff'].includes(user?.role)) {
      checkLongShifts();
      const longShiftInterval = setInterval(checkLongShifts, 300000); // Check every 5 minutes
      return () => {
        clearInterval(interval);
        clearInterval(longShiftInterval);
      };
    }
    
    return () => clearInterval(interval);
  }, [user]);

  const fetchUnreadCount = async () => {
    try {
      const res = await axios.get(`${API}/notifications/unread-count`);
      setUnreadCount(res.data.count || 0);
    } catch (e) {
      // Silently fail - user might have logged out
    }
  };

  const fetchPendingAcks = async () => {
    try {
      const res = await axios.get(`${API}/forms/pending-acknowledgements/count`);
      setPendingAcksCount(res.data.count || 0);
    } catch (e) { /* silent */ }
  };

  const checkLongShifts = async () => {
    try {
      // This triggers notifications on the backend for long-running shifts
      await axios.get(`${API}/time/check-long-shifts`);
    } catch (e) {
      // Silently fail
    }
  };

  const totalCount = unreadCount + pendingAcksCount;
  const tooltip = pendingAcksCount > 0
    ? `${unreadCount} alert${unreadCount === 1 ? '' : 's'} · ${pendingAcksCount} form${pendingAcksCount === 1 ? '' : 's'} to sign`
    : `${unreadCount} unread alert${unreadCount === 1 ? '' : 's'}`;

  return (
    <button 
      onClick={() => {
        // If only acks are outstanding (no notifications), jump straight to forms
        if (pendingAcksCount > 0 && unreadCount === 0) {
          navigate('/forms?tab=acknowledgements');
        } else {
          navigate('/notifications');
        }
      }}
      data-testid="notification-bell"
      title={tooltip}
      className="relative p-2 hover:bg-slate-100 rounded-md transition-colors"
    >
      <Bell className="w-6 h-6 text-slate-700" />
      {totalCount > 0 && (
        <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 bg-red-600 text-white text-xs font-bold rounded-full flex items-center justify-center"
              data-testid="bell-badge">
          {totalCount > 9 ? '9+' : totalCount}
        </span>
      )}
      {/* Distinct amber sub-badge for ack-only state to draw the eye */}
      {pendingAcksCount > 0 && unreadCount === 0 && (
        <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-amber-500 rounded-full ring-2 ring-white" />
      )}
    </button>
  );
};

// Main Layout
const ImpersonationBanner = () => {
  const { user, currentCompany } = useAuth();
  if (user?.role !== "super_admin" || !currentCompany) return null;
  const handleReturn = () => {
    localStorage.removeItem("currentCompanyId");
    delete axios.defaults.headers.common["X-Company-Id"];
    window.location.href = "/super-admin";
  };
  return (
    <div className="bg-orange-500 text-white px-4 py-2 text-sm font-semibold flex items-center justify-between flex-wrap gap-2 sticky top-0 z-50" data-testid="impersonation-banner">
      <span className="uppercase tracking-wide">
        ⚠ Viewing <span className="font-bold underline">{currentCompany.name} ({currentCompany.code})</span> as System Admin
      </span>
      <button
        onClick={handleReturn}
        className="px-3 py-1 bg-white text-orange-600 rounded font-bold hover:bg-orange-50"
        data-testid="return-to-super-admin"
      >
        ← Back to Companies
      </button>
    </div>
  );
};

const Layout = ({ children }) => {
  const { user, currentCompany } = useAuth();
  const location = useLocation();
  const [showTutorials, setShowTutorials] = useState(false);

  // Listen for tutorials open event from sidebar
  useEffect(() => {
    const handleOpenTutorials = () => setShowTutorials(true);
    window.addEventListener('openTutorials', handleOpenTutorials);
    return () => window.removeEventListener('openTutorials', handleOpenTutorials);
  }, []);

  // Helper — is this feature enabled for the current company?
  // When super_admin is viewing a company, gate by that company's features
  // (so the live profile matches what the company actually sees).
  const featureEnabled = (key) => {
    if (!key) return true;
    const enabled = currentCompany?.enabled_features;
    if (!enabled || enabled.length === 0) {
      // No company in context (super admin landing page, or legacy)
      if (user?.role === 'super_admin') return true;
      return true; // legacy fallback for users without enabled_features set
    }
    return enabled.includes(key);
  };

  const getNavItems = () => {
    const baseItems = [
      { path: '/dashboard', label: 'Dashboard', icon: Home }, // always on
      { path: '/jobs', label: 'Jobs', icon: Briefcase, feature: 'jobs' },
      { path: '/time', label: 'Time', icon: Clock, feature: 'clock_on' },
      { path: '/timesheets', label: 'Timesheets', icon: ClipboardList, feature: 'timesheets' },
      { path: '/machines', label: 'Machines', icon: Truck, feature: 'machines' },
      { path: '/inventory', label: 'Inventory', icon: Box, feature: 'inventory' },
      { path: '/hire-out', label: 'Hire Out', icon: Car, feature: 'dry_hires' },
      { path: '/daybook', label: 'Daybook', icon: ClipboardList, feature: 'daybook' },
    ];

    // Admin/Manager/Office get Clients (under jobs feature)
    if (['admin', 'manager', 'office_staff'].includes(user?.role)) {
      baseItems.push({ path: '/clients', label: 'Clients', icon: Building2, feature: 'jobs' });
      baseItems.push({ path: '/qr-codes', label: 'QR Codes', icon: ClipboardList });
    }

    // Invoicing — admin + office only
    if (['admin', 'office_staff'].includes(user?.role)) {
      baseItems.push({ path: '/invoicing', label: 'Invoicing', icon: FileText, feature: 'invoicing' });
    }

    // Team visible to all (admin edits, workers view)
    baseItems.push({ path: '/employees', label: 'Team', icon: Users, feature: 'teams' });
    baseItems.push({ path: '/forms', label: 'Forms', icon: FileText, feature: 'forms_library' });

    // MCS custom tabs
    baseItems.push({ path: '/shift-production', label: 'Shift Production', icon: ClipboardList, feature: 'shift_production' });
    baseItems.push({ path: '/shift-equipment', label: 'Multi-Machine', icon: Truck, feature: 'shift_equipment' });
    baseItems.push({ path: '/fuel-pods', label: 'Fuel Pods', icon: Fuel, feature: 'fuel_pods' });
    baseItems.push({ path: '/incident-log', label: 'Incidents', icon: Shield, feature: 'incident_log' });

    // Analytics - consolidated (admin/manager/office/supervisor)
    if (['admin', 'manager', 'office_staff', 'supervisor'].includes(user?.role)) {
      baseItems.push({ path: '/analytics', label: 'Analytics', icon: BarChart3, feature: 'analytics' });
      baseItems.push({ path: '/records', label: 'Records', icon: Archive, feature: 'analytics' });
    }

    // Per-employee records hub (admin/manager/office_staff only)
    if (['admin', 'manager', 'office_staff'].includes(user?.role)) {
      baseItems.push({ path: '/employee-records', label: 'Employee Records', icon: ClipboardList, feature: 'teams' });
    }

    baseItems.push({ path: '/my-records', label: 'My Records', icon: User }); // always on
    baseItems.push({ path: '/tutorials', label: 'Tutorials', icon: BookOpen }); // always on per request
    baseItems.push({ path: '/messages', label: 'Messages', icon: MessageSquare }); // always on
    baseItems.push({ path: '/notifications', label: 'Alerts', icon: Bell }); // always on

    // Admin & Manager
    if (user?.role === 'admin' || user?.role === 'manager') {
      baseItems.push({ path: '/settings', label: 'Settings', icon: Settings }); // always on
    }

    // Super admin gets its own management tab when not viewing a company
    if (user?.role === 'super_admin' && !currentCompany) {
      return [
        { path: '/super-admin', label: 'Companies', icon: Building2 },
      ];
    }

    // Super admin viewing a specific company: prepend a "Back to Companies" item + add Assistant
    if (user?.role === 'super_admin' && currentCompany) {
      const filtered = baseItems.filter(item => featureEnabled(item.feature));
      return [
        { path: '/super-admin', label: '← Companies', icon: Building2 },
        { path: '/assistant', label: 'AI Assistant', icon: Sparkles },
        { path: '/tutorials-admin', label: 'Tutorials', icon: BookOpen },
        { path: '/qr-codes', label: 'QR Codes', icon: ClipboardList },
        ...filtered,
      ];
    }

    return baseItems.filter(item => featureEnabled(item.feature));
  };

  const navItems = getNavItems();

  return (
    <div className="min-h-screen bg-slate-50">
      <ImpersonationBanner />
      <Sidebar navItems={navItems} currentPath={location.pathname} />
      <MobileHeader title="ShadowOps" />
      
      <main className="md:ml-64 min-h-screen pb-32 md:pb-0">
        <div className="hidden md:flex h-16 bg-white border-b-2 border-slate-200 items-center justify-between px-8 sticky top-0 z-40">
          <h2 className="text-xl font-bold uppercase tracking-tight font-heading">
            {navItems.find(i => i.path === location.pathname)?.label || 'ShadowOps'}
          </h2>
          <div className="flex items-center gap-4">
            <NotificationBell />
          </div>
        </div>
        <div className="p-4 md:p-8">
          {children}
        </div>
      </main>

      <MobileNav navItems={navItems} currentPath={location.pathname} />
      
      {/* Tutorials Modal */}
      <TutorialsModal isOpen={showTutorials} onClose={() => setShowTutorials(false)} />

      {/* Floating AI Assistant bubble — only for super_admin viewing a company */}
      {user?.role === 'super_admin' && currentCompany && (
        <CompanyAIAssistantBubble company={currentCompany} />
      )}
    </div>
  );
};

// ==================== PAGES ====================

// Login Page
const LoginPage = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [companyCode, setCompanyCode] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showTutorials, setShowTutorials] = useState(false);
  const [isSystemAdmin, setIsSystemAdmin] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const userData = await login(email, password, isSystemAdmin ? null : companyCode);
      // After login, honour ?next= for QR deep-links
      const params = new URLSearchParams(window.location.search);
      const next = params.get("next") || sessionStorage.getItem("postLoginRedirect");
      if (next) {
        sessionStorage.removeItem("postLoginRedirect");
        navigate(next, { replace: true });
      } else if (userData?.role === "super_admin") {
        navigate("/super-admin");
      } else {
        navigate("/dashboard");
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (Array.isArray(detail)) {
        setError(detail.map(d => d?.msg || String(d)).join(", "));
      } else if (typeof detail === "string") {
        setError(detail);
      } else {
        setError("Login failed");
      }
    } finally {
      setLoading(false);
    }
  };



  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold uppercase tracking-tight font-heading text-orange-500">
            ShadowOps
          </h1>
          <p className="text-slate-400 mt-2">Construction Business Management</p>
        </div>

        <Card className="animate-fade-in">
          <form onSubmit={handleSubmit} className="space-y-6">
            <h2 className="text-2xl font-bold uppercase tracking-tight font-heading text-center">
              Sign In
            </h2>

            {error && (
              <div className="p-4 bg-red-50 border-2 border-red-200 rounded-md text-red-700 flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                {error}
              </div>
            )}

            {!isSystemAdmin && (
              <Input
                label="Company ID"
                type="text"
                value={companyCode}
                onChange={(e) => setCompanyCode(e.target.value.toUpperCase())}
                placeholder="e.g. MIRRI"
                required
                data-testid="login-company-code"
              />
            )}

            <Input
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              required
              data-testid="login-email"
            />

            <Input
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              required
              data-testid="login-password"
            />

            <Button 
              type="submit" 
              className="w-full" 
              disabled={loading}
              data-testid="login-submit"
            >
              {loading ? <Spinner size="sm" /> : "Sign In"}
            </Button>

            <button
              type="button"
              onClick={() => { setIsSystemAdmin(!isSystemAdmin); setError(""); }}
              className="w-full text-center text-xs text-slate-500 hover:text-orange-500 underline mt-2"
              data-testid="toggle-system-admin-login"
            >
              {isSystemAdmin ? "← Company login" : "System Admin login →"}
            </button>
          </form>

          <div className="mt-6 pt-6 border-t-2 border-slate-100 space-y-3">
            {/* Watch Tutorials Button */}
            <button 
              type="button"
              onClick={() => setShowTutorials(true)}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-md font-semibold transition-colors"
              data-testid="watch-tutorials-btn"
            >
              <Video className="w-5 h-5" />
              Watch Tutorials
            </button>
            <p className="text-xs text-slate-500 text-center">
              New here? Learn how to use ShadowOps
            </p>

            <a
              href="/pricing"
              className="block text-center text-xs font-semibold uppercase tracking-wide text-slate-700 hover:text-orange-600 underline pt-2"
              data-testid="login-view-pricing-link"
            >
              View pricing & plans →
            </a>
          </div>
        </Card>
      </div>
      
      {/* Tutorials Modal */}
      {showTutorials && <LoginTutorialsModal onClose={() => setShowTutorials(false)} />}
    </div>
  );
};

// DashboardPage extracted to pages/DashboardPage.jsx


// Jobs grouped by client view (used inside JobsPage)
const JobsByClientGrouped = ({ jobs, expanded, setExpanded, onSelect, statusColors }) => {
  // Group jobs by client_name (fallback to "(No client)")
  const groups = {};
  jobs.forEach(j => {
    const key = j.client_name || '(No client)';
    if (!groups[key]) groups[key] = { client_name: key, items: [], total_contract: 0, active: 0, completed: 0 };
    groups[key].items.push(j);
    groups[key].total_contract += Number(j.contract_amount) || 0;
    if (j.status === 'completed') groups[key].completed += 1;
    else if (j.status !== 'cancelled') groups[key].active += 1;
  });
  const sorted = Object.values(groups).sort((a, b) => a.client_name.localeCompare(b.client_name));

  if (sorted.length === 0) {
    return (
      <div className="text-center py-12 text-slate-500">
        <Briefcase className="w-12 h-12 mx-auto mb-4 text-slate-300" />
        <p className="text-lg font-semibold">No jobs found</p>
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="jobs-by-client-grouped">
      {sorted.map(g => {
        const isOpen = expanded[g.client_name] !== false;
        return (
          <Card key={g.client_name} className="p-0 overflow-hidden" data-testid={`jobs-client-${g.client_name}`}>
            <button
              onClick={() => setExpanded(e => ({ ...e, [g.client_name]: !isOpen }))}
              className="w-full bg-slate-100 px-4 py-3 flex items-center justify-between border-b hover:bg-slate-200"
            >
              <div className="flex items-center gap-2 flex-wrap">
                {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                <User className="w-4 h-4 text-slate-500" />
                <h3 className="font-bold uppercase">{g.client_name}</h3>
                <Badge variant="info">{g.items.length} job(s)</Badge>
                {g.active > 0 && <Badge variant="warning">{g.active} active</Badge>}
                {g.completed > 0 && <Badge variant="success">{g.completed} done</Badge>}
              </div>
              {g.total_contract > 0 && (
                <span className="text-sm font-bold text-green-600">
                  ${g.total_contract.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              )}
            </button>
            {isOpen && (
              <div className="divide-y divide-slate-100">
                {g.items.map(j => (
                  <button
                    key={j.id}
                    onClick={() => onSelect(j)}
                    className="w-full text-left p-3 hover:bg-slate-50 flex items-center gap-3"
                    data-testid={`grouped-job-${j.id}`}
                  >
                    <Briefcase className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold truncate">{j.title}</span>
                        <Badge variant={statusColors[j.status] || 'default'}>{(j.status || '').replace('_', ' ')}</Badge>
                      </div>
                      <div className="flex gap-3 text-xs text-slate-500 mt-0.5 flex-wrap">
                        {j.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{j.location}</span>}
                        {j.start_date && <span>Start: {j.start_date}</span>}
                        {j.end_date && <span>End: {j.end_date}</span>}
                        {j.manager_name && <span className="text-orange-600 font-semibold">{j.manager_name}</span>}
                      </div>
                    </div>
                    {j.contract_amount ? (
                      <span className="text-sm font-bold text-green-600">
                        ${Number(j.contract_amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    ) : null}
                    <ChevronRight className="w-4 h-4 text-slate-400" />
                  </button>
                ))}
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
};

// Jobs Page
const JobsPage = () => {
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [filter, setFilter] = useState("all");
  const [viewMode, setViewMode] = useState("list"); // 'list' | 'by-client'
  const [expandedClients, setExpandedClients] = useState({});

  const canCreateJob = ['admin', 'manager', 'office_staff'].includes(user?.role);
  const canEditJob = ['admin', 'manager', 'office_staff'].includes(user?.role);
  const isWorkerOrSupervisor = ['supervisor', 'crane_operator', 'rigger', 'labourer'].includes(user?.role);

  useEffect(() => {
    fetchJobs();
  }, []);

  // Auto-open job modal when /jobs?open={jobId} is visited (from dashboard deep link)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const openId = params.get('open');
    if (openId && jobs.length > 0) {
      const target = jobs.find(j => j.id === openId);
      if (target) {
        setSelectedJob(target);
        setShowModal(true);
      }
    }
  }, [jobs]);

  const fetchJobs = async () => {
    try {
      const res = await axios.get(`${API}/jobs`);
      setJobs(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const filteredJobs = jobs.filter(job => {
    // Workers and supervisors can't see completed/cancelled jobs
    if (isWorkerOrSupervisor && ['completed', 'cancelled'].includes(job.status)) {
      return false;
    }
    if (filter === "all") return true;
    return job.status === filter;
  });

  // Filter tabs - workers/supervisors don't see completed/cancelled tabs
  const availableFilters = isWorkerOrSupervisor 
    ? ["all", "scheduled", "in_progress"]
    : ["all", "scheduled", "in_progress", "completed", "cancelled"];

  const statusColors = {
    scheduled: "info",
    in_progress: "warning",
    completed: "success",
    cancelled: "error"
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="jobs-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold uppercase tracking-tight font-heading">Jobs</h1>
          <p className="text-slate-600">{filteredJobs.length} job(s) found</p>
        </div>
        {canCreateJob && (
          <Button onClick={() => { setSelectedJob(null); setShowModal(true); }} data-testid="create-job-btn">
            <Plus className="w-5 h-5 mr-2" />
            New Job
          </Button>
        )}
      </div>

      {/* View toggle + Filter */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        <div className="flex gap-1 bg-slate-100 rounded-md p-1 w-fit" data-testid="jobs-view-toggle">
          <button
            onClick={() => setViewMode('list')}
            className={`px-3 py-1 rounded text-xs font-bold uppercase ${viewMode === 'list' ? 'bg-white shadow text-slate-900' : 'text-slate-500'}`}
            data-testid="jobs-view-list"
          >List</button>
          <button
            onClick={() => setViewMode('by-client')}
            className={`px-3 py-1 rounded text-xs font-bold uppercase ${viewMode === 'by-client' ? 'bg-white shadow text-slate-900' : 'text-slate-500'}`}
            data-testid="jobs-view-by-client"
          >By Client</button>
        </div>
        <div className="flex gap-2 overflow-x-auto pb-2 flex-1">
          {availableFilters.map(status => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`px-4 py-2 rounded-md font-semibold text-sm uppercase whitespace-nowrap transition-colors ${
                filter === status 
                  ? 'bg-orange-500 text-slate-900' 
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {status.replace('_', ' ')}
              {status !== 'all' && (
                <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-slate-200">
                  {jobs.filter(j => j.status === status && (!isWorkerOrSupervisor || !['completed', 'cancelled'].includes(j.status))).length}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Jobs List or Grouped-by-Client */}
      {viewMode === 'list' ? (
        <div className="grid gap-4">
          {filteredJobs.map(job => (
            <Card 
              key={job.id} 
              interactive 
              onClick={() => { setSelectedJob(job); setShowModal(true); }}
              data-testid={`job-card-${job.id}`}
            >
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-xl font-bold font-heading uppercase">{job.title}</h3>
                    <Badge variant={statusColors[job.status]}>{job.status.replace('_', ' ')}</Badge>
                    {job.gps_required && (
                      <Badge variant="info" className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        GPS
                      </Badge>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-4 text-sm text-slate-600">
                    <span className="flex items-center gap-1">
                      <User className="w-4 h-4" />
                      {job.client_name}
                    </span>
                    <span className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      {job.location}
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {job.start_date}
                    </span>
                    {job.manager_name && (
                      <span className="flex items-center gap-1 text-orange-600 font-semibold">
                        <Wrench className="w-4 h-4" />
                        {job.manager_name}
                      </span>
                    )}
                  </div>
                </div>
                <ChevronRight className="w-6 h-6 text-slate-400 hidden md:block" />
              </div>
            </Card>
          ))}

          {filteredJobs.length === 0 && (
            <div className="text-center py-12 text-slate-500">
              <Briefcase className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p className="text-lg font-semibold">No jobs found</p>
              <p className="text-sm">Jobs assigned to you will appear here</p>
            </div>
          )}
        </div>
      ) : (
        <JobsByClientGrouped
          jobs={filteredJobs}
          expanded={expandedClients}
          setExpanded={setExpandedClients}
          onSelect={(j) => { setSelectedJob(j); setShowModal(true); }}
          statusColors={statusColors}
        />
      )}

      {/* Job Modal */}
      <JobModal 
        isOpen={showModal} 
        onClose={() => { setShowModal(false); setSelectedJob(null); }}
        job={selectedJob}
        onSave={fetchJobs}
        canEdit={canCreateJob}
      />
    </div>
  );
};

// Documents View Component

// Time Tracking Page


// Forms Page
const FormsPage = () => {
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [companyDocs, setCompanyDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [filter, setFilter] = useState("all");
  const [activeTab, setActiveTab] = useState("forms"); // forms, documents, signatures, acknowledgements, submissions
  const [showDocModal, setShowDocModal] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [showSubmitDocModal, setShowSubmitDocModal] = useState(false);
  const [pendingAcks, setPendingAcks] = useState({ total: 0, allocations: [] });
  const [ackToSign, setAckToSign] = useState(null);

  const isAdmin = ['admin', 'office_staff'].includes(user?.role);

  // Pre-bound job (when coming from JobModal "Fill New Form" shortcut)
  const [preboundJob, setPreboundJob] = useState(null);

  useEffect(() => {
    fetchData();
    // Check URL params for ?fill_for_job=<id> or ?tab=<tabId>
    try {
      const params = new URLSearchParams(window.location.search);
      const tab = params.get('tab');
      if (tab && ['forms', 'documents', 'signatures', 'acknowledgements', 'submissions'].includes(tab)) {
        setActiveTab(tab);
      }
      const jobId = params.get('fill_for_job');
      if (jobId) {
        const cached = localStorage.getItem('shadowops_fill_form_target_job');
        if (cached) {
          const parsed = JSON.parse(cached);
          if (parsed.id === jobId) setPreboundJob(parsed);
          else setPreboundJob({ id: jobId, title: 'Job' });
        } else {
          setPreboundJob({ id: jobId, title: 'Job' });
        }
      }
    } catch (e) {}
  }, []);

  const fetchData = async () => {
    try {
      const [templatesRes, submissionsRes, docsRes, ackRes] = await Promise.all([
        isAdmin ? axios.get(`${API}/forms/templates-admin`) : axios.get(`${API}/forms/templates`),
        axios.get(`${API}/forms/submissions`),
        axios.get(`${API}/company-documents`),
        axios.get(`${API}/forms/pending-acknowledgements`).catch(() => ({ data: { total: 0, allocations: [] } }))
      ]);
      setTemplates(templatesRes.data);
      setSubmissions(submissionsRes.data);
      setCompanyDocs(docsRes.data);
      setPendingAcks(ackRes.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleEditTemplate = (template, e) => {
    e.stopPropagation();
    setEditingTemplate(template);
    setShowEditModal(true);
  };

  const handleDeleteTemplate = async (template, e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete form template "${template.name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/forms/templates/${template.id}`);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to delete template");
    }
  };

  const handleCreateTemplate = () => {
    setEditingTemplate(null);
    setShowEditModal(true);
  };

  const handleEditDoc = (doc, e) => {
    e?.stopPropagation();
    setEditingDoc(doc);
    setShowDocModal(true);
  };

  const handleDeleteDoc = async (doc, e) => {
    e?.stopPropagation();
    if (!window.confirm(`Delete document "${doc.name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/company-documents/${doc.id}`);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to delete document");
    }
  };

  const handleCreateDoc = () => {
    setEditingDoc(null);
    setShowDocModal(true);
  };

  const categoryColors = {
    prestart: "orange",
    swms: "info",
    incident: "error",
    weekly_report: "success",
    timesheet: "default",
    custom: "default",
    policy: "info",
    procedure: "warning",
    safety: "error",
    template: "success",
    other: "default"
  };

  const filteredTemplates = templates.filter(t => {
    if (filter === "all") return true;
    return t.category === filter;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="forms-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <h1 className="text-3xl font-bold uppercase tracking-tight font-heading">Forms & Documents</h1>
        {isAdmin && (
          <div className="flex gap-2">
            <Button onClick={handleCreateTemplate} size="sm" data-testid="create-template-btn">
              <Plus className="w-4 h-4 mr-2" />
              New Form
            </Button>
            <Button onClick={handleCreateDoc} variant="secondary" size="sm" data-testid="create-doc-btn">
              <Upload className="w-4 h-4 mr-2" />
              Upload Document
            </Button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-4 border-b-2 border-slate-200 overflow-x-auto">
        {[
          { id: "forms", label: "Fill Forms", icon: FileText },
          { id: "documents", label: "Company Documents", icon: Upload },
          { id: "signatures", label: "Pending Signatures", icon: Edit3 },
          { id: "acknowledgements", label: `Acknowledge${pendingAcks.total > 0 ? ` (${pendingAcks.total})` : ''}`, icon: CheckCircle, badge: pendingAcks.total },
          { id: "submissions", label: "My Submissions", icon: CheckCircle }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-3 font-semibold text-sm uppercase border-b-2 -mb-[2px] transition-colors whitespace-nowrap relative ${
              activeTab === tab.id
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
            data-testid={`tab-${tab.id}`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
            {tab.badge > 0 && (
              <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-red-500" />
            )}
          </button>
        ))}
      </div>

      {/* Forms Tab */}
      {activeTab === "forms" && (
        <>
          {/* Pre-bound job banner (from JobModal "Fill New Form" shortcut) */}
          {preboundJob && (
            <Card className="bg-blue-50 border-2 border-blue-300" data-testid="prebound-job-banner">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-bold text-blue-900">Filling form for: {preboundJob.title}</p>
                  <p className="text-xs text-blue-700 mt-1">Pick a template below — it'll be auto-linked to this job. Choose an incident/SWMS/etc. template and assign it to the person who needs to sign.</p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setPreboundJob(null);
                    // Clean URL
                    window.history.replaceState({}, '', '/forms');
                  }}
                  data-testid="clear-prebound-job"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </Card>
          )}

          {/* Category Filter */}
          <div className="flex gap-2 overflow-x-auto pb-2">
            {["all", "prestart", "swms", "incident", "weekly_report"].map(cat => (
              <button
                key={cat}
                onClick={() => setFilter(cat)}
                className={`px-4 py-2 rounded-md font-semibold text-sm uppercase whitespace-nowrap transition-colors ${
                  filter === cat 
                    ? 'bg-orange-500 text-slate-900' 
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {cat.replace('_', ' ')}
              </button>
            ))}
          </div>

          {/* Form Templates */}
          <div className="grid md:grid-cols-2 gap-4">
            {filteredTemplates.map(template => (
              <Card 
                key={template.id}
                interactive
                onClick={() => { setSelectedTemplate(template); setShowSubmitModal(true); }}
                data-testid={`form-template-${template.id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <Badge variant={categoryColors[template.category]}>{template.category.replace('_', ' ')}</Badge>
                  <div className="flex items-center gap-2">
                    {isAdmin && (
                      <>
                        <button
                          onClick={(e) => handleEditTemplate(template, e)}
                          className="p-1.5 hover:bg-slate-100 rounded-md text-slate-500 hover:text-blue-600 transition-colors"
                          data-testid={`edit-template-${template.id}`}
                        >
                          <Settings className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => handleDeleteTemplate(template, e)}
                          className="p-1.5 hover:bg-red-50 rounded-md text-slate-500 hover:text-red-600 transition-colors"
                          data-testid={`delete-template-${template.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </>
                    )}
                    <FileText className="w-5 h-5 text-slate-400" />
                  </div>
                </div>
                <h3 className="text-xl font-bold font-heading uppercase mb-2">{template.name}</h3>
                <p className="text-sm text-slate-600">{template.description}</p>
                <p className="text-xs text-slate-400 mt-3">{template.fields?.length || 0} fields</p>
              </Card>
            ))}

            {filteredTemplates.length === 0 && (
              <div className="col-span-2 text-center py-12 text-slate-500">
                <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p className="text-lg font-semibold">No forms found</p>
                {isAdmin && <p className="text-sm">Create a new form template to get started</p>}
              </div>
            )}
          </div>
        </>
      )}

      {/* Company Documents Tab */}
      {activeTab === "documents" && (
        <div className="space-y-4">
          <p className="text-slate-600">
            Download company documents, SWMS templates, weekly report forms, and other resources. You can fill out forms and resubmit them.
          </p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {companyDocs.map(doc => (
              <Card key={doc.id} className="relative" data-testid={`company-doc-${doc.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <Badge variant={categoryColors[doc.category] || "default"}>
                    {doc.category?.replace('_', ' ') || 'document'}
                  </Badge>
                  {isAdmin && (
                    <div className="flex gap-1">
                      <button
                        onClick={(e) => handleEditDoc(doc, e)}
                        className="p-1.5 hover:bg-slate-100 rounded-md text-slate-500 hover:text-blue-600 transition-colors"
                        data-testid={`edit-doc-${doc.id}`}
                      >
                        <Settings className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => handleDeleteDoc(doc, e)}
                        className="p-1.5 hover:bg-red-50 rounded-md text-slate-500 hover:text-red-600 transition-colors"
                        data-testid={`delete-doc-${doc.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
                <h3 className="font-bold mb-2">{doc.name}</h3>
                {doc.description && (
                  <p className="text-sm text-slate-600 mb-3">{doc.description}</p>
                )}
                <div className="flex flex-col gap-2">
                  <span className="text-xs text-slate-400">
                    {doc.file_type?.toUpperCase() || 'FILE'}
                  </span>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openFile(doc.file_url, doc.name)}
                      data-testid={`download-doc-${doc.id}`}
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      View
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => { setSelectedDoc(doc); setShowSubmitDocModal(true); }}
                      data-testid={`fill-doc-${doc.id}`}
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Fill & Submit
                    </Button>
                  </div>
                </div>
                <p className="text-xs text-slate-400 mt-3">
                  Uploaded by {doc.uploaded_by_name} on {new Date(doc.created_at).toLocaleDateString()}
                </p>
              </Card>
            ))}

            {companyDocs.length === 0 && (
              <div className="col-span-3 text-center py-12 text-slate-500">
                <Upload className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p className="text-lg font-semibold">No documents uploaded yet</p>
                {isAdmin && <p className="text-sm">Upload company documents for your team to access</p>}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Pending Signatures Tab */}
      {activeTab === "signatures" && (
        <PendingSignaturesTab onRefresh={fetchData} />
      )}

      {/* Acknowledgements Tab — supervisor-filled forms awaiting crew sign-off */}
      {activeTab === "acknowledgements" && (
        <AcknowledgementsTab
          data={pendingAcks}
          onSign={(alloc) => setAckToSign(alloc)}
        />
      )}

      {/* Submissions Tab - Shows form submissions AND document submissions */}
      {activeTab === "submissions" && (
        <DocumentSubmissionsTab isAdmin={isAdmin} onRefresh={fetchData} />
      )}

      {/* Acknowledgement Sign Modal */}
      {ackToSign && (
        <AcknowledgeSignModal
          allocation={ackToSign}
          onClose={() => setAckToSign(null)}
          onSigned={() => { setAckToSign(null); fetchData(); }}
        />
      )}

      {/* Form Submit Modal */}
      <FormSubmitModal 
        isOpen={showSubmitModal}
        onClose={() => { setShowSubmitModal(false); setSelectedTemplate(null); }}
        template={selectedTemplate}
        onSubmit={fetchData}
        preboundJob={preboundJob}
      />

      {/* Form Template Edit Modal */}
      <FormTemplateEditModal
        isOpen={showEditModal}
        onClose={() => { setShowEditModal(false); setEditingTemplate(null); }}
        template={editingTemplate}
        onSave={fetchData}
      />

      {/* Company Document Modal */}
      <CompanyDocumentModal
        isOpen={showDocModal}
        onClose={() => { setShowDocModal(false); setEditingDoc(null); }}
        document={editingDoc}
        onSave={fetchData}
      />

      {/* Document Submit Modal (Fill & Resubmit) */}
      <DocumentSubmitModal
        isOpen={showSubmitDocModal}
        onClose={() => { setShowSubmitDocModal(false); setSelectedDoc(null); }}
        document={selectedDoc}
        onSubmit={fetchData}
      />
    </div>
  );
};

// Form Submit Modal
const FormSubmitModal = ({ isOpen, onClose, template, onSubmit, preboundJob }) => {
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedJob, setSelectedJob] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [newAttachment, setNewAttachment] = useState({ name: "", file_url: "" });
  
  // Enhanced form state
  const [involvedEmployees, setInvolvedEmployees] = useState([]);
  const [requiredSignatures, setRequiredSignatures] = useState([]);
  const [guestSignatures, setGuestSignatures] = useState([]);
  const [showGuestSignature, setShowGuestSignature] = useState(false);
  const [guestName, setGuestName] = useState("");
  const [signatureCanvasRef, setSignatureCanvasRef] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setFormData({});
      setAttachments([]);
      setNewAttachment({ name: "", file_url: "" });
      setInvolvedEmployees([]);
      setRequiredSignatures([]);
      setGuestSignatures([]);
      // Pre-bind job from prop (incident report flow etc.)
      if (preboundJob?.id) {
        setSelectedJob(preboundJob.id);
      } else {
        setSelectedJob("");
      }
      fetchJobs();
      fetchEmployees();
    }
  }, [isOpen, preboundJob]);

  const fetchJobs = async () => {
    try {
      const res = await axios.get(`${API}/jobs`);
      // Only show scheduled or in_progress jobs
      setJobs(res.data.filter(j => ['scheduled', 'in_progress'].includes(j.status)));
    } catch (e) {}
  };

  const fetchEmployees = async () => {
    try {
      const res = await axios.get(`${API}/users`);
      setEmployees(res.data.filter(u => u.is_active));
    } catch (e) {}
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/forms/submissions`, {
        template_id: template.id,
        job_id: selectedJob || null,
        data: formData,
        attachments: attachments,
        involved_employees: involvedEmployees,
        required_signatures: requiredSignatures,
        guest_signatures: guestSignatures
      });
      alert("Form submitted successfully!");
      onSubmit();
      onClose();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to submit form");
    } finally {
      setLoading(false);
    }
  };

  const addAttachment = () => {
    if (newAttachment.name && newAttachment.file_url) {
      setAttachments([...attachments, { ...newAttachment, id: Date.now() }]);
      setNewAttachment({ name: "", file_url: "" });
    }
  };

  const removeAttachment = (id) => {
    setAttachments(attachments.filter(a => a.id !== id));
  };

  const toggleInvolvedEmployee = (empId) => {
    if (involvedEmployees.includes(empId)) {
      setInvolvedEmployees(involvedEmployees.filter(id => id !== empId));
    } else {
      setInvolvedEmployees([...involvedEmployees, empId]);
    }
  };

  const toggleRequiredSignature = (empId) => {
    if (requiredSignatures.includes(empId)) {
      setRequiredSignatures(requiredSignatures.filter(id => id !== empId));
    } else {
      setRequiredSignatures([...requiredSignatures, empId]);
    }
  };

  const addGuestSignature = (signatureData) => {
    if (guestName && signatureData) {
      setGuestSignatures([...guestSignatures, { 
        name: guestName, 
        signature_data: signatureData,
        id: Date.now()
      }]);
      setGuestName("");
      setShowGuestSignature(false);
    }
  };

  const removeGuestSignature = (id) => {
    setGuestSignatures(guestSignatures.filter(g => g.id !== id));
  };

  const renderField = (field) => {
    switch (field.type) {
      case 'textarea':
        return (
          <Textarea
            key={field.name}
            label={field.label}
            value={formData[field.name] || ""}
            onChange={(e) => setFormData({...formData, [field.name]: e.target.value})}
            required={field.required}
          />
        );
      case 'checkbox':
        return (
          <label key={field.name} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg cursor-pointer">
            <input
              type="checkbox"
              checked={formData[field.name] || false}
              onChange={(e) => setFormData({...formData, [field.name]: e.target.checked})}
              className="w-6 h-6"
              required={field.required}
            />
            <span className="font-medium">{field.label}</span>
          </label>
        );
      case 'select':
        return (
          <Select
            key={field.name}
            label={field.label}
            value={formData[field.name] || ""}
            onChange={(e) => setFormData({...formData, [field.name]: e.target.value})}
            options={[
              { value: "", label: "Select..." },
              ...(field.options || []).map(opt => ({ value: opt, label: opt }))
            ]}
            required={field.required}
          />
        );
      case 'job_site':
        return (
          <div key={field.name} className="p-4 bg-blue-50 border-2 border-blue-200 rounded-lg space-y-2">
            <label className="block text-sm font-bold uppercase text-blue-800">{field.label}</label>
            {preboundJob?.id ? (
              <div className="flex items-center gap-2 p-2 bg-white rounded border border-blue-300" data-testid="form-prebound-job">
                <FileText className="w-4 h-4 text-blue-600" />
                <span className="font-semibold">{preboundJob.title}</span>
                <Badge variant="info">Pre-linked</Badge>
              </div>
            ) : (
              <Select
                value={formData[field.name] || selectedJob}
                onChange={(e) => {
                  setFormData({...formData, [field.name]: e.target.value});
                  setSelectedJob(e.target.value);
                }}
                options={[
                  { value: "", label: "Select Job Site..." },
                  ...jobs.map(j => ({ 
                    value: j.id, 
                    label: `${j.title} - ${j.client_name} (${j.status === 'scheduled' ? 'Not Started' : 'In Progress'})`
                  }))
                ]}
                required={field.required}
              />
            )}
            <p className="text-xs text-blue-600">Selecting a job links this form to that job's records</p>
          </div>
        );
      case 'employee_single':
        return (
          <div key={field.name} className="p-4 bg-green-50 border-2 border-green-200 rounded-lg space-y-2">
            <label className="block text-sm font-bold uppercase text-green-800">{field.label}</label>
            <Select
              value={formData[field.name] || ""}
              onChange={(e) => setFormData({...formData, [field.name]: e.target.value})}
              options={[
                { value: "", label: "Select Employee..." },
                ...employees.map(emp => ({ 
                  value: emp.id, 
                  label: `${emp.first_name} ${emp.last_name} (${emp.role?.replace('_', ' ')})`
                }))
              ]}
              required={field.required}
            />
          </div>
        );
      case 'employee_multi':
        return (
          <div key={field.name} className="p-4 bg-green-50 border-2 border-green-200 rounded-lg space-y-3">
            <label className="block text-sm font-bold uppercase text-green-800">{field.label}</label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-48 overflow-y-auto">
              {employees.map(emp => (
                <label key={emp.id} className="flex items-center gap-2 p-2 bg-white rounded border cursor-pointer hover:bg-green-100">
                  <input
                    type="checkbox"
                    checked={involvedEmployees.includes(emp.id)}
                    onChange={() => toggleInvolvedEmployee(emp.id)}
                    className="w-5 h-5"
                  />
                  <span className="text-sm">{emp.first_name} {emp.last_name}</span>
                </label>
              ))}
            </div>
            {involvedEmployees.length > 0 && (
              <p className="text-xs text-green-700">{involvedEmployees.length} employee(s) selected</p>
            )}
          </div>
        );
      case 'signature_employee':
        return (
          <div key={field.name} className="p-4 bg-amber-50 border-2 border-amber-200 rounded-lg space-y-3">
            <label className="block text-sm font-bold uppercase text-amber-800 flex items-center gap-2">
              <Edit3 className="w-4 h-4" />
              {field.label} - Select Employees to Sign
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-48 overflow-y-auto">
              {employees.map(emp => (
                <label key={emp.id} className="flex items-center gap-2 p-2 bg-white rounded border cursor-pointer hover:bg-amber-100">
                  <input
                    type="checkbox"
                    checked={requiredSignatures.includes(emp.id)}
                    onChange={() => toggleRequiredSignature(emp.id)}
                    className="w-5 h-5"
                  />
                  <span className="text-sm">{emp.first_name} {emp.last_name}</span>
                </label>
              ))}
            </div>
            {requiredSignatures.length > 0 && (
              <p className="text-xs text-amber-700">
                <AlertCircle className="w-3 h-3 inline mr-1" />
                {requiredSignatures.length} employee(s) will be notified to sign this form
              </p>
            )}
          </div>
        );
      case 'signature_guest':
        return (
          <div key={field.name} className="p-4 bg-purple-50 border-2 border-purple-200 rounded-lg space-y-3">
            <label className="block text-sm font-bold uppercase text-purple-800 flex items-center gap-2">
              <Edit3 className="w-4 h-4" />
              {field.label} - Guest/Non-Employee Signatures
            </label>
            
            {/* Existing guest signatures */}
            {guestSignatures.length > 0 && (
              <div className="space-y-2">
                {guestSignatures.map(sig => (
                  <div key={sig.id} className="flex items-center justify-between p-2 bg-white rounded border">
                    <span className="text-sm font-medium">{sig.name}</span>
                    <div className="flex items-center gap-2">
                      <Badge variant="success">Signed</Badge>
                      <button type="button" onClick={() => removeGuestSignature(sig.id)} className="text-red-500">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {/* Add guest signature */}
            {!showGuestSignature ? (
              <Button type="button" variant="outline" size="sm" onClick={() => setShowGuestSignature(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Add Guest Signature
              </Button>
            ) : (
              <div className="space-y-3 p-3 bg-white rounded-lg border">
                <Input
                  label="Guest Name"
                  value={guestName}
                  onChange={(e) => setGuestName(e.target.value)}
                  placeholder="Enter guest's full name"
                />
                <FormSignaturePad onSign={(data) => addGuestSignature(data)} onCancel={() => setShowGuestSignature(false)} />
              </div>
            )}
          </div>
        );
      default:
        return (
          <Input
            key={field.name}
            type={field.type || "text"}
            label={field.label}
            value={formData[field.name] || ""}
            onChange={(e) => setFormData({...formData, [field.name]: e.target.value})}
            required={field.required}
          />
        );
    }
  };

  if (!template) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={template.name} size="lg">
      <form onSubmit={handleSubmit} className="space-y-6">
        {template.description && (
          <p className="text-slate-600 bg-slate-50 p-4 rounded-lg">{template.description}</p>
        )}

        {/* Show job selector if template doesn't have a job_site field */}
        {!template.fields?.some(f => f.type === 'job_site') && (
          <Select
            label="Related Job (Optional)"
            value={selectedJob}
            onChange={(e) => setSelectedJob(e.target.value)}
            options={[
              { value: "", label: "No specific job" },
              ...jobs.map(j => ({ value: j.id, label: `${j.title} - ${j.client_name}` }))
            ]}
          />
        )}

        {template.fields?.map(renderField)}

        {/* Document Attachments Section */}
        <div className="p-4 bg-blue-50 border-2 border-blue-200 rounded-lg space-y-4">
          <h4 className="font-bold uppercase text-blue-800 flex items-center gap-2">
            <Camera className="w-5 h-5" />
            Attach Documents / Photos
          </h4>
          
          <div className="grid md:grid-cols-2 gap-4">
            <Input
              label="Document Name"
              value={newAttachment.name}
              onChange={(e) => setNewAttachment({...newAttachment, name: e.target.value})}
              placeholder="e.g., Signed SWMS Page 1"
            />
            <FileUploadInput
              label="Upload File/Photo"
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif"
              onFileSelect={(fileUrl, fileName) => setNewAttachment({...newAttachment, file_url: fileUrl})}
              currentFile={newAttachment.file_url ? "File selected" : null}
              onClear={() => setNewAttachment({...newAttachment, file_url: ""})}
            />
          </div>
          <Button type="button" variant="outline" size="sm" onClick={addAttachment} disabled={!newAttachment.file_url}>
            <Plus className="w-4 h-4 mr-2" />
            Add Attachment
          </Button>
          
          {attachments.length > 0 && (
            <div className="space-y-2 mt-4">
              <p className="text-sm font-semibold text-slate-600">Attached Files:</p>
              {attachments.map(att => (
                <div key={att.id} className="flex items-center justify-between bg-white p-2 rounded border">
                  <span className="text-sm">{att.name}</span>
                  <button type="button" onClick={() => removeAttachment(att.id)} className="text-red-500 hover:text-red-700">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
          
          <p className="text-xs text-blue-600">
            Upload photos of signed forms, documents, or any relevant files directly from your device.
          </p>
        </div>

        <div className="flex gap-4">
          <Button type="submit" disabled={loading} data-testid="submit-form-btn">
            {loading ? <Spinner size="sm" /> : "Submit Form"}
          </Button>
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
        </div>
      </form>
    </Modal>
  );
};

// FormSignaturePad extracted to /components/FormSignaturePad.jsx



// Pending Signatures Tab Component
const PendingSignaturesTab = ({ onRefresh }) => {
  const { user } = useAuth();
  const [pendingForms, setPendingForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedForm, setSelectedForm] = useState(null);
  const [showSignModal, setShowSignModal] = useState(false);

  useEffect(() => {
    fetchPendingSignatures();
  }, []);

  const fetchPendingSignatures = async () => {
    try {
      const res = await axios.get(`${API}/forms/pending-signatures`);
      setPendingForms(res.data);
    } catch (e) {
      console.error("Failed to fetch pending signatures", e);
    } finally {
      setLoading(false);
    }
  };

  const handleSign = async (signatureData) => {
    try {
      await axios.post(`${API}/forms/submissions/${selectedForm.id}/sign`, {
        signature_data: signatureData
      });
      alert("Form signed successfully!");
      setShowSignModal(false);
      setSelectedForm(null);
      fetchPendingSignatures();
      onRefresh();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to sign form");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-amber-50 border-2 border-amber-200 rounded-lg p-4">
        <h3 className="font-bold text-amber-800 flex items-center gap-2">
          <Edit3 className="w-5 h-5" />
          Forms Requiring Your Signature
        </h3>
        <p className="text-sm text-amber-700">Sign these forms to complete the submission process</p>
      </div>

      {pendingForms.length === 0 ? (
        <div className="text-center py-12 text-slate-500 bg-slate-50 rounded-lg">
          <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-400" />
          <p className="text-lg font-semibold">All caught up!</p>
          <p className="text-sm">No forms require your signature right now</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {pendingForms.map(form => (
            <Card key={form.id} interactive onClick={() => { setSelectedForm(form); setShowSignModal(true); }}>
              <div className="flex items-start justify-between">
                <div>
                  <h4 className="font-bold text-lg">{form.template_name}</h4>
                  <p className="text-sm text-slate-500">Submitted by {form.user_name}</p>
                  {form.job_title && (
                    <p className="text-xs text-slate-400 flex items-center gap-1 mt-1">
                      <MapPin className="w-3 h-3" />
                      {form.job_title}
                    </p>
                  )}
                </div>
                <Badge variant="warning">Pending</Badge>
              </div>
              <div className="mt-4 flex items-center justify-between">
                <span className="text-xs text-slate-400">
                  {new Date(form.submitted_at).toLocaleDateString()}
                </span>
                <Button size="sm" onClick={(e) => { e.stopPropagation(); setSelectedForm(form); setShowSignModal(true); }}>
                  <Edit3 className="w-4 h-4 mr-2" />
                  Sign Now
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Sign Modal */}
      <Modal 
        isOpen={showSignModal} 
        onClose={() => { setShowSignModal(false); setSelectedForm(null); }} 
        title={`Sign: ${selectedForm?.template_name}`}
      >
        <div className="space-y-4">
          {selectedForm && (
            <>
              <div className="bg-slate-50 p-4 rounded-lg space-y-2">
                <p><strong>Submitted by:</strong> {selectedForm.user_name}</p>
                {selectedForm.job_title && <p><strong>Job:</strong> {selectedForm.job_title}</p>}
                <p><strong>Date:</strong> {new Date(selectedForm.submitted_at).toLocaleString()}</p>
              </div>

              {/* Show form data summary */}
              <div className="bg-slate-100 p-4 rounded-lg">
                <h4 className="font-bold text-sm uppercase mb-2">Form Data</h4>
                <div className="space-y-1 text-sm">
                  {Object.entries(selectedForm.data || {}).map(([key, value]) => (
                    <p key={key}><strong>{key}:</strong> {String(value)}</p>
                  ))}
                </div>
              </div>

              {/* Signature Pad */}
              <div className="border-2 border-amber-200 rounded-lg p-4 bg-amber-50">
                <h4 className="font-bold text-amber-800 mb-3">Your Signature</h4>
                <FormSignaturePad 
                  onSign={handleSign} 
                  onCancel={() => { setShowSignModal(false); setSelectedForm(null); }} 
                />
              </div>
            </>
          )}
        </div>
      </Modal>
    </div>
  );
};

// Form Template Edit Modal (for Admin to create/edit form templates)
const FormTemplateEditModal = ({ isOpen, onClose, template, onSave }) => {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    category: "custom",
    fields: [],
    roles_access: ["admin", "supervisor", "crane_operator", "rigger", "labourer", "office_staff"]
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (template) {
      setFormData({
        name: template.name || "",
        description: template.description || "",
        category: template.category || "custom",
        fields: template.fields || [],
        roles_access: template.roles_access || ["admin", "supervisor", "crane_operator", "rigger", "labourer", "office_staff"]
      });
    } else {
      setFormData({
        name: "",
        description: "",
        category: "custom",
        fields: [],
        roles_access: ["admin", "supervisor", "crane_operator", "rigger", "labourer", "office_staff"]
      });
    }
  }, [template, isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (template) {
        await axios.put(`${API}/forms/templates/${template.id}`, formData);
        alert("Form template updated!");
      } else {
        await axios.post(`${API}/forms/templates`, formData);
        alert("Form template created!");
      }
      onSave();
      onClose();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to save template");
    } finally {
      setLoading(false);
    }
  };

  // Quick add field with one click
  const quickAddField = (type, label, name) => {
    const field = { type, label, name, required: false };
    if (type === 'select') field.options = [];
    setFormData({ ...formData, fields: [...formData.fields, field] });
  };

  const removeField = (index) => {
    setFormData({
      ...formData,
      fields: formData.fields.filter((_, i) => i !== index)
    });
  };

  const updateField = (index, updates) => {
    const newFields = [...formData.fields];
    newFields[index] = { ...newFields[index], ...updates };
    setFormData({ ...formData, fields: newFields });
  };

  const moveField = (index, direction) => {
    const newFields = [...formData.fields];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= newFields.length) return;
    [newFields[index], newFields[newIndex]] = [newFields[newIndex], newFields[index]];
    setFormData({ ...formData, fields: newFields });
  };

  // Field type configs with friendly names and icons
  const fieldTypes = [
    { type: 'text', label: 'Short Answer', icon: '✏️', desc: 'Single line text' },
    { type: 'textarea', label: 'Long Answer', icon: '📝', desc: 'Multiple lines' },
    { type: 'number', label: 'Number', icon: '🔢', desc: 'Numbers only' },
    { type: 'date', label: 'Date', icon: '📅', desc: 'Pick a date' },
    { type: 'time', label: 'Time', icon: '🕐', desc: 'Pick a time' },
    { type: 'checkbox', label: 'Yes/No Tick', icon: '☑️', desc: 'Checkbox' },
    { type: 'select', label: 'Dropdown', icon: '📋', desc: 'Choose from list' },
    { type: 'job_site', label: 'Job Site', icon: '🏗️', desc: 'Link to a job' },
    { type: 'employee_single', label: 'Pick Employee', icon: '👤', desc: 'Select one person' },
    { type: 'employee_multi', label: 'Pick Multiple People', icon: '👥', desc: 'Select multiple people' },
    { type: 'signature_employee', label: 'Employee Signatures', icon: '✍️', desc: 'Workers sign the form' },
    { type: 'signature_guest', label: 'Guest Signatures', icon: '🖊️', desc: 'Non-employees sign' },
  ];

  const getFieldIcon = (type) => {
    const found = fieldTypes.find(f => f.type === type);
    return found?.icon || '📝';
  };

  const getFieldLabel = (type) => {
    const found = fieldTypes.find(f => f.type === type);
    return found?.label || type;
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={template ? "Edit Form" : "Create New Form"} size="xl">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Step 1: Basic Info */}
        <div className="bg-blue-50 p-4 rounded-lg border-2 border-blue-200">
          <h3 className="font-bold text-blue-800 mb-3 flex items-center gap-2">
            <span className="bg-blue-600 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm">1</span>
            Name Your Form
          </h3>
          <div className="grid md:grid-cols-2 gap-4">
            <Input
              label="Form Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Toolbox Talk Sign-Off"
              required
              data-testid="template-name-input"
            />
            <Select
              label="Category"
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              options={[
                { value: "prestart", label: "Pre-Start Check" },
                { value: "swms", label: "SWMS" },
                { value: "incident", label: "Incident Report" },
                { value: "toolbox", label: "Toolbox Talk" },
                { value: "weekly_report", label: "Weekly Report" },
                { value: "custom", label: "Other" }
              ]}
            />
          </div>
          <div className="mt-3">
            <Textarea
              label="Description (optional)"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="What is this form for?"
              rows={2}
            />
          </div>
        </div>

        {/* Step 2: Add Fields */}
        <div className="bg-green-50 p-4 rounded-lg border-2 border-green-200">
          <h3 className="font-bold text-green-800 mb-3 flex items-center gap-2">
            <span className="bg-green-600 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm">2</span>
            Add Fields - Click to Add
          </h3>
          
          {/* Quick Add Buttons */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
            {fieldTypes.map(ft => (
              <button
                key={ft.type}
                type="button"
                onClick={() => quickAddField(ft.type, '', ft.type + '_' + Date.now())}
                className="p-3 bg-white border-2 border-green-200 rounded-lg hover:border-green-500 hover:bg-green-50 transition-all text-left"
              >
                <div className="flex items-center gap-2">
                  <span className="text-xl">{ft.icon}</span>
                  <div>
                    <p className="font-semibold text-sm text-slate-800">{ft.label}</p>
                    <p className="text-xs text-slate-500">{ft.desc}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Step 3: Your Fields */}
        <div className="bg-amber-50 p-4 rounded-lg border-2 border-amber-200">
          <h3 className="font-bold text-amber-800 mb-3 flex items-center gap-2">
            <span className="bg-amber-600 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm">3</span>
            Your Form Fields ({formData.fields.length})
          </h3>
          
          {formData.fields.length === 0 ? (
            <div className="text-center py-8 text-slate-500 bg-white rounded-lg border-2 border-dashed border-slate-300">
              <p className="text-lg">👆 Click the buttons above to add fields</p>
              <p className="text-sm">Your form fields will appear here</p>
            </div>
          ) : (
            <div className="space-y-3">
              {formData.fields.map((field, idx) => (
                <div key={idx} className="p-4 bg-white border-2 rounded-lg border-slate-200 hover:border-amber-300">
                  <div className="flex items-start gap-3">
                    {/* Move buttons */}
                    <div className="flex flex-col gap-1">
                      <button 
                        type="button" 
                        onClick={() => moveField(idx, -1)} 
                        disabled={idx === 0}
                        className="text-slate-400 hover:text-slate-600 disabled:opacity-30"
                      >▲</button>
                      <button 
                        type="button" 
                        onClick={() => moveField(idx, 1)} 
                        disabled={idx === formData.fields.length - 1}
                        className="text-slate-400 hover:text-slate-600 disabled:opacity-30"
                      >▼</button>
                    </div>
                    
                    {/* Field icon */}
                    <span className="text-2xl">{getFieldIcon(field.type)}</span>
                    
                    {/* Field config */}
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center gap-2">
                        <Badge variant="default">{getFieldLabel(field.type)}</Badge>
                        {field.required && <Badge variant="warning">Required</Badge>}
                      </div>
                      
                      <Input
                        placeholder="Question / Label (e.g., 'What happened?')"
                        value={field.label}
                        onChange={(e) => updateField(idx, { label: e.target.value })}
                        className="text-lg font-medium"
                      />
                      
                      {/* Options for dropdown */}
                      {field.type === 'select' && (
                        <Input
                          placeholder="Options (comma separated): Yes, No, N/A"
                          value={field.options?.join(', ') || ''}
                          onChange={(e) => updateField(idx, { options: e.target.value.split(',').map(o => o.trim()).filter(Boolean) })}
                        />
                      )}
                      
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={field.required || false}
                          onChange={(e) => updateField(idx, { required: e.target.checked })}
                          className="w-4 h-4"
                        />
                        <span className="text-sm text-slate-600">Required field</span>
                      </label>
                    </div>
                    
                    {/* Delete */}
                    <button 
                      type="button" 
                      onClick={() => removeField(idx)} 
                      className="text-red-500 hover:text-red-700 p-2"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Save */}
        <div className="flex gap-4 pt-4 border-t-2">
          <Button type="submit" disabled={loading || !formData.name || formData.fields.length === 0} data-testid="save-template-btn">
            {loading ? <Spinner size="sm" /> : (template ? "Save Changes" : "Create Form")}
          </Button>
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
        </div>
      </form>
    </Modal>
  );
};

// Company Document Modal (for Admin to upload/edit company documents)
const CompanyDocumentModal = ({ isOpen, onClose, document, onSave }) => {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    category: "other",
    file_url: "",
    file_type: "pdf"
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (document) {
      setFormData({
        name: document.name || "",
        description: document.description || "",
        category: document.category || "other",
        file_url: document.file_url || "",
        file_type: document.file_type || "pdf"
      });
    } else {
      setFormData({
        name: "",
        description: "",
        category: "other",
        file_url: "",
        file_type: "pdf"
      });
    }
  }, [document, isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.file_url) {
      alert("File URL is required");
      return;
    }
    setLoading(true);
    try {
      if (document) {
        await axios.put(`${API}/company-documents/${document.id}`, formData);
        alert("Document updated!");
      } else {
        await axios.post(`${API}/company-documents`, formData);
        alert("Document uploaded!");
      }
      onSave();
      onClose();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to save document");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={document ? "Edit Company Document" : "Upload Company Document"} size="default">
      <form onSubmit={handleSubmit} className="space-y-6">
        <Input
          label="Document Name"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="e.g., Weekly Report Template"
          required
          data-testid="doc-name-input"
        />

        <Select
          label="Category"
          value={formData.category}
          onChange={(e) => setFormData({ ...formData, category: e.target.value })}
          options={[
            { value: "swms", label: "SWMS Template" },
            { value: "weekly_report", label: "Weekly Report" },
            { value: "policy", label: "Company Policy" },
            { value: "procedure", label: "Procedure" },
            { value: "safety", label: "Safety Document" },
            { value: "template", label: "Blank Template" },
            { value: "other", label: "Other" }
          ]}
        />

        <Textarea
          label="Description (optional)"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          placeholder="Brief description of the document..."
        />

        <FileUploadInput
          label="Upload Document"
          accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
          onFileSelect={(fileUrl, fileName) => setFormData({ ...formData, file_url: fileUrl })}
          currentFile={formData.file_url ? "Document uploaded" : null}
          onClear={() => setFormData({ ...formData, file_url: "" })}
        />

        <Select
          label="File Type"
          value={formData.file_type}
          onChange={(e) => setFormData({ ...formData, file_type: e.target.value })}
          options={[
            { value: "pdf", label: "PDF Document" },
            { value: "doc", label: "Word Document" },
            { value: "xls", label: "Excel Spreadsheet" },
            { value: "image", label: "Image" },
            { value: "other", label: "Other" }
          ]}
        />

        <div className="flex gap-4">
          <Button type="submit" disabled={loading || !formData.file_url} data-testid="save-doc-btn">
            {loading ? <Spinner size="sm" /> : (document ? "Update Document" : "Upload Document")}
          </Button>
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
        </div>
      </form>
    </Modal>
  );
};

// Document Submit Modal (Fill & Resubmit a company document)
const DocumentSubmitModal = ({ isOpen, onClose, document, onSubmit }) => {
  const [formData, setFormData] = useState({
    notes: "",
    file_url: "",
    file_name: ""
  });
  const [loading, setLoading] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState("");

  useEffect(() => {
    if (isOpen) {
      setFormData({ notes: "", file_url: "", file_name: "" });
      setSelectedJob("");
      fetchJobs();
    }
  }, [isOpen]);

  const fetchJobs = async () => {
    try {
      const res = await axios.get(`${API}/jobs`);
      setJobs(res.data);
    } catch (e) {}
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.file_url) {
      alert("Please provide the URL of your completed document");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/document-submissions`, {
        document_id: document.id,
        job_id: selectedJob || null,
        notes: formData.notes,
        file_url: formData.file_url,
        file_name: formData.file_name || `Completed_${document.name}`
      });
      alert("Document submitted successfully! Admin will review it.");
      onSubmit();
      onClose();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to submit document");
    } finally {
      setLoading(false);
    }
  };

  if (!document) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Submit: ${document.name}`} size="default">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="p-4 bg-blue-50 border-2 border-blue-200 rounded-lg">
          <h4 className="font-bold text-blue-800 mb-2">Instructions:</h4>
          <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
            <li>Download and view the original document using the button below</li>
            <li>Fill out the form (print, scan, or use digital tools)</li>
            <li>Upload your completed document to a file sharing service</li>
            <li>Paste the URL below and submit</li>
          </ol>
        </div>

        <Button
          type="button"
          variant="outline"
          onClick={() => openFile(document.file_url, document.name)}
          className="w-full"
        >
          <Eye className="w-4 h-4 mr-2" />
          View Original Document
        </Button>

        <Select
          label="Related Job (Optional)"
          value={selectedJob}
          onChange={(e) => setSelectedJob(e.target.value)}
          options={[
            { value: "", label: "No specific job" },
            ...jobs.map(j => ({ value: j.id, label: j.title }))
          ]}
        />

        <FileUploadInput
          label="Upload Your Completed Document"
          accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
          onFileSelect={(fileUrl, fileName) => setFormData({ ...formData, file_url: fileUrl, file_name: fileName || formData.file_name })}
          currentFile={formData.file_url ? "Document uploaded" : null}
          onClear={() => setFormData({ ...formData, file_url: "" })}
        />

        <Input
          label="File Name (optional)"
          value={formData.file_name}
          onChange={(e) => setFormData({ ...formData, file_name: e.target.value })}
          placeholder="e.g., Weekly_Report_John_Week3"
        />

        <Textarea
          label="Notes (optional)"
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          placeholder="Any additional notes for the admin..."
        />

        <div className="p-4 bg-amber-50 border-2 border-amber-200 rounded-lg">
          <p className="text-sm text-amber-800">
            <strong>Privacy:</strong> Your submission will only be visible to you and administrators.
          </p>
        </div>

        <div className="flex gap-4">
          <Button type="submit" disabled={loading || !formData.file_url} data-testid="submit-filled-doc-btn">
            {loading ? <Spinner size="sm" /> : "Submit Document"}
          </Button>
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
        </div>
      </form>
    </Modal>
  );
};

// Document Submissions Tab (shows form submissions + document submissions)
const DocumentSubmissionsTab = ({ isAdmin, onRefresh }) => {
  const [formSubmissions, setFormSubmissions] = useState([]);
  const [docSubmissions, setDocSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSubTab, setActiveSubTab] = useState("forms");

  useEffect(() => {
    fetchSubmissions();
  }, []);

  const fetchSubmissions = async () => {
    try {
      const [formRes, docRes] = await Promise.all([
        axios.get(`${API}/forms/submissions`),
        axios.get(`${API}/document-submissions`)
      ]);
      setFormSubmissions(formRes.data);
      setDocSubmissions(docRes.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleReview = async (submissionId, status) => {
    const notes = status === 'rejected' ? window.prompt("Reason for rejection (optional):") : null;
    try {
      await axios.put(`${API}/document-submissions/${submissionId}/review?status=${status}${notes ? `&notes=${encodeURIComponent(notes)}` : ''}`);
      alert(`Submission ${status}`);
      fetchSubmissions();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to update submission");
    }
  };

  const statusColors = {
    submitted: "warning",
    reviewed: "info",
    approved: "success",
    rejected: "error"
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Sub-tabs */}
      <div className="flex gap-2">
        <button
          onClick={() => setActiveSubTab("forms")}
          className={`px-4 py-2 rounded-md font-semibold text-sm transition-colors ${
            activeSubTab === "forms" ? 'bg-orange-500 text-slate-900' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
        >
          Form Submissions ({formSubmissions.length})
        </button>
        <button
          onClick={() => setActiveSubTab("documents")}
          className={`px-4 py-2 rounded-md font-semibold text-sm transition-colors ${
            activeSubTab === "documents" ? 'bg-orange-500 text-slate-900' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
        >
          Document Submissions ({docSubmissions.length})
        </button>
      </div>

      {/* Form Submissions */}
      {activeSubTab === "forms" && (
        <div className="space-y-3">
          {formSubmissions.length > 0 ? (
            formSubmissions.map(sub => (
              <Card key={sub.id}>
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-bold">{sub.template_name}</h3>
                    <p className="text-sm text-slate-500">
                      {isAdmin && <span className="font-medium text-slate-700">{sub.user_name} • </span>}
                      {new Date(sub.submitted_at).toLocaleDateString()} at {new Date(sub.submitted_at).toLocaleTimeString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    {sub.attachments?.length > 0 && (
                      <Badge variant="info">{sub.attachments.length} file(s)</Badge>
                    )}
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  </div>
                </div>
              </Card>
            ))
          ) : (
            <div className="text-center py-8 text-slate-500">
              <FileText className="w-10 h-10 mx-auto mb-3 text-slate-300" />
              <p className="font-semibold">No form submissions yet</p>
            </div>
          )}
        </div>
      )}

      {/* Document Submissions */}
      {activeSubTab === "documents" && (
        <div className="space-y-3">
          {docSubmissions.length > 0 ? (
            docSubmissions.map(sub => (
              <Card key={sub.id}>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-bold">{sub.document_name}</h3>
                      <Badge variant={statusColors[sub.status]}>{sub.status}</Badge>
                    </div>
                    <p className="text-sm text-slate-500">
                      {isAdmin && <span className="font-medium text-slate-700">{sub.user_name} ({sub.user_role}) • </span>}
                      Submitted {new Date(sub.submitted_at).toLocaleDateString()}
                    </p>
                    {sub.notes && (
                      <p className="text-sm text-slate-600 mt-1 bg-slate-50 p-2 rounded">{sub.notes}</p>
                    )}
                    {sub.review_notes && (
                      <p className="text-sm text-blue-600 mt-1">Review notes: {sub.review_notes}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openFile(sub.file_url, sub.file_name)}
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      View
                    </Button>
                    {isAdmin && sub.status === 'submitted' && (
                      <>
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => handleReview(sub.id, 'approved')}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleReview(sub.id, 'rejected')}
                        >
                          <X className="w-4 h-4 mr-1" />
                          Reject
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </Card>
            ))
          ) : (
            <div className="text-center py-8 text-slate-500">
              <Upload className="w-10 h-10 mx-auto mb-3 text-slate-300" />
              <p className="font-semibold">No document submissions yet</p>
              <p className="text-sm">Go to Company Documents tab to fill and submit documents</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Employees Page (Admin only)
const EmployeesPage = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [filter, setFilter] = useState("all");
  const [showPayslipModal, setShowPayslipModal] = useState(false);
  const [payslipUser, setPayslipUser] = useState(null);
  
  // Time history modal state
  const [showTimeHistory, setShowTimeHistory] = useState(false);
  const [timeHistoryUser, setTimeHistoryUser] = useState(null);
  const [timeHistoryData, setTimeHistoryData] = useState(null);
  const [historyDays, setHistoryDays] = useState(30);
  const [openHistoryDates, setOpenHistoryDates] = useState({});

  // Weekly summary modal state
  const [showWeeklySummary, setShowWeeklySummary] = useState(false);
  const [weeklySummaryUser, setWeeklySummaryUser] = useState(null);

  // Profile modal state
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profileUser, setProfileUser] = useState(null);
  const [profileData, setProfileData] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({});
  const [savingProfile, setSavingProfile] = useState(false);

  // Permission checks
  const canManageUsers = ['admin', 'manager', 'office_staff'].includes(currentUser?.role);
  const canAddUsers = ['admin'].includes(currentUser?.role); // Only admin can add users
  const canDeleteUsers = ['admin'].includes(currentUser?.role); // Only admin can delete
  const canUploadPayslips = ['admin', 'office_staff'].includes(currentUser?.role);
  const canViewTimeHistory = ['admin', 'manager', 'office_staff'].includes(currentUser?.role);
  const canViewProfiles = ['admin', 'manager', 'office_staff'].includes(currentUser?.role);
  const isWorkerOrSupervisor = ['supervisor', 'crane_operator', 'rigger', 'labourer'].includes(currentUser?.role);
  const canViewFullDetails = !isWorkerOrSupervisor; // Workers/supervisors only see name and position

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/users`);
      setUsers(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const fetchTimeHistory = async (userId) => {
    try {
      const res = await axios.get(`${API}/users/${userId}/time-history?days=${historyDays}`);
      setTimeHistoryData(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  const handleViewTimeHistory = (user) => {
    setTimeHistoryUser(user);
    setShowTimeHistory(true);
    fetchTimeHistory(user.id);
  };

  const handleViewProfile = async (user) => {
    setProfileUser(user);
    setShowProfileModal(true);
    setProfileLoading(true);
    setEditingProfile(false);
    try {
      const res = await axios.get(`${API}/users/${user.id}/profile`);
      setProfileData(res.data);
      setProfileForm(res.data);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load profile");
    } finally {
      setProfileLoading(false);
    }
  };

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      await axios.put(`${API}/users/${profileUser.id}/profile`, {
        date_of_birth: profileForm.date_of_birth,
        gender: profileForm.gender,
        address_line_1: profileForm.address_line_1,
        address_line_2: profileForm.address_line_2,
        suburb: profileForm.suburb,
        state: profileForm.state,
        postcode: profileForm.postcode,
        country: profileForm.country,
        tax_file_number: profileForm.tax_file_number,
        employment_type: profileForm.employment_type,
        start_date: profileForm.start_date,
        position_title: profileForm.position_title,
        // Pay / classification (conditional per employment_type — backend tolerates nulls)
        hourly_rate: profileForm.hourly_rate ? Number(profileForm.hourly_rate) : null,
        casual_loading_pct: profileForm.casual_loading_pct ? Number(profileForm.casual_loading_pct) : null,
        award_or_agreement: profileForm.award_or_agreement,
        award_level: profileForm.award_level,
        annual_salary: profileForm.annual_salary ? Number(profileForm.annual_salary) : null,
        salary_hours_per_week: profileForm.salary_hours_per_week ? Number(profileForm.salary_hours_per_week) : null,
        overtime_eligible: profileForm.overtime_eligible,
        // ABN contractor
        abn: profileForm.abn,
        business_name: profileForm.business_name,
        gst_registered: profileForm.gst_registered,
        contractor_insurance_expiry: profileForm.contractor_insurance_expiry,
        contractor_hourly_rate: profileForm.contractor_hourly_rate ? Number(profileForm.contractor_hourly_rate) : null,
        drivers_license_number: profileForm.drivers_license_number,
        drivers_license_class: profileForm.drivers_license_class,
        drivers_license_expiry: profileForm.drivers_license_expiry,
        medical_conditions: profileForm.medical_conditions,
        emergency_contact: profileForm.emergency_contact || {},
        superannuation: profileForm.superannuation || {},
        bank_details: profileForm.bank_details || {},
      });
      // Persist capabilities (admin can edit anyone's; endpoint handles authz)
      if (Array.isArray(profileForm.capabilities)) {
        await axios.put(`${API}/users/${profileUser.id}/capabilities`, { capabilities: profileForm.capabilities });
      }
      toast.success("Profile updated!");
      setEditingProfile(false);
      // Refresh profile data
      const res = await axios.get(`${API}/users/${profileUser.id}/profile`);
      setProfileData(res.data);
      setProfileForm(res.data);
    } catch (e) {
      toast.error("Failed to save profile");
    } finally {
      setSavingProfile(false);
    }
  };

  const updateProfileField = (field, value) => {
    setProfileForm(prev => ({ ...prev, [field]: value }));
  };

  const updateNestedField = (parent, field, value) => {
    setProfileForm(prev => ({
      ...prev,
      [parent]: { ...(prev[parent] || {}), [field]: value }
    }));
  };

  const formatDate = (dateStr) => dateStr ? new Date(dateStr).toLocaleDateString('en-AU') : '-';

  const filteredUsers = users.filter(u => {
    if (filter === "all") return true;
    return u.role === filter;
  });

  const roleColors = {
    admin: "error",
    manager: "warning",
    supervisor: "warning",
    crane_operator: "info",
    rigger: "success",
    labourer: "default",
    office_staff: "orange"
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="employees-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold uppercase tracking-tight font-heading">Team Members</h1>
          <p className="text-slate-600">{filteredUsers.length} employee(s)</p>
        </div>
        {canAddUsers && (
          <Button onClick={() => { setSelectedUser(null); setShowModal(true); }} data-testid="add-employee-btn">
            <Plus className="w-5 h-5 mr-2" />
            Add Employee
          </Button>
        )}
      </div>

      {/* Role Filter */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {["all", "admin", "manager", "supervisor", "crane_operator", "rigger", "labourer", "office_staff"].map(role => (
          <button
            key={role}
            onClick={() => setFilter(role)}
            className={`px-4 py-2 rounded-md font-semibold text-sm uppercase whitespace-nowrap transition-colors ${
              filter === role 
                ? 'bg-orange-500 text-slate-900' 
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            {role.replace('_', ' ')}
          </button>
        ))}
      </div>

      {/* Users Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredUsers.map(user => (
          <Card 
            key={user.id}
            interactive={canViewFullDetails || user.id === currentUser?.id}
            onClick={() => { 
              // Workers can only click on their own profile
              if (canViewFullDetails || user.id === currentUser?.id) {
                setSelectedUser(user); 
                setShowModal(true); 
              }
            }}
            data-testid={`employee-card-${user.id}`}
            className={!canViewFullDetails && user.id !== currentUser?.id ? 'cursor-default' : ''}
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-orange-500 rounded-md flex items-center justify-center text-slate-900 font-bold text-lg">
                {user.first_name?.[0]}{user.last_name?.[0]}
              </div>
              <div className="flex-1">
                <h3 className="font-bold">{user.first_name} {user.last_name}</h3>
                {/* Workers/supervisors only see name and role - no email */}
                {canViewFullDetails ? (
                  <p className="text-sm text-slate-500">{user.email}</p>
                ) : (
                  <p className="text-sm text-slate-400 italic">
                    {user.id === currentUser?.id ? user.email : ''}
                  </p>
                )}
              </div>
            </div>
            <div className="mt-4 flex items-center justify-between">
              <Badge variant={roleColors[user.role]}>{user.role?.replace('_', ' ')}</Badge>
              <div className="flex gap-2">
                {canViewProfiles && (
                  <button 
                    onClick={(e) => { e.stopPropagation(); handleViewProfile(user); }}
                    className="text-xs text-orange-600 hover:text-orange-800 font-semibold flex items-center gap-1"
                    title="View Full Profile"
                    data-testid={`view-profile-${user.id}`}
                  >
                    <User className="w-4 h-4" />
                  </button>
                )}
                {canViewTimeHistory && (
                  <button 
                    onClick={(e) => { e.stopPropagation(); handleViewTimeHistory(user); }}
                    className="text-xs text-blue-600 hover:text-blue-800 font-semibold flex items-center gap-1"
                    title="View Time History"
                    data-testid={`view-time-history-${user.id}`}
                  >
                    <Clock className="w-4 h-4" />
                  </button>
                )}
                {canViewTimeHistory && (
                  <button 
                    onClick={(e) => { e.stopPropagation(); setWeeklySummaryUser(user); setShowWeeklySummary(true); }}
                    className="text-xs text-purple-600 hover:text-purple-800 font-semibold flex items-center gap-1"
                    title="Weekly Summary (sites, travel, machines, receipts, LAA, reimbursements, signed docs)"
                    data-testid={`view-weekly-summary-${user.id}`}
                  >
                    <CalendarDays className="w-4 h-4" />
                  </button>
                )}
                {canUploadPayslips && (
                  <button 
                    onClick={(e) => { e.stopPropagation(); setPayslipUser(user); setShowPayslipModal(true); }}
                    className="text-xs text-green-600 hover:text-green-800 font-semibold"
                    title="Upload Payslip"
                  >
                    <FileText className="w-4 h-4" />
                  </button>
                )}
                {canViewFullDetails && (
                  <Badge variant={user.is_active ? "success" : "default"}>
                    {user.is_active ? "Active" : "Inactive"}
                  </Badge>
                )}
              </div>
            </div>
            {/* Capabilities row — quick overview for admin/manager */}
            {Array.isArray(user.capabilities) && user.capabilities.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1" data-testid={`employee-caps-${user.id}`}>
                {user.capabilities.map(c => (
                  <Badge key={c} variant="info" className="text-[10px] py-0.5 px-2">{c}</Badge>
                ))}
              </div>
            )}
          </Card>
        ))}
      </div>

      {/* Employee Time History Modal */}
      {showTimeHistory && (
        <Modal isOpen={showTimeHistory} onClose={() => { setShowTimeHistory(false); setTimeHistoryData(null); }} title={`Time History - ${timeHistoryUser?.first_name} ${timeHistoryUser?.last_name}`}>
          <div className="space-y-4">
            {/* Current Status */}
            {timeHistoryData?.current_status?.clocked_in && (
              <Card className="bg-green-50 border-green-200">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                  <div>
                    <p className="font-bold text-green-700">Currently Clocked In</p>
                    <p className="text-sm text-green-600">
                      {timeHistoryData.current_status.job_title} • {timeHistoryData.current_status.hours_so_far}h so far
                    </p>
                    <p className="text-xs text-green-500">
                      Since {new Date(timeHistoryData.current_status.clock_in_time).toLocaleString()}
                    </p>
                  </div>
                </div>
              </Card>
            )}

            {/* Summary */}
            {timeHistoryData?.summary && (
              <div className="grid grid-cols-3 gap-4">
                <Card className="text-center py-3">
                  <p className="text-2xl font-bold text-orange-500">{timeHistoryData.summary.total_hours}</p>
                  <p className="text-xs text-slate-500">Hours in Period</p>
                </Card>
                <Card className="text-center py-3">
                  <p className="text-2xl font-bold text-blue-500">{timeHistoryData.summary.total_entries}</p>
                  <p className="text-xs text-slate-500">Entries</p>
                </Card>
                <Card className="text-center py-3">
                  <p className="text-2xl font-bold text-green-500">{timeHistoryData.summary.days_worked}</p>
                  <p className="text-xs text-slate-500">Days Worked</p>
                </Card>
              </div>
            )}

            {/* Period Selector */}
            <div className="flex items-center gap-4">
              <Select
                value={historyDays}
                onChange={(e) => { setHistoryDays(parseInt(e.target.value)); fetchTimeHistory(timeHistoryUser.id); }}
                options={[
                  { value: 7, label: "Last 7 Days" },
                  { value: 30, label: "Last 30 Days" },
                  { value: 90, label: "Last 3 Months" },
                  { value: 180, label: "Last 6 Months" },
                  { value: 365, label: "Last 12 Months" }
                ]}
              />
            </div>

            {/* Time Entries grouped by date — calendar style */}
            <div className="max-h-[28rem] overflow-y-auto space-y-3" data-testid="time-history-by-date">
              {(timeHistoryData?.by_date || []).map(group => {
                const isOpen = openHistoryDates[group.date] !== false; // default open
                return (
                  <div key={group.date} className="border-2 border-slate-200 rounded-lg overflow-hidden" data-testid={`history-date-${group.date}`}>
                    <button
                      type="button"
                      onClick={() => setOpenHistoryDates(prev => ({ ...prev, [group.date]: !isOpen }))}
                      className="w-full flex items-center justify-between px-3 py-2 bg-slate-100 hover:bg-slate-200 transition"
                    >
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-orange-500" />
                        <p className="font-bold text-sm">
                          {new Date(group.date + 'T00:00:00').toLocaleDateString('en-AU', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' })}
                        </p>
                        <Badge variant="orange">{group.total_hours}h</Badge>
                        <span className="text-xs text-slate-500">{group.entries.length} entries</span>
                      </div>
                      <span className="text-xs text-slate-500 truncate max-w-[40%]">
                        {group.jobs.join(' · ') || 'No jobs'}
                      </span>
                    </button>
                    {isOpen && (
                      <div className="divide-y divide-slate-100">
                        {group.entries.map(entry => (
                          <div key={entry.id} className="flex items-center justify-between px-3 py-2 hover:bg-slate-50">
                            <div className="min-w-0">
                              <p className="text-sm text-blue-700 font-medium truncate" data-testid="history-entry-job">
                                {entry.job_title || 'No job'}
                              </p>
                              <p className="text-xs text-slate-500">
                                {new Date(entry.clock_in).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})} → {entry.clock_out ? new Date(entry.clock_out).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : <span className="text-green-600 font-bold">Active</span>}
                                {entry.job_location && <span className="ml-2 text-slate-400">· {entry.job_location}</span>}
                              </p>
                            </div>
                            <p className="font-bold text-orange-500 text-sm whitespace-nowrap">
                              {entry.total_hours ?? '-'}h
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
              {(!timeHistoryData?.by_date || timeHistoryData.by_date.length === 0) && (
                <p className="text-center text-slate-500 py-8">No time entries found</p>
              )}
            </div>
          </div>
        </Modal>
      )}

      {/* Employee Modal */}
      <EmployeeModal 
        isOpen={showModal}
        onClose={() => { setShowModal(false); setSelectedUser(null); }}
        user={selectedUser}
        onSave={fetchUsers}
        canManageUsers={canManageUsers}
        canDeleteUsers={canDeleteUsers}
        currentUserId={currentUser?.id}
      />

      {/* Weekly Summary Modal */}
      <EmployeeWeeklySummaryModal
        isOpen={showWeeklySummary}
        onClose={() => { setShowWeeklySummary(false); setWeeklySummaryUser(null); }}
        user={weeklySummaryUser}
      />

      {/* Payslip Upload Modal */}
      {showPayslipModal && (
        <PayslipUploadModal
          isOpen={showPayslipModal}
          onClose={() => { setShowPayslipModal(false); setPayslipUser(null); }}
          user={payslipUser}
        />
      )}

      {/* Employee Profile Modal */}
      {showProfileModal && profileUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto my-4">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-2xl font-bold uppercase">{profileUser.first_name} {profileUser.last_name}</h2>
                <p className="text-slate-500">{profileUser.email} • <span className="capitalize">{profileUser.role?.replace('_', ' ')}</span></p>
              </div>
              <div className="flex gap-2">
                {!profileLoading && (
                  <Button
                    onClick={() => editingProfile ? saveProfile() : setEditingProfile(true)}
                    disabled={savingProfile}
                  >
                    {savingProfile ? <Spinner size="sm" /> : editingProfile ? 'Save Changes' : 'Edit Profile'}
                  </Button>
                )}
                <Button variant="outline" onClick={() => { setShowProfileModal(false); setProfileUser(null); setEditingProfile(false); }}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {profileLoading ? (
              <div className="flex justify-center py-12"><Spinner size="lg" /></div>
            ) : profileData ? (
              <div className="grid md:grid-cols-2 gap-6">
                {/* Basic Info */}
                <div className="bg-slate-50 rounded-lg p-4">
                  <h3 className="font-bold uppercase text-sm text-slate-500 mb-4">Basic Information</h3>
                  <div className="space-y-3">
                    <div>
                      <label className="text-xs text-slate-500 uppercase">Phone</label>
                      <p className="font-medium">{profileData.phone || '-'}</p>
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 uppercase">Date of Birth</label>
                      {editingProfile ? (
                        <input type="date" value={profileForm.date_of_birth || ''} onChange={(e) => updateProfileField('date_of_birth', e.target.value)} className="w-full mt-1 px-3 py-2 border rounded-md" />
                      ) : (
                        <p className="font-medium">{formatDate(profileData.date_of_birth)}</p>
                      )}
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 uppercase">Gender</label>
                      {editingProfile ? (
                        <select value={profileForm.gender || ''} onChange={(e) => updateProfileField('gender', e.target.value)} className="w-full mt-1 px-3 py-2 border rounded-md">
                          <option value="">Select...</option>
                          <option value="male">Male</option>
                          <option value="female">Female</option>
                          <option value="other">Other</option>
                          <option value="prefer_not_to_say">Prefer not to say</option>
                        </select>
                      ) : (
                        <p className="font-medium capitalize">{profileData.gender?.replace('_', ' ') || '-'}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Address */}
                <div className="bg-slate-50 rounded-lg p-4">
                  <h3 className="font-bold uppercase text-sm text-slate-500 mb-4">Home Address</h3>
                  {editingProfile ? (
                    <div className="space-y-2">
                      <input placeholder="Address Line 1" value={profileForm.address_line_1 || ''} onChange={(e) => updateProfileField('address_line_1', e.target.value)} className="w-full px-3 py-2 border rounded-md" />
                      <input placeholder="Address Line 2" value={profileForm.address_line_2 || ''} onChange={(e) => updateProfileField('address_line_2', e.target.value)} className="w-full px-3 py-2 border rounded-md" />
                      <div className="grid grid-cols-2 gap-2">
                        <input placeholder="Suburb" value={profileForm.suburb || ''} onChange={(e) => updateProfileField('suburb', e.target.value)} className="px-3 py-2 border rounded-md" />
                        <select value={profileForm.state || ''} onChange={(e) => updateProfileField('state', e.target.value)} className="px-3 py-2 border rounded-md">
                          <option value="">State...</option>
                          <option value="NSW">NSW</option><option value="VIC">VIC</option><option value="QLD">QLD</option>
                          <option value="WA">WA</option><option value="SA">SA</option><option value="TAS">TAS</option>
                          <option value="NT">NT</option><option value="ACT">ACT</option>
                        </select>
                      </div>
                      <input placeholder="Postcode" value={profileForm.postcode || ''} onChange={(e) => updateProfileField('postcode', e.target.value)} className="w-32 px-3 py-2 border rounded-md" />
                    </div>
                  ) : (
                    <div className="space-y-1">
                      <p className="font-medium">{profileData.address_line_1 || '-'}</p>
                      {profileData.address_line_2 && <p className="font-medium">{profileData.address_line_2}</p>}
                      <p className="font-medium">{profileData.suburb}{profileData.suburb && profileData.state ? ', ' : ''}{profileData.state} {profileData.postcode}</p>
                    </div>
                  )}
                </div>

                {/* Emergency Contact */}
                <div className="bg-red-50 rounded-lg p-4">
                  <h3 className="font-bold uppercase text-sm text-red-600 mb-4 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" /> Emergency Contact
                  </h3>
                  {editingProfile ? (
                    <div className="space-y-2">
                      <input placeholder="Contact Name" value={profileForm.emergency_contact?.name || ''} onChange={(e) => updateNestedField('emergency_contact', 'name', e.target.value)} className="w-full px-3 py-2 border rounded-md" />
                      <input placeholder="Relationship" value={profileForm.emergency_contact?.relationship || ''} onChange={(e) => updateNestedField('emergency_contact', 'relationship', e.target.value)} className="w-full px-3 py-2 border rounded-md" />
                      <input placeholder="Phone" value={profileForm.emergency_contact?.phone || ''} onChange={(e) => updateNestedField('emergency_contact', 'phone', e.target.value)} className="w-full px-3 py-2 border rounded-md" />
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div><label className="text-xs text-slate-500">Name</label><p className="font-medium">{profileData.emergency_contact?.name || '-'}</p></div>
                      <div><label className="text-xs text-slate-500">Relationship</label><p className="font-medium">{profileData.emergency_contact?.relationship || '-'}</p></div>
                      <div><label className="text-xs text-slate-500">Phone</label><p className="font-medium">{profileData.emergency_contact?.phone || '-'}</p></div>
                    </div>
                  )}
                </div>

                {/* Employment Details */}
                <div className="bg-slate-50 rounded-lg p-4">
                  <h3 className="font-bold uppercase text-sm text-slate-500 mb-4">Employment Details</h3>
                  <div className="space-y-3">
                    <div>
                      <label className="text-xs text-slate-500 uppercase">Position Title</label>
                      {editingProfile ? (
                        <input value={profileForm.position_title || ''} onChange={(e) => updateProfileField('position_title', e.target.value)} className="w-full mt-1 px-3 py-2 border rounded-md" />
                      ) : (
                        <p className="font-medium">{profileData.position_title || '-'}</p>
                      )}
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 uppercase">Employment Type</label>
                      {editingProfile ? (
                        <select value={profileForm.employment_type || ''} onChange={(e) => updateProfileField('employment_type', e.target.value)} className="w-full mt-1 px-3 py-2 border rounded-md" data-testid="employment-type-select">
                          <option value="">Select...</option>
                          <option value="full_time">Full Time (PAYG)</option>
                          <option value="part_time">Part Time (PAYG)</option>
                          <option value="casual">Casual (PAYG + loading)</option>
                          <option value="salary">Salary (annual)</option>
                          <option value="abn_contractor">ABN Contractor (subbie)</option>
                        </select>
                      ) : (
                        <p className="font-medium capitalize">{(profileData.employment_type || '').replace(/_/g,' ') || '-'}</p>
                      )}
                    </div>
                    {/* Conditional fields per employment type — only show when relevant */}
                    {(() => {
                      const et = (editingProfile ? profileForm.employment_type : profileData.employment_type) || '';
                      if (!et) return null;
                      const isCasual = et === 'casual';
                      const isSalary = et === 'salary';
                      const isABN = et === 'abn_contractor' || et === 'contractor';
                      const isPAYG = et === 'full_time' || et === 'part_time' || isCasual;
                      return (
                        <div className="md:col-span-2 p-3 bg-slate-50 border border-slate-200 rounded space-y-3" data-testid={`employment-details-${et}`}>
                          <p className="text-[11px] font-bold uppercase text-slate-500">{isABN ? 'Contractor / ABN Details' : isSalary ? 'Salary Details' : 'Pay Rate / Award'}</p>
                          {/* PAYG: hourly + award */}
                          {isPAYG && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              <div>
                                <label className="text-xs text-slate-500 uppercase">Hourly Rate ($)</label>
                                {editingProfile ? (
                                  <input type="number" step="0.01" value={profileForm.hourly_rate || ''} onChange={(e) => updateProfileField('hourly_rate', e.target.value)} className="w-full mt-1 px-3 py-2 border rounded-md" data-testid="hourly-rate-input" />
                                ) : (<p className="font-medium">{profileData.hourly_rate ? `$${profileData.hourly_rate}` : '-'}</p>)}
                              </div>
                              {isCasual && (
                                <div>
                                  <label className="text-xs text-slate-500 uppercase">Casual Loading %</label>
                                  {editingProfile ? (
                                    <input type="number" step="0.1" value={profileForm.casual_loading_pct || ''} onChange={(e) => updateProfileField('casual_loading_pct', e.target.value)} placeholder="25" className="w-full mt-1 px-3 py-2 border rounded-md" data-testid="casual-loading-input" />
                                  ) : (<p className="font-medium">{profileData.casual_loading_pct ? `${profileData.casual_loading_pct}%` : '-'}</p>)}
                                </div>
                              )}
                              <div>
                                <label className="text-xs text-slate-500 uppercase">Award / Agreement</label>
                                {editingProfile ? (
                                  <input value={profileForm.award_or_agreement || ''} onChange={(e) => updateProfileField('award_or_agreement', e.target.value)} placeholder="e.g. Building & Construction Award 2020" className="w-full mt-1 px-3 py-2 border rounded-md" />
                                ) : (<p className="font-medium">{profileData.award_or_agreement || '-'}</p>)}
                              </div>
                              <div>
                                <label className="text-xs text-slate-500 uppercase">Award Level / Classification</label>
                                {editingProfile ? (
                                  <input value={profileForm.award_level || ''} onChange={(e) => updateProfileField('award_level', e.target.value)} placeholder="e.g. CW3" className="w-full mt-1 px-3 py-2 border rounded-md" />
                                ) : (<p className="font-medium">{profileData.award_level || '-'}</p>)}
                              </div>
                            </div>
                          )}
                          {/* Salary */}
                          {isSalary && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              <div>
                                <label className="text-xs text-slate-500 uppercase">Annual Salary ($)</label>
                                {editingProfile ? (
                                  <input type="number" step="0.01" value={profileForm.annual_salary || ''} onChange={(e) => updateProfileField('annual_salary', e.target.value)} className="w-full mt-1 px-3 py-2 border rounded-md" data-testid="annual-salary-input" />
                                ) : (<p className="font-medium">{profileData.annual_salary ? `$${Number(profileData.annual_salary).toLocaleString()}` : '-'}</p>)}
                              </div>
                              <div>
                                <label className="text-xs text-slate-500 uppercase">Standard Hours / Week</label>
                                {editingProfile ? (
                                  <input type="number" step="0.5" value={profileForm.salary_hours_per_week || ''} onChange={(e) => updateProfileField('salary_hours_per_week', e.target.value)} placeholder="38" className="w-full mt-1 px-3 py-2 border rounded-md" />
                                ) : (<p className="font-medium">{profileData.salary_hours_per_week || '-'}</p>)}
                              </div>
                              <div className="md:col-span-2">
                                <label className="inline-flex items-center gap-2 text-sm">
                                  <input type="checkbox" disabled={!editingProfile} checked={!!(editingProfile ? profileForm.overtime_eligible : profileData.overtime_eligible)} onChange={(e) => updateProfileField('overtime_eligible', e.target.checked)} />
                                  Eligible for overtime (above standard hours)
                                </label>
                              </div>
                            </div>
                          )}
                          {/* ABN Contractor */}
                          {isABN && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              <div>
                                <label className="text-xs text-slate-500 uppercase">ABN (11 digits)</label>
                                {editingProfile ? (
                                  <input value={profileForm.abn || ''} onChange={(e) => updateProfileField('abn', e.target.value)} placeholder="12 345 678 901" className="w-full mt-1 px-3 py-2 border rounded-md font-mono" data-testid="abn-input" />
                                ) : (<p className="font-medium font-mono">{profileData.abn || '-'}</p>)}
                              </div>
                              <div>
                                <label className="text-xs text-slate-500 uppercase">Business Name</label>
                                {editingProfile ? (
                                  <input value={profileForm.business_name || ''} onChange={(e) => updateProfileField('business_name', e.target.value)} className="w-full mt-1 px-3 py-2 border rounded-md" />
                                ) : (<p className="font-medium">{profileData.business_name || '-'}</p>)}
                              </div>
                              <div>
                                <label className="text-xs text-slate-500 uppercase">Contractor Hourly Rate ($)</label>
                                {editingProfile ? (
                                  <input type="number" step="0.01" value={profileForm.contractor_hourly_rate || ''} onChange={(e) => updateProfileField('contractor_hourly_rate', e.target.value)} className="w-full mt-1 px-3 py-2 border rounded-md" />
                                ) : (<p className="font-medium">{profileData.contractor_hourly_rate ? `$${profileData.contractor_hourly_rate}` : '-'}</p>)}
                              </div>
                              <div>
                                <label className="text-xs text-slate-500 uppercase">Insurance Expiry (Public Liability)</label>
                                {editingProfile ? (
                                  <input type="date" value={profileForm.contractor_insurance_expiry || ''} onChange={(e) => updateProfileField('contractor_insurance_expiry', e.target.value)} className="w-full mt-1 px-3 py-2 border rounded-md" />
                                ) : (<p className="font-medium">{formatDate(profileData.contractor_insurance_expiry)}</p>)}
                              </div>
                              <div className="md:col-span-2">
                                <label className="inline-flex items-center gap-2 text-sm">
                                  <input type="checkbox" disabled={!editingProfile} checked={!!(editingProfile ? profileForm.gst_registered : profileData.gst_registered)} onChange={(e) => updateProfileField('gst_registered', e.target.checked)} />
                                  GST registered (charges 10% GST on invoices)
                                </label>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                    <div>
                      <label className="text-xs text-slate-500 uppercase">Start Date</label>
                      {editingProfile ? (
                        <input type="date" value={profileForm.start_date || ''} onChange={(e) => updateProfileField('start_date', e.target.value)} className="w-full mt-1 px-3 py-2 border rounded-md" />
                      ) : (
                        <p className="font-medium">{formatDate(profileData.start_date)}</p>
                      )}
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 uppercase">Job Capabilities</label>
                      <p className="text-[11px] text-slate-500 mb-2">Which seats can this person fill on a job? Drives operator / dogman / rigger dropdowns.</p>
                      <div className="space-y-2" data-testid="employee-capabilities">
                        {['operator', 'dogman', 'rigger'].map(cap => {
                          const current = Array.isArray(profileForm.capabilities) ? profileForm.capabilities : (profileData.capabilities || []);
                          const on = current.includes(cap);
                          return (
                            <label key={cap} className={`flex items-center gap-2 p-2 border rounded text-sm ${editingProfile ? 'cursor-pointer' : 'cursor-default'} ${on ? 'border-orange-500 bg-orange-50' : 'border-slate-200 bg-white'}`} data-testid={`employee-capability-${cap}`}>
                              <input
                                type="checkbox"
                                className="w-4 h-4"
                                disabled={!editingProfile}
                                checked={on}
                                onChange={() => {
                                  const next = on ? current.filter(c => c !== cap) : [...current, cap];
                                  updateProfileField('capabilities', next);
                                }}
                              />
                              <span className="capitalize font-semibold">{cap}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Tax & Super */}
                <div className="bg-green-50 rounded-lg p-4">
                  <h3 className="font-bold uppercase text-sm text-green-700 mb-4">Tax & Superannuation</h3>
                  <div className="space-y-3">
                    <div>
                      <label className="text-xs text-slate-500 uppercase">Tax File Number (TFN)</label>
                      {editingProfile ? (
                        <input type="password" placeholder="Enter TFN" value={profileForm.tax_file_number || ''} onChange={(e) => updateProfileField('tax_file_number', e.target.value)} className="w-full mt-1 px-3 py-2 border rounded-md" />
                      ) : (
                        <p className="font-medium">{profileData.tax_file_number ? '••• ••• •••' : 'Not provided'}</p>
                      )}
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 uppercase">Super Fund</label>
                      {editingProfile ? (
                        <input value={profileForm.superannuation?.fund_name || ''} onChange={(e) => updateNestedField('superannuation', 'fund_name', e.target.value)} className="w-full mt-1 px-3 py-2 border rounded-md" />
                      ) : (
                        <p className="font-medium">{profileData.superannuation?.fund_name || '-'}</p>
                      )}
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 uppercase">Member Number</label>
                      {editingProfile ? (
                        <input value={profileForm.superannuation?.member_number || ''} onChange={(e) => updateNestedField('superannuation', 'member_number', e.target.value)} className="w-full mt-1 px-3 py-2 border rounded-md" />
                      ) : (
                        <p className="font-medium">{profileData.superannuation?.member_number || '-'}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Bank Details */}
                <div className="bg-blue-50 rounded-lg p-4">
                  <h3 className="font-bold uppercase text-sm text-blue-700 mb-4">Bank Details (Payroll)</h3>
                  {editingProfile ? (
                    <div className="space-y-2">
                      <input placeholder="Bank Name" value={profileForm.bank_details?.bank_name || ''} onChange={(e) => updateNestedField('bank_details', 'bank_name', e.target.value)} className="w-full px-3 py-2 border rounded-md" />
                      <input placeholder="Account Name" value={profileForm.bank_details?.account_name || ''} onChange={(e) => updateNestedField('bank_details', 'account_name', e.target.value)} className="w-full px-3 py-2 border rounded-md" />
                      <div className="grid grid-cols-2 gap-2">
                        <input placeholder="BSB" value={profileForm.bank_details?.bsb || ''} onChange={(e) => updateNestedField('bank_details', 'bsb', e.target.value)} className="px-3 py-2 border rounded-md" />
                        <input placeholder="Account Number" value={profileForm.bank_details?.account_number || ''} onChange={(e) => updateNestedField('bank_details', 'account_number', e.target.value)} className="px-3 py-2 border rounded-md" />
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div><label className="text-xs text-slate-500">Bank</label><p className="font-medium">{profileData.bank_details?.bank_name || '-'}</p></div>
                      <div><label className="text-xs text-slate-500">BSB</label><p className="font-medium">{profileData.bank_details?.bsb || '-'}</p></div>
                      <div><label className="text-xs text-slate-500">Account</label><p className="font-medium">{profileData.bank_details?.account_number ? '•••••' + profileData.bank_details.account_number.slice(-4) : '-'}</p></div>
                    </div>
                  )}
                </div>

                {/* Driver's License */}
                <div className="bg-slate-50 rounded-lg p-4">
                  <h3 className="font-bold uppercase text-sm text-slate-500 mb-4">Driver's License</h3>
                  <div className="space-y-3">
                    <div>
                      <label className="text-xs text-slate-500 uppercase">License Number</label>
                      {editingProfile ? (
                        <input value={profileForm.drivers_license_number || ''} onChange={(e) => updateProfileField('drivers_license_number', e.target.value)} className="w-full mt-1 px-3 py-2 border rounded-md" />
                      ) : (
                        <p className="font-medium">{profileData.drivers_license_number || '-'}</p>
                      )}
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 uppercase">License Class</label>
                      {editingProfile ? (
                        <input placeholder="e.g. C, LR, MR, HR" value={profileForm.drivers_license_class || ''} onChange={(e) => updateProfileField('drivers_license_class', e.target.value)} className="w-full mt-1 px-3 py-2 border rounded-md" />
                      ) : (
                        <p className="font-medium">{profileData.drivers_license_class || '-'}</p>
                      )}
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 uppercase">Expiry</label>
                      {editingProfile ? (
                        <input type="date" value={profileForm.drivers_license_expiry || ''} onChange={(e) => updateProfileField('drivers_license_expiry', e.target.value)} className="w-full mt-1 px-3 py-2 border rounded-md" />
                      ) : (
                        <p className="font-medium">{formatDate(profileData.drivers_license_expiry)}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Medical Conditions */}
                <div className="bg-amber-50 rounded-lg p-4">
                  <h3 className="font-bold uppercase text-sm text-amber-700 mb-4">Medical Conditions</h3>
                  {editingProfile ? (
                    <textarea placeholder="Any conditions site supervisors should know..." value={profileForm.medical_conditions || ''} onChange={(e) => updateProfileField('medical_conditions', e.target.value)} className="w-full px-3 py-2 border rounded-md" rows={3} />
                  ) : (
                    <p className="font-medium">{profileData.medical_conditions || 'None specified'}</p>
                  )}
                </div>

                {/* Certificates */}
                {profileData.certificates?.length > 0 && (
                  <div className="md:col-span-2 bg-slate-50 rounded-lg p-4">
                    <h3 className="font-bold uppercase text-sm text-slate-500 mb-4">Certificates & Tickets ({profileData.certificates.length})</h3>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {profileData.certificates.map(cert => (
                        <div key={cert.id} className="bg-white p-3 rounded border">
                          <p className="font-bold text-sm">{cert.name}</p>
                          <p className="text-xs text-slate-500 capitalize">{cert.certificate_type}</p>
                          {cert.expiry_date && (
                            <p className={`text-xs mt-1 ${new Date(cert.expiry_date) < new Date() ? 'text-red-600 font-bold' : 'text-slate-400'}`}>
                              Expires: {formatDate(cert.expiry_date)}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-center text-slate-500 py-8">Failed to load profile data</p>
            )}

            {editingProfile && (
              <div className="flex gap-2 justify-end mt-6 pt-4 border-t">
                <Button variant="outline" onClick={() => { setEditingProfile(false); setProfileForm(profileData); }}>Cancel</Button>
                <Button onClick={saveProfile} disabled={savingProfile}>{savingProfile ? <Spinner size="sm" /> : 'Save Changes'}</Button>
              </div>
            )}
          </Card>
        </div>
      )}
    </div>
  );
};

// Payslip Upload Modal
const PayslipUploadModal = ({ isOpen, onClose, user }) => {
  const [formData, setFormData] = useState({
    pay_period_start: "",
    pay_period_end: "",
    file_url: "",
    file_name: "",
    notes: ""
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.file_url) {
      alert("Please upload a payslip file");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/payslips`, {
        user_id: user.id,
        ...formData
      });
      alert("Payslip uploaded successfully!");
      onClose();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to upload payslip");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Upload Payslip for ${user?.first_name} ${user?.last_name}`}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Pay Period Start"
            type="date"
            value={formData.pay_period_start}
            onChange={(e) => setFormData({...formData, pay_period_start: e.target.value})}
            required
          />
          <Input
            label="Pay Period End"
            type="date"
            value={formData.pay_period_end}
            onChange={(e) => setFormData({...formData, pay_period_end: e.target.value})}
            required
          />
        </div>

        <FileUploadInput
          label="Payslip File (PDF)"
          accept=".pdf"
          onFileSelect={(fileUrl, fileName) => setFormData({...formData, file_url: fileUrl, file_name: fileName})}
          currentFile={formData.file_name}
          onClear={() => setFormData({...formData, file_url: "", file_name: ""})}
        />

        <Textarea
          label="Notes (optional)"
          value={formData.notes}
          onChange={(e) => setFormData({...formData, notes: e.target.value})}
          placeholder="Any additional notes..."
        />

        <div className="flex gap-4">
          <Button type="submit" disabled={loading || !formData.file_url}>
            {loading ? <Spinner size="sm" /> : "Upload Payslip"}
          </Button>
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
        </div>
      </form>
    </Modal>
  );
};

// Employee Modal
const EmployeeModal = ({ isOpen, onClose, user, onSave, canManageUsers, canDeleteUsers, currentUserId }) => {
  const [activeTab, setActiveTab] = useState('profile');
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    first_name: "",
    last_name: "",
    role: "labourer",
    phone: ""
  });
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // Certificates state
  const [certificates, setCertificates] = useState([]);
  const [showAddCert, setShowAddCert] = useState(false);
  const [newCert, setNewCert] = useState({
    name: "",
    certificate_type: "ticket",
    certificate_number: "",
    issue_date: "",
    expiry_date: "",
    issuing_authority: "",
    file_url: null,
    notes: ""
  });

  // Workers can only edit their own profile (except role)
  const isOwnProfile = user?.id === currentUserId;
  const canEdit = canManageUsers || isOwnProfile;
  const canChangeRole = canManageUsers; // Only managers+ can change roles
  const canDelete = canDeleteUsers && !isOwnProfile; // Can't delete yourself
  const canResetPassword = canManageUsers;
  const canManageCerts = canManageUsers || isOwnProfile;

  useEffect(() => {
    if (user) {
      setFormData({
        email: user.email || "",
        password: "",
        first_name: user.first_name || "",
        last_name: user.last_name || "",
        role: user.role || "labourer",
        phone: user.phone || ""
      });
      fetchCertificates();
    } else {
      setFormData({
        email: "",
        password: "",
        first_name: "",
        last_name: "",
        role: "labourer",
        phone: ""
      });
      setCertificates([]);
    }
  }, [user]);

  const fetchCertificates = async () => {
    if (!user) return;
    try {
      const res = await axios.get(`${API}/users/${user.id}/certificates`);
      setCertificates(res.data);
    } catch (e) {
      console.error('Failed to fetch certificates:', e);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (user) {
        const updateData = { ...formData };
        delete updateData.email;
        delete updateData.password;
        await axios.put(`${API}/users/${user.id}`, updateData);
      } else {
        await axios.post(`${API}/auth/register`, formData);
      }
      onSave();
      onClose();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to save employee");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete ${user.first_name} ${user.last_name}? This action cannot be undone.`)) {
      return;
    }
    setDeleting(true);
    try {
      await axios.delete(`${API}/users/${user.id}`);
      onSave();
      onClose();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to delete employee");
    } finally {
      setDeleting(false);
    }
  };

  const handleResetPassword = async () => {
    const newPassword = window.prompt(`Enter new password for ${user.first_name} ${user.last_name}:\n(minimum 6 characters)`);
    if (!newPassword) return;
    if (newPassword.length < 6) {
      alert("Password must be at least 6 characters");
      return;
    }
    try {
      await axios.put(`${API}/users/${user.id}/reset-password`, { new_password: newPassword });
      alert(`Password reset successfully!\n\nNew password: ${newPassword}\n\nPlease share this with the employee securely.`);
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to reset password");
    }
  };

  const handleCertFileUpload = async (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      setNewCert({...newCert, file_url: reader.result});
    };
    reader.readAsDataURL(file);
  };

  const handleAddCertificate = async (e) => {
    e.preventDefault();
    if (!newCert.name) {
      alert("Please enter a certificate name");
      return;
    }
    try {
      await axios.post(`${API}/users/${user.id}/certificates`, newCert);
      setShowAddCert(false);
      setNewCert({
        name: "",
        certificate_type: "ticket",
        certificate_number: "",
        issue_date: "",
        expiry_date: "",
        issuing_authority: "",
        file_url: null,
        notes: ""
      });
      fetchCertificates();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to add certificate");
    }
  };

  const handleDeleteCertificate = async (certId) => {
    if (!window.confirm("Are you sure you want to delete this certificate?")) return;
    try {
      await axios.delete(`${API}/users/${user.id}/certificates/${certId}`);
      fetchCertificates();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to delete certificate");
    }
  };

  const isExpiringSoon = (expiryDate) => {
    if (!expiryDate) return false;
    const expiry = new Date(expiryDate);
    const today = new Date();
    const daysUntilExpiry = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
    return daysUntilExpiry <= 30 && daysUntilExpiry >= 0;
  };

  const isExpired = (expiryDate) => {
    if (!expiryDate) return false;
    return new Date(expiryDate) < new Date();
  };

  const certTypeLabels = {
    ticket: "Ticket",
    license: "License",
    certification: "Certification",
    training: "Training",
    induction: "Induction"
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={user ? `${user.first_name} ${user.last_name}` : "Add Employee"}>
      {/* Tabs for existing users */}
      {user && (
        <div className="flex gap-2 mb-6 border-b border-slate-200 pb-2">
          <button
            onClick={() => setActiveTab('profile')}
            className={`px-4 py-2 font-semibold text-sm rounded-t transition-colors ${
              activeTab === 'profile' ? 'bg-orange-500 text-slate-900' : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            Profile
          </button>
          <button
            onClick={() => setActiveTab('certificates')}
            className={`px-4 py-2 font-semibold text-sm rounded-t transition-colors flex items-center gap-2 ${
              activeTab === 'certificates' ? 'bg-orange-500 text-slate-900' : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            Tickets & Certificates
            {certificates.length > 0 && (
              <span className="bg-slate-200 text-slate-600 text-xs px-2 py-0.5 rounded-full">{certificates.length}</span>
            )}
          </button>
        </div>
      )}

      {/* Profile Tab */}
      {(activeTab === 'profile' || !user) && (
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <Input
              label="First Name"
              value={formData.first_name}
              onChange={(e) => setFormData({...formData, first_name: e.target.value})}
              required
            />
            <Input
              label="Last Name"
              value={formData.last_name}
              onChange={(e) => setFormData({...formData, last_name: e.target.value})}
              required
            />
          </div>

          {!user && (
            <>
              <Input
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                required
              />
              <Input
                label="Password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                required
              />
            </>
          )}

          <Input
            label="Phone"
            type="tel"
            value={formData.phone}
            onChange={(e) => setFormData({...formData, phone: e.target.value})}
          />

          {canChangeRole ? (
            <Select
              label="Role"
              value={formData.role}
              onChange={(e) => setFormData({...formData, role: e.target.value})}
              options={[
                { value: "admin", label: "Admin" },
                { value: "manager", label: "Manager" },
                { value: "supervisor", label: "Supervisor" },
                { value: "crane_operator", label: "Crane Operator" },
                { value: "rigger", label: "Rigger" },
                { value: "labourer", label: "Labourer" },
                { value: "office_staff", label: "Office Staff" }
              ]}
            />
          ) : (
            <div className="p-3 bg-slate-100 rounded-lg">
              <p className="text-sm font-bold uppercase text-slate-500">Role</p>
              <p className="text-lg capitalize">{formData.role?.replace('_', ' ')}</p>
            </div>
          )}

          <div className="flex gap-4 flex-wrap">
            {canEdit && (
              <Button type="submit" disabled={loading} data-testid="save-employee-btn">
                {loading ? <Spinner size="sm" /> : (user ? "Update" : "Add Employee")}
              </Button>
            )}
            {user && canResetPassword && (
              <Button 
                type="button" 
                variant="outline"
                onClick={handleResetPassword}
                data-testid="reset-password-btn"
              >
                <Key className="w-4 h-4 mr-2" />Reset Password
              </Button>
            )}
            {user && canDelete && (
              <Button 
                type="button" 
                variant="destructive" 
                onClick={handleDelete}
                disabled={deleting}
                data-testid="delete-employee-btn"
              >
                {deleting ? <Spinner size="sm" /> : <><Trash2 className="w-4 h-4 mr-2" />Delete</>}
              </Button>
            )}
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          </div>
        </form>
      )}

      {/* Certificates Tab */}
      {activeTab === 'certificates' && user && (
        <div className="space-y-4">
          {/* Add Certificate Button */}
          {canManageCerts && !showAddCert && (
            <Button onClick={() => setShowAddCert(true)} data-testid="add-certificate-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Ticket/Certificate
            </Button>
          )}

          {/* Add Certificate Form */}
          {showAddCert && (
            <Card className="bg-blue-50 border-blue-200">
              <h4 className="font-bold uppercase mb-4">Add New Ticket/Certificate</h4>
              <form onSubmit={handleAddCertificate} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <Input
                    label="Certificate/Ticket Name *"
                    value={newCert.name}
                    onChange={(e) => setNewCert({...newCert, name: e.target.value})}
                    placeholder="e.g., High Risk Work License"
                    required
                  />
                  <Select
                    label="Type"
                    value={newCert.certificate_type}
                    onChange={(e) => setNewCert({...newCert, certificate_type: e.target.value})}
                    options={[
                      { value: "ticket", label: "Ticket" },
                      { value: "license", label: "License" },
                      { value: "certification", label: "Certification" },
                      { value: "training", label: "Training" },
                      { value: "induction", label: "Site Induction" }
                    ]}
                  />
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <Input
                    label="Certificate/License Number"
                    value={newCert.certificate_number}
                    onChange={(e) => setNewCert({...newCert, certificate_number: e.target.value})}
                    placeholder="e.g., CN12345678"
                  />
                  <Input
                    label="Issuing Authority"
                    value={newCert.issuing_authority}
                    onChange={(e) => setNewCert({...newCert, issuing_authority: e.target.value})}
                    placeholder="e.g., SafeWork NSW"
                  />
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-1.5">
                    <label className="text-sm font-bold uppercase text-slate-600">Issue Date</label>
                    <input
                      type="date"
                      value={newCert.issue_date}
                      onChange={(e) => setNewCert({...newCert, issue_date: e.target.value})}
                      className="h-12 bg-white border-2 border-slate-200 focus:border-orange-500 focus:outline-none rounded-md px-3"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className="text-sm font-bold uppercase text-slate-600">Expiry Date</label>
                    <input
                      type="date"
                      value={newCert.expiry_date}
                      onChange={(e) => setNewCert({...newCert, expiry_date: e.target.value})}
                      className="h-12 bg-white border-2 border-slate-200 focus:border-orange-500 focus:outline-none rounded-md px-3"
                    />
                  </div>
                </div>
                
                {/* File Upload */}
                <div>
                  <label className="block text-sm font-bold uppercase text-slate-600 mb-2">
                    Upload Document (Photo/PDF)
                  </label>
                  {newCert.file_url ? (
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span className="text-sm text-green-700">Document uploaded</span>
                      <Button 
                        type="button"
                        variant="ghost" 
                        size="sm" 
                        onClick={() => setNewCert({...newCert, file_url: null})}
                      >
                        Remove
                      </Button>
                    </div>
                  ) : (
                    <input
                      type="file"
                      accept="image/*,.pdf"
                      onChange={(e) => handleCertFileUpload(e.target.files[0])}
                      className="block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-orange-50 file:text-orange-700 hover:file:bg-orange-100"
                    />
                  )}
                </div>

                <Textarea
                  label="Notes"
                  value={newCert.notes}
                  onChange={(e) => setNewCert({...newCert, notes: e.target.value})}
                  placeholder="Any additional notes..."
                />

                <div className="flex gap-3">
                  <Button type="submit">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Certificate
                  </Button>
                  <Button type="button" variant="ghost" onClick={() => setShowAddCert(false)}>
                    Cancel
                  </Button>
                </div>
              </form>
            </Card>
          )}

          {/* Certificates List */}
          <div className="space-y-3">
            {certificates.map(cert => (
              <Card 
                key={cert.id} 
                className={`${
                  isExpired(cert.expiry_date) ? 'border-red-300 bg-red-50' : 
                  isExpiringSoon(cert.expiry_date) ? 'border-amber-300 bg-amber-50' : ''
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-bold">{cert.name}</h4>
                      <Badge variant={
                        cert.certificate_type === 'ticket' ? 'info' :
                        cert.certificate_type === 'license' ? 'success' :
                        cert.certificate_type === 'certification' ? 'warning' : 'default'
                      }>
                        {certTypeLabels[cert.certificate_type] || cert.certificate_type}
                      </Badge>
                      {isExpired(cert.expiry_date) && (
                        <Badge variant="error">EXPIRED</Badge>
                      )}
                      {isExpiringSoon(cert.expiry_date) && !isExpired(cert.expiry_date) && (
                        <Badge variant="warning">EXPIRING SOON</Badge>
                      )}
                    </div>
                    {cert.certificate_number && (
                      <p className="text-sm text-slate-600">#{cert.certificate_number}</p>
                    )}
                    {cert.issuing_authority && (
                      <p className="text-sm text-slate-500">Issued by: {cert.issuing_authority}</p>
                    )}
                    <div className="flex gap-4 mt-2 text-xs text-slate-500">
                      {cert.issue_date && (
                        <span>Issued: {new Date(cert.issue_date).toLocaleDateString()}</span>
                      )}
                      {cert.expiry_date && (
                        <span className={isExpired(cert.expiry_date) ? 'text-red-600 font-semibold' : ''}>
                          Expires: {new Date(cert.expiry_date).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                    {cert.notes && (
                      <p className="text-sm text-slate-500 mt-2">{cert.notes}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {cert.file_url && (
                      <a 
                        href={cert.file_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800"
                        title="View Document"
                      >
                        <Eye className="w-5 h-5" />
                      </a>
                    )}
                    {canManageCerts && (
                      <button
                        onClick={() => handleDeleteCertificate(cert.id)}
                        className="text-red-600 hover:text-red-800"
                        title="Delete Certificate"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                </div>
              </Card>
            ))}

            {certificates.length === 0 && !showAddCert && (
              <div className="text-center py-8 text-slate-500">
                <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p className="text-lg font-semibold">No certificates or tickets</p>
                <p className="text-sm">Add tickets and certificates to track employee qualifications</p>
              </div>
            )}
          </div>

          <Button type="button" variant="ghost" onClick={onClose} className="mt-4">
            Close
          </Button>
        </div>
      )}
    </Modal>
  );
};

// ==================== DAYBOOK PAGE ====================
const DaybookPage = () => {
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    fetchEntries();
  }, []);

  const fetchEntries = async () => {
    try {
      const res = await axios.get(`${API}/daybook`);
      setEntries(res.data);
    } catch (e) {
      console.error('Failed to fetch daybook entries:', e);
    } finally {
      setLoading(false);
    }
  };

  const filteredEntries = entries.filter(e => {
    if (filter === "all") return true;
    return e.status === filter;
  });

  const getStatusBadge = (status) => {
    const variants = {
      draft: "default",
      pending_client_sign: "warning",
      pending_rep_sign: "warning",
      completed: "success"
    };
    const labels = {
      draft: "Draft",
      pending_client_sign: "Awaiting Client",
      pending_rep_sign: "Awaiting Rep",
      completed: "Completed"
    };
    return <Badge variant={variants[status] || "default"}>{labels[status] || status}</Badge>;
  };

  const getWorkTypeBadge = (type) => {
    const colors = {
      daywork: "bg-blue-100 text-blue-800",
      variation: "bg-purple-100 text-purple-800",
      crane_hire: "bg-orange-100 text-orange-800",
      general: "bg-slate-100 text-slate-800"
    };
    const labels = {
      daywork: "Daywork",
      variation: "Variation",
      crane_hire: "Crane Hire",
      general: "General"
    };
    return (
      <span className={`px-2 py-1 rounded text-xs font-semibold ${colors[type] || colors.general}`}>
        {labels[type] || type}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="daybook-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <h1 className="text-3xl font-bold uppercase tracking-tight font-heading">Daybook</h1>
        <Button onClick={() => { setSelectedEntry(null); setShowModal(true); }} data-testid="new-daybook-btn">
          <Plus className="w-5 h-5 mr-2" />
          New Entry
        </Button>
      </div>

      {/* Intro Card */}
      <Card className="bg-gradient-to-r from-orange-50 to-amber-50 border-orange-200">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-orange-500 rounded-lg flex items-center justify-center">
            <ClipboardList className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="font-bold text-lg text-orange-900">Dayworks Sign-Off Book</h3>
            <p className="text-sm text-orange-700">
              Record dayworks, variations, and crane hire. Get builder signatures for invoicing.
            </p>
          </div>
        </div>
      </Card>

      {/* Filters */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {[
          { id: "all", label: "All" },
          { id: "draft", label: "Drafts" },
          { id: "pending_client_sign", label: "Awaiting Client" },
          { id: "pending_rep_sign", label: "Awaiting Rep" },
          { id: "completed", label: "Completed" }
        ].map(f => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className={`px-4 py-2 rounded-lg font-semibold text-sm whitespace-nowrap transition-colors ${
              filter === f.id 
                ? 'bg-orange-500 text-white' 
                : 'bg-white text-slate-600 border-2 border-slate-200 hover:border-orange-300'
            }`}
          >
            {f.label}
            <span className="ml-2 px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-xs">
              {entries.filter(e => f.id === "all" ? true : e.status === f.id).length}
            </span>
          </button>
        ))}
      </div>

      {/* Entries List */}
      {filteredEntries.length === 0 ? (
        <Card className="text-center py-12">
          <ClipboardList className="w-16 h-16 mx-auto mb-4 text-slate-300" />
          <h3 className="text-lg font-semibold text-slate-600">No Daybook Entries</h3>
          <p className="text-slate-500 mb-4">Create your first daywork entry to get started</p>
          <Button onClick={() => { setSelectedEntry(null); setShowModal(true); }}>
            <Plus className="w-4 h-4 mr-2" />
            Create Entry
          </Button>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredEntries.map(entry => (
            <Card 
              key={entry.id} 
              interactive 
              onClick={() => { setSelectedEntry(entry); setShowModal(true); }}
              data-testid={`daybook-entry-${entry.id}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  {getWorkTypeBadge(entry.work_type)}
                </div>
                {getStatusBadge(entry.status)}
              </div>
              
              <h3 className="font-bold text-lg mb-1">{entry.job_title}</h3>
              <p className="text-sm text-slate-500 mb-2">{entry.client_name}</p>
              
              <div className="space-y-1 text-sm text-slate-600">
                <p className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-slate-400" />
                  {new Date(entry.work_date).toLocaleDateString()}
                </p>
                <p className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-slate-400" />
                  {entry.job_location || entry.location_details || "No location"}
                </p>
              </div>
              
              <div className="mt-4 pt-4 border-t flex items-center justify-between text-xs text-slate-400">
                <span>{entry.labour_entries?.length || 0} workers, {entry.equipment_entries?.length || 0} machines</span>
                <span>{entry.photos?.length || 0} photos</span>
              </div>
              
              {/* Signature indicators */}
              <div className="mt-3 flex gap-2">
                {entry.client_signature ? (
                  <span className="flex items-center gap-1 text-xs text-green-600">
                    <CheckCircle className="w-3 h-3" /> Client Signed
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-xs text-slate-400">
                    <Edit3 className="w-3 h-3" /> Client
                  </span>
                )}
                {entry.rep_signature ? (
                  <span className="flex items-center gap-1 text-xs text-green-600">
                    <CheckCircle className="w-3 h-3" /> Rep Signed
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-xs text-slate-400">
                    <Edit3 className="w-3 h-3" /> Rep
                  </span>
                )}
              </div>

              {/* View PDF button for completed entries — admin, office, supervisor */}
              {entry.status === 'completed' && ['admin', 'office_staff', 'supervisor', 'manager'].includes(user?.role) && (
                <div className="mt-3 pt-3 border-t flex gap-2" onClick={(e) => e.stopPropagation()}>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={async () => {
                      try {
                        const res = await axios.get(`${API}/daybook/${entry.id}/pdf`, { responseType: 'blob' });
                        const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                        window.open(url, '_blank', 'noopener,noreferrer');
                        setTimeout(() => URL.revokeObjectURL(url), 60000);
                      } catch (err) {
                        toast.error(err.response?.data?.detail || "Couldn't open PDF");
                      }
                    }}
                    data-testid={`view-daybook-pdf-${entry.id}`}
                  >
                    <FileText className="w-3.5 h-3.5 mr-1" /> View PDF
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={async () => {
                      try {
                        const res = await axios.get(`${API}/daybook/${entry.id}/pdf`, { responseType: 'blob' });
                        const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `daybook_${entry.client_name || 'docket'}_${entry.work_date || 'unknown'}.pdf`;
                        document.body.appendChild(a);
                        a.click();
                        a.remove();
                        setTimeout(() => URL.revokeObjectURL(url), 60000);
                      } catch (err) {
                        toast.error(err.response?.data?.detail || "Couldn't download PDF");
                      }
                    }}
                    data-testid={`download-daybook-pdf-${entry.id}`}
                  >
                    <Download className="w-3.5 h-3.5 mr-1" /> Download
                  </Button>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* Daybook Modal */}
      <DaybookModal
        isOpen={showModal}
        onClose={() => { setShowModal(false); setSelectedEntry(null); }}
        entry={selectedEntry}
        onSave={fetchEntries}
      />
    </div>
  );
};

// Daybook Entry Modal
const DaybookModal = ({ isOpen, onClose, entry, onSave }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1: Details, 2: Labour/Equipment, 3: Photos, 4: Sign
  const [jobs, setJobs] = useState([]);
  const [clients, setClients] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [machines, setMachines] = useState([]);
  const [terms, setTerms] = useState("");
  const [showTerms, setShowTerms] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  
  const [formData, setFormData] = useState({
    job_id: "",
    client_id: "",
    work_date: new Date().toISOString().split('T')[0],
    work_type: "daywork",
    description: "",
    location_details: "",
    labour_entries: [],
    equipment_entries: [],
    photos: [],
    notes: "",
    work_summary: ""
  });

  // Signature states
  const [clientName, setClientName] = useState("");
  const [showClientSign, setShowClientSign] = useState(false);
  const [showRepSign, setShowRepSign] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchData();
      if (entry) {
        setFormData({
          job_id: entry.job_id || "",
          client_id: entry.client_id || "",
          work_date: entry.work_date || new Date().toISOString().split('T')[0],
          work_type: entry.work_type || "daywork",
          description: entry.description || "",
          location_details: entry.location_details || "",
          labour_entries: entry.labour_entries || [],
          equipment_entries: entry.equipment_entries || [],
          photos: entry.photos || [],
          notes: entry.notes || "",
          work_summary: entry.work_summary || ""
        });
        setStep(1);
      } else {
        setFormData({
          job_id: "",
          client_id: "",
          work_date: new Date().toISOString().split('T')[0],
          work_type: "daywork",
          description: "",
          location_details: "",
          labour_entries: [],
          equipment_entries: [],
          photos: [],
          notes: "",
          work_summary: ""
        });
        setStep(1);
      }
    }
  }, [isOpen, entry]);

  const fetchData = async () => {
    try {
      const [jobsRes, clientsRes, employeesRes, machinesRes, termsRes] = await Promise.all([
        axios.get(`${API}/jobs`),
        axios.get(`${API}/clients`),
        axios.get(`${API}/users`),
        axios.get(`${API}/machines`),
        axios.get(`${API}/daybook/terms`)
      ]);
      setJobs(jobsRes.data.filter(j => ['scheduled', 'in_progress'].includes(j.status)));
      setClients(clientsRes.data);
      setEmployees(employeesRes.data.filter(e => e.is_active));
      setMachines(machinesRes.data);
      setTerms(termsRes.data.terms);
    } catch (e) {
      console.error('Failed to fetch data:', e);
    }
  };

  const syncHours = async () => {
    if (!entry?.id) return;
    try {
      const res = await axios.get(`${API}/daybook/${entry.id}/sync-hours`);
      setFormData({
        ...formData,
        labour_entries: res.data.labour_entries,
        equipment_entries: res.data.equipment_entries
      });
      alert('Hours synced from clock-in data!');
    } catch (e) {
      alert('Failed to sync hours');
    }
  };

  const handleSave = async () => {
    if (!formData.job_id || !formData.client_id || !formData.description) {
      alert('Please fill in all required fields');
      return;
    }
    
    setLoading(true);
    try {
      if (entry?.id) {
        await axios.put(`${API}/daybook/${entry.id}`, formData);
        alert('Daybook entry updated!');
      } else {
        await axios.post(`${API}/daybook`, formData);
        alert('Daybook entry created!');
      }
      onSave();
      onClose();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to save');
    } finally {
      setLoading(false);
    }
  };

  const handleClientSign = async (signatureData) => {
    if (!termsAccepted) {
      alert('Please accept the terms and conditions');
      return;
    }
    if (!clientName) {
      alert('Please enter the client/builder name');
      return;
    }
    
    try {
      await axios.post(`${API}/daybook/${entry.id}/client-sign`, {
        client_name: clientName,
        signature_data: signatureData,
        terms_accepted: true
      });
      alert('Client signature recorded!');
      setShowClientSign(false);
      onSave();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to sign');
    }
  };

  const handleRepSign = async (signatureData) => {
    try {
      await axios.post(`${API}/daybook/${entry.id}/rep-sign`, {
        signature_data: signatureData
      });
      alert('Representative signature recorded!');
      setShowRepSign(false);
      onSave();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to sign');
    }
  };

  const addLabourEntry = (empId) => {
    const emp = employees.find(e => e.id === empId);
    if (!emp) return;
    if (formData.labour_entries.some(le => le.employee_id === empId)) return;
    
    setFormData({
      ...formData,
      labour_entries: [...formData.labour_entries, {
        employee_id: empId,
        employee_name: `${emp.first_name} ${emp.last_name}`,
        role: emp.role || "",
        hours: 0,
        synced_from_clock: false
      }]
    });
  };

  const updateLabourHours = (empId, hours) => {
    setFormData({
      ...formData,
      labour_entries: formData.labour_entries.map(le => 
        le.employee_id === empId ? { ...le, hours: parseFloat(hours) || 0 } : le
      )
    });
  };

  const removeLabourEntry = (empId) => {
    setFormData({
      ...formData,
      labour_entries: formData.labour_entries.filter(le => le.employee_id !== empId)
    });
  };

  const addEquipmentEntry = (machineId) => {
    const machine = machines.find(m => m.id === machineId);
    if (!machine) return;
    if (formData.equipment_entries.some(ee => ee.machine_id === machineId)) return;
    
    setFormData({
      ...formData,
      equipment_entries: [...formData.equipment_entries, {
        machine_id: machineId,
        machine_name: machine.name || "",
        machine_type: machine.machine_type || "",
        hours: 0,
        operator_id: null,
        operator_name: "",
        synced_from_clock: false
      }]
    });
  };

  const updateEquipmentHours = (machineId, hours) => {
    setFormData({
      ...formData,
      equipment_entries: formData.equipment_entries.map(ee => 
        ee.machine_id === machineId ? { ...ee, hours: parseFloat(hours) || 0 } : ee
      )
    });
  };

  const removeEquipmentEntry = (machineId) => {
    setFormData({
      ...formData,
      equipment_entries: formData.equipment_entries.filter(ee => ee.machine_id !== machineId)
    });
  };

  const addPhoto = (photoUrl) => {
    if (formData.photos.length >= 2) {
      alert('Maximum 2 photos allowed');
      return;
    }
    setFormData({ ...formData, photos: [...formData.photos, photoUrl] });
  };

  const removePhoto = (index) => {
    setFormData({ ...formData, photos: formData.photos.filter((_, i) => i !== index) });
  };

  const selectedJob = jobs.find(j => j.id === formData.job_id);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={entry ? "Daybook Entry" : "New Daybook Entry"} size="xl">
      {/* Progress Steps */}
      <div className="flex items-center justify-between mb-6 pb-4 border-b">
        {[
          { num: 1, label: "Details" },
          { num: 2, label: "Labour & Equipment" },
          { num: 3, label: "Photos" },
          { num: 4, label: "Sign Off" }
        ].map((s, idx) => (
          <button
            key={s.num}
            onClick={() => setStep(s.num)}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
              step === s.num ? 'bg-orange-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            <span className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-sm font-bold">
              {s.num}
            </span>
            <span className="hidden md:inline font-medium">{s.label}</span>
          </button>
        ))}
      </div>

      {/* Step 1: Details */}
      {step === 1 && (
        <div className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <Select
              label="Job Site *"
              value={formData.job_id}
              onChange={(e) => {
                const job = jobs.find(j => j.id === e.target.value);
                setFormData({ 
                  ...formData, 
                  job_id: e.target.value,
                  client_id: job?.client_id || formData.client_id
                });
              }}
              options={[
                { value: "", label: "Select Job..." },
                ...jobs.map(j => ({ value: j.id, label: `${j.title} - ${j.location || 'No location'}` }))
              ]}
              required
            />
            <Select
              label="Client/Builder *"
              value={formData.client_id}
              onChange={(e) => setFormData({ ...formData, client_id: e.target.value })}
              options={[
                { value: "", label: "Select Client..." },
                ...clients.map(c => ({ value: c.id, label: c.name }))
              ]}
              required
            />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <DatePicker
              label="Work Date *"
              value={formData.work_date}
              onChange={(v) => setFormData({ ...formData, work_date: v })}
              required
              testId="daybook-work-date"
            />
            <Select
              label="Work Type *"
              value={formData.work_type}
              onChange={(e) => setFormData({ ...formData, work_type: e.target.value })}
              options={[
                { value: "daywork", label: "Daywork" },
                { value: "variation", label: "Variation" },
                { value: "crane_hire", label: "Crane Hire" },
                { value: "general", label: "General" }
              ]}
            />
          </div>

          <Input
            label="Location Details"
            value={formData.location_details}
            onChange={(e) => setFormData({ ...formData, location_details: e.target.value })}
            placeholder="e.g., Level 3, North Tower"
          />

          <Textarea
            label="Description of Works *"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Describe the work completed..."
            rows={3}
            required
          />

          <Textarea
            label="Notes (optional)"
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            placeholder="Any additional notes..."
            rows={2}
          />

          <div className="flex justify-end">
            <Button onClick={() => setStep(2)}>
              Next: Labour & Equipment
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      )}

      {/* Step 2: Labour & Equipment */}
      {step === 2 && (
        <div className="space-y-6">
          {/* Labour - Simple role-based */}
          <div className="p-4 bg-green-50 rounded-lg border-2 border-green-200">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-bold text-green-800 flex items-center gap-2">
                <Users className="w-5 h-5" />
                Labour
              </h4>
              <Button 
                type="button" 
                size="sm" 
                variant="secondary"
                onClick={() => setFormData({
                  ...formData,
                  labour_entries: [...formData.labour_entries, {
                    id: Date.now(),
                    role: "rigger",
                    qty: 1,
                    start_time: "07:00",
                    end_time: "15:00"
                  }]
                })}
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Labour
              </Button>
            </div>
            
            {formData.labour_entries.length === 0 ? (
              <p className="text-sm text-green-700 text-center py-4">Click "Add Labour" to add workers</p>
            ) : (
              <div className="space-y-3">
                {formData.labour_entries.map((le, idx) => (
                  <div key={le.id || idx} className="p-3 bg-white rounded-lg border space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <select
                        value={le.qty || 1}
                        onChange={(e) => {
                          const updated = [...formData.labour_entries];
                          updated[idx] = { ...updated[idx], qty: parseInt(e.target.value) };
                          setFormData({ ...formData, labour_entries: updated });
                        }}
                        className="w-16 h-10 border-2 border-slate-200 rounded-md px-2 font-bold"
                      >
                        {[1,2,3,4,5,6,7,8,9,10].map(n => (
                          <option key={n} value={n}>{n}x</option>
                        ))}
                      </select>
                      
                      <select
                        value={le.role}
                        onChange={(e) => {
                          const updated = [...formData.labour_entries];
                          updated[idx] = { ...updated[idx], role: e.target.value };
                          setFormData({ ...formData, labour_entries: updated });
                        }}
                        className="flex-1 min-w-[140px] h-10 border-2 border-slate-200 rounded-md px-2 font-semibold"
                      >
                        <option value="rigger">Rigger</option>
                        <option value="crane_operator">Crane Operator</option>
                        <option value="labourer">Labourer</option>
                        <option value="rigging_supervisor">Rigging Supervisor</option>
                        <option value="dogman">Dogman</option>
                      </select>
                      
                      <div className="flex items-center gap-1">
                        <input
                          type="time"
                          value={le.start_time || "07:00"}
                          onChange={(e) => {
                            const updated = [...formData.labour_entries];
                            updated[idx] = { ...updated[idx], start_time: e.target.value };
                            setFormData({ ...formData, labour_entries: updated });
                          }}
                          className="w-24 h-10 border-2 border-slate-200 rounded-md px-2"
                        />
                        <span className="text-slate-400">-</span>
                        <input
                          type="time"
                          value={le.end_time || "15:00"}
                          onChange={(e) => {
                            const updated = [...formData.labour_entries];
                            updated[idx] = { ...updated[idx], end_time: e.target.value };
                            setFormData({ ...formData, labour_entries: updated });
                          }}
                          className="w-24 h-10 border-2 border-slate-200 rounded-md px-2"
                        />
                      </div>
                      
                      <button 
                        type="button"
                        onClick={() => {
                          setFormData({
                            ...formData,
                            labour_entries: formData.labour_entries.filter((_, i) => i !== idx)
                          });
                        }} 
                        className="text-red-500 p-2 hover:bg-red-50 rounded"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    {/* Description field for this labour entry */}
                    <input
                      type="text"
                      value={le.description || ""}
                      onChange={(e) => {
                        const updated = [...formData.labour_entries];
                        updated[idx] = { ...updated[idx], description: e.target.value };
                        setFormData({ ...formData, labour_entries: updated });
                      }}
                      placeholder="What work did they do? (e.g., Rigging for tower crane installation)"
                      className="w-full h-9 border-2 border-slate-200 rounded-md px-3 text-sm"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Equipment - Simple machine selection */}
          <div className="p-4 bg-orange-50 rounded-lg border-2 border-orange-200">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-bold text-orange-800 flex items-center gap-2">
                <Truck className="w-5 h-5" />
                Equipment
              </h4>
              <Button 
                type="button" 
                size="sm" 
                variant="secondary"
                onClick={() => setFormData({
                  ...formData,
                  equipment_entries: [...formData.equipment_entries, {
                    id: Date.now(),
                    machine_id: machines[0]?.id || "",
                    machine_name: machines[0]?.name || "",
                    qty: 1,
                    start_time: "07:00",
                    end_time: "15:00"
                  }]
                })}
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Equipment
              </Button>
            </div>
            
            {formData.equipment_entries.length === 0 ? (
              <p className="text-sm text-orange-700 text-center py-4">Click "Add Equipment" to add machines</p>
            ) : (
              <div className="space-y-3">
                {formData.equipment_entries.map((ee, idx) => (
                  <div key={ee.id || idx} className="p-3 bg-white rounded-lg border space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <select
                        value={ee.qty || 1}
                        onChange={(e) => {
                          const updated = [...formData.equipment_entries];
                          updated[idx] = { ...updated[idx], qty: parseInt(e.target.value) };
                          setFormData({ ...formData, equipment_entries: updated });
                        }}
                        className="w-16 h-10 border-2 border-slate-200 rounded-md px-2 font-bold"
                      >
                        {[1,2,3,4,5].map(n => (
                          <option key={n} value={n}>{n}x</option>
                        ))}
                      </select>
                      
                      <select
                        value={ee.machine_id}
                        onChange={(e) => {
                          const machine = machines.find(m => m.id === e.target.value);
                          const updated = [...formData.equipment_entries];
                          updated[idx] = { 
                            ...updated[idx], 
                            machine_id: e.target.value,
                            machine_name: machine?.name || ""
                          };
                          setFormData({ ...formData, equipment_entries: updated });
                        }}
                        className="flex-1 min-w-[180px] h-10 border-2 border-slate-200 rounded-md px-2 font-semibold"
                      >
                        <option value="">Select machine...</option>
                        {machines.map(m => (
                          <option key={m.id} value={m.id}>{m.name}</option>
                        ))}
                      </select>
                      
                      <div className="flex items-center gap-1">
                        <input
                          type="time"
                          value={ee.start_time || "07:00"}
                          onChange={(e) => {
                            const updated = [...formData.equipment_entries];
                            updated[idx] = { ...updated[idx], start_time: e.target.value };
                            setFormData({ ...formData, equipment_entries: updated });
                          }}
                          className="w-24 h-10 border-2 border-slate-200 rounded-md px-2"
                        />
                        <span className="text-slate-400">-</span>
                        <input
                          type="time"
                          value={ee.end_time || "15:00"}
                          onChange={(e) => {
                            const updated = [...formData.equipment_entries];
                            updated[idx] = { ...updated[idx], end_time: e.target.value };
                            setFormData({ ...formData, equipment_entries: updated });
                          }}
                          className="w-24 h-10 border-2 border-slate-200 rounded-md px-2"
                        />
                      </div>
                      
                      <button 
                        type="button"
                        onClick={() => {
                          setFormData({
                            ...formData,
                            equipment_entries: formData.equipment_entries.filter((_, i) => i !== idx)
                          });
                        }} 
                        className="text-red-500 p-2 hover:bg-red-50 rounded"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    {/* Description field for this equipment entry */}
                    <input
                      type="text"
                      value={ee.description || ""}
                      onChange={(e) => {
                        const updated = [...formData.equipment_entries];
                        updated[idx] = { ...updated[idx], description: e.target.value };
                        setFormData({ ...formData, equipment_entries: updated });
                      }}
                      placeholder="What work did this machine do? (e.g., Lifting precast panels to level 3)"
                      className="w-full h-9 border-2 border-slate-200 rounded-md px-3 text-sm"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-between">
            <Button variant="ghost" onClick={() => setStep(1)}>
              <ChevronLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <Button onClick={() => setStep(3)}>
              Next: Photos
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      )}

      {/* Step 3: Photos */}
      {step === 3 && (
        <div className="space-y-6">
          <div className="p-4 bg-purple-50 rounded-lg border-2 border-purple-200">
            <h4 className="font-bold text-purple-800 mb-3 flex items-center gap-2">
              <Camera className="w-5 h-5" />
              Job Photos (Max 2)
            </h4>
            
            {formData.photos.length < 2 && (
              <FileUploadInput
                label="Upload Photo"
                accept="image/*"
                onFileSelect={(url) => addPhoto(url)}
              />
            )}

            {formData.photos.length > 0 && (
              <div className="grid grid-cols-2 gap-4 mt-4">
                {formData.photos.map((photo, idx) => (
                  <div key={idx} className="relative">
                    <img 
                      src={photo} 
                      alt={`Job photo ${idx + 1}`} 
                      className="w-full h-40 object-cover rounded-lg border-2 border-purple-200"
                    />
                    <button 
                      onClick={() => removePhoto(idx)}
                      className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-between">
            <Button variant="ghost" onClick={() => setStep(2)}>
              <ChevronLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div className="flex gap-3">
              <Button variant="secondary" onClick={handleSave} disabled={loading}>
                {loading ? <Spinner size="sm" /> : "Save Draft"}
              </Button>
              <Button onClick={() => { handleSave(); setStep(4); }} disabled={loading}>
                Next: Sign Off
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Step 4: Sign Off */}
      {step === 4 && entry?.id && (
        <div className="space-y-6">
          {/* Work Summary - Overall description before sign-off */}
          <div className="p-4 bg-purple-50 rounded-lg border-2 border-purple-200">
            <h4 className="font-bold text-purple-800 mb-3 flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Work Summary (For Invoice)
            </h4>
            <textarea
              value={formData.work_summary || ""}
              onChange={(e) => setFormData({ ...formData, work_summary: e.target.value })}
              placeholder="Summarise the work completed today for invoicing purposes...&#10;e.g., Tower crane installation - completed lift of 12 precast panels to levels 2-4. All rigging and dogman duties for the day."
              rows={4}
              className="w-full border-2 border-purple-200 rounded-lg p-3 text-sm"
            />
            <Button 
              size="sm" 
              variant="secondary" 
              className="mt-2"
              onClick={async () => {
                try {
                  await axios.put(`${API}/daybook/${entry.id}`, { ...formData });
                  alert('Work summary saved!');
                } catch (err) {
                  console.error('Failed to save summary:', err);
                }
              }}
            >
              Save Summary
            </Button>
          </div>

          {/* Entry Summary */}
          <div className="p-4 bg-slate-50 rounded-lg border-2">
            <h4 className="font-bold mb-3">Entry Summary</h4>
            <div className="grid md:grid-cols-2 gap-4 text-sm">
              <div>
                <p><strong>Job:</strong> {entry.job_title}</p>
                <p><strong>Client:</strong> {entry.client_name}</p>
                <p><strong>Date:</strong> {new Date(entry.work_date).toLocaleDateString()}</p>
              </div>
              <div>
                <p><strong>Labour:</strong> {formData.labour_entries.length} entries</p>
                <p><strong>Equipment:</strong> {formData.equipment_entries.length} machines</p>
              </div>
            </div>
            
            {/* Show labour entries with descriptions */}
            {formData.labour_entries.length > 0 && (
              <div className="mt-3 pt-3 border-t">
                <p className="font-semibold text-sm mb-2">Labour Details:</p>
                <ul className="text-xs space-y-1 text-slate-600">
                  {formData.labour_entries.map((le, idx) => (
                    <li key={idx}>
                      {le.qty}x {le.role?.replace('_', ' ')} ({le.start_time}-{le.end_time})
                      {le.description && <span className="italic"> - {le.description}</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {/* Show equipment entries with descriptions */}
            {formData.equipment_entries.length > 0 && (
              <div className="mt-3 pt-3 border-t">
                <p className="font-semibold text-sm mb-2">Equipment Details:</p>
                <ul className="text-xs space-y-1 text-slate-600">
                  {formData.equipment_entries.map((ee, idx) => (
                    <li key={idx}>
                      {ee.qty}x {ee.machine_name} ({ee.start_time}-{ee.end_time})
                      {ee.description && <span className="italic"> - {ee.description}</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Client Signature */}
          <div className="p-4 bg-blue-50 rounded-lg border-2 border-blue-200">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-bold text-blue-800 flex items-center gap-2">
                <Edit3 className="w-5 h-5" />
                Client/Builder Signature
              </h4>
              {entry.client_signature ? (
                <Badge variant="success">Signed by {entry.client_signature.name}</Badge>
              ) : (
                <Button size="sm" onClick={() => { setShowTerms(true); }}>
                  Sign Now
                </Button>
              )}
            </div>
            
            {entry.client_signature && (
              <div className="flex items-center gap-4">
                <img src={entry.client_signature.signature_data} alt="Client signature" className="h-16 border rounded" />
                <div className="text-sm text-slate-600">
                  <p>{entry.client_signature.name}</p>
                  <p>{new Date(entry.client_signature.signed_at).toLocaleString()}</p>
                </div>
              </div>
            )}
          </div>

          {/* Rep Signature */}
          <div className="p-4 bg-green-50 rounded-lg border-2 border-green-200">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-bold text-green-800 flex items-center gap-2">
                <Edit3 className="w-5 h-5" />
                Mirri Representative Signature
              </h4>
              {entry.rep_signature ? (
                <Badge variant="success">Signed by {entry.rep_signature.name}</Badge>
              ) : (
                <Button size="sm" variant="secondary" onClick={() => setShowRepSign(true)}>
                  Sign Now
                </Button>
              )}
            </div>
            
            {entry.rep_signature && (
              <div className="flex items-center gap-4">
                <img src={entry.rep_signature.signature_data} alt="Rep signature" className="h-16 border rounded" />
                <div className="text-sm text-slate-600">
                  <p>{entry.rep_signature.name}</p>
                  <p>{new Date(entry.rep_signature.signed_at).toLocaleString()}</p>
                </div>
              </div>
            )}
          </div>

          {entry.status === "completed" && (
            <div className="p-4 bg-green-100 rounded-lg border-2 border-green-300 text-center">
              <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-600" />
              <h4 className="font-bold text-green-800">Daybook Entry Complete!</h4>
              <p className="text-sm text-green-700">Both signatures recorded. Ready for invoicing.</p>
            </div>
          )}

          <div className="flex justify-between">
            <Button variant="ghost" onClick={() => setStep(3)}>
              <ChevronLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <Button variant="ghost" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      )}

      {/* Step 4 for new entries (not yet saved) */}
      {step === 4 && !entry?.id && (
        <div className="text-center py-8">
          <AlertCircle className="w-12 h-12 mx-auto mb-4 text-amber-500" />
          <h4 className="font-bold text-lg mb-2">Save Entry First</h4>
          <p className="text-slate-600 mb-4">You need to save the entry before collecting signatures.</p>
          <Button onClick={handleSave} disabled={loading}>
            {loading ? <Spinner size="sm" /> : "Save Entry"}
          </Button>
        </div>
      )}

      {/* Terms Modal */}
      <Modal isOpen={showTerms} onClose={() => setShowTerms(false)} title="Terms & Conditions" size="lg">
        <div className="space-y-4">
          <div className="max-h-64 overflow-y-auto p-4 bg-slate-50 rounded-lg border text-sm whitespace-pre-wrap">
            {terms}
          </div>
          
          <label className="flex items-center gap-3 p-4 bg-amber-50 rounded-lg border-2 border-amber-200 cursor-pointer">
            <input
              type="checkbox"
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
              className="w-6 h-6"
            />
            <span className="font-medium text-amber-800">I have read and accept the terms and conditions</span>
          </label>

          <Input
            label="Client/Builder Name *"
            value={clientName}
            onChange={(e) => setClientName(e.target.value)}
            placeholder="Enter your full name"
          />

          {termsAccepted && clientName && (
            <div className="p-4 border-2 rounded-lg">
              <h4 className="font-bold mb-3">Sign Below</h4>
              <FormSignaturePad 
                onSign={(data) => { handleClientSign(data); setShowTerms(false); }} 
                onCancel={() => setShowTerms(false)} 
              />
            </div>
          )}

          <div className="flex justify-end">
            <Button variant="ghost" onClick={() => setShowTerms(false)}>Cancel</Button>
          </div>
        </div>
      </Modal>

      {/* Rep Sign Modal */}
      <Modal isOpen={showRepSign} onClose={() => setShowRepSign(false)} title="Representative Signature">
        <div className="space-y-4">
          <p className="text-slate-600">
            Signing as: <strong>{user?.first_name} {user?.last_name}</strong>
          </p>
          
          <div className="p-4 border-2 rounded-lg">
            <FormSignaturePad 
              onSign={(data) => handleRepSign(data)} 
              onCancel={() => setShowRepSign(false)} 
            />
          </div>
        </div>
      </Modal>
    </Modal>
  );
};

// Notifications Page
// Messages Page - Private messaging system
// MessagesPage extracted to pages/MessagesPage.jsx

// NotificationsPage extracted to /pages/NotificationsPage.jsx

// Companies Management Page (Admin only)
// CompaniesPage extracted to /pages/CompaniesPage.jsx


// My Records Page - Personal dashboard for timesheets, payslips, documents
// MyRecordsPage extracted to /pages/MyRecordsPage.jsx


// ==================== SITE ATTENDANCE PAGE ====================
// Simplified view for tracking who's where and their hours
const SiteAttendancePage = () => {
  const { user } = useAuth();
  const [viewMode, setViewMode] = useState("today"); // today, date, range
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [jobFilter, setJobFilter] = useState("");
  const [workerFilter, setWorkerFilter] = useState("");
  const [jobs, setJobs] = useState([]);
  const [users, setUsers] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [liveData, setLiveData] = useState([]);

  useEffect(() => {
    fetchJobs();
    fetchUsers();
    if (viewMode === "today") {
      fetchLiveAttendance();
    }
  }, []);

  useEffect(() => {
    if (viewMode === "today") {
      fetchLiveAttendance();
      const interval = setInterval(fetchLiveAttendance, 30000); // Refresh every 30s
      return () => clearInterval(interval);
    } else {
      fetchHistoricalData();
    }
  }, [viewMode, selectedDate, startDate, endDate, jobFilter]);

  const fetchJobs = async () => {
    try {
      const res = await axios.get(`${API}/jobs`);
      setJobs(res.data);
    } catch (e) { console.error(e); }
  };

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/users`);
      setUsers(res.data);
    } catch (e) { console.error(e); }
  };

  const fetchLiveAttendance = async () => {
    try {
      const res = await axios.get(`${API}/dashboard/clocked-in-users`);
      setLiveData(res.data);
      setLoading(false);
    } catch (e) { 
      console.error(e);
      setLoading(false);
    }
  };

  const fetchHistoricalData = async () => {
    setLoading(true);
    try {
      let url = viewMode === "date" 
        ? `${API}/analytics/attendance-history?date=${selectedDate}`
        : `${API}/analytics/attendance-history?start_date=${startDate}&end_date=${endDate}`;
      if (jobFilter) url += `&job_id=${jobFilter}`;
      const res = await axios.get(url);
      setData(res.data);
    } catch (e) { 
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (dateStr) => dateStr ? new Date(dateStr).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' }) : '-';
  const formatDate = (dateStr) => dateStr ? new Date(dateStr).toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' }) : '-';
  const formatHours = (hours) => {
    if (!hours && hours !== 0) return '-';
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  };
  const getHoursOnSite = (clockIn) => {
    if (!clockIn) return '-';
    const diff = (new Date() - new Date(clockIn)) / 3600000;
    return formatHours(diff);
  };

  // Filter live data
  const filteredLiveData = liveData.filter(entry => {
    if (jobFilter && entry.job_id !== jobFilter) return false;
    if (workerFilter && !entry.user_name?.toLowerCase().includes(workerFilter.toLowerCase())) return false;
    return true;
  });

  // Group by job site
  const groupByJob = (entries) => {
    const grouped = {};
    entries.forEach(entry => {
      const key = entry.job_title || entry.job_name || 'Unassigned';
      if (!grouped[key]) {
        grouped[key] = { 
          job_id: entry.job_id,
          location: entry.job_location || entry.location,
          workers: [],
          totalHours: 0
        };
      }
      grouped[key].workers.push(entry);
      grouped[key].totalHours += entry.hours || 0;
    });
    return grouped;
  };

  const canView = ['admin', 'manager', 'supervisor', 'office_staff'].includes(user?.role);

  if (!canView) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">You don't have permission to view site attendance.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="site-attendance-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold uppercase tracking-tight font-heading">Site Attendance</h1>
          <p className="text-slate-600">Track who's on site and view attendance history</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setViewMode("today")}
            className={`px-4 py-2 rounded-md font-semibold text-sm transition-colors ${
              viewMode === "today" ? 'bg-green-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            Live Now
          </button>
          <button
            onClick={() => setViewMode("date")}
            className={`px-4 py-2 rounded-md font-semibold text-sm transition-colors ${
              viewMode === "date" ? 'bg-orange-500 text-slate-900' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            By Date
          </button>
          <button
            onClick={() => setViewMode("range")}
            className={`px-4 py-2 rounded-md font-semibold text-sm transition-colors ${
              viewMode === "range" ? 'bg-orange-500 text-slate-900' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            Date Range
          </button>
        </div>
      </div>

      {/* Filters */}
      <Card className="bg-slate-50">
        <div className="flex flex-wrap items-center gap-4">
          {viewMode === "date" && (
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-slate-400" />
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="border-2 border-slate-200 rounded-md px-3 py-2 font-medium bg-white"
              />
            </div>
          )}
          {viewMode === "range" && (
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-slate-400" />
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="border-2 border-slate-200 rounded-md px-3 py-2 font-medium bg-white"
              />
              <span className="text-slate-400">to</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="border-2 border-slate-200 rounded-md px-3 py-2 font-medium bg-white"
              />
            </div>
          )}
          <select
            value={jobFilter}
            onChange={(e) => setJobFilter(e.target.value)}
            className="border-2 border-slate-200 rounded-md px-3 py-2 font-medium bg-white"
          >
            <option value="">All Sites</option>
            {jobs.filter(j => j.status === 'in_progress' || j.status === 'active').map(j => (
              <option key={j.id} value={j.id}>{j.title}</option>
            ))}
          </select>
          {viewMode === "today" && (
            <input
              type="text"
              placeholder="Search worker..."
              value={workerFilter}
              onChange={(e) => setWorkerFilter(e.target.value)}
              className="border-2 border-slate-200 rounded-md px-3 py-2 font-medium bg-white"
            />
          )}
          <Button variant="outline" onClick={viewMode === "today" ? fetchLiveAttendance : fetchHistoricalData}>
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>
      </Card>

      {loading ? (
        <div className="flex justify-center py-12"><Spinner size="lg" /></div>
      ) : (
        <>
          {/* LIVE VIEW */}
          {viewMode === "today" && (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="text-center bg-green-50 border-green-200">
                  <p className="text-4xl font-bold text-green-600">{filteredLiveData.length}</p>
                  <p className="text-sm text-slate-600 uppercase font-semibold">On Site Now</p>
                </Card>
                <Card className="text-center bg-blue-50 border-blue-200">
                  <p className="text-4xl font-bold text-blue-600">
                    {Object.keys(groupByJob(filteredLiveData)).length}
                  </p>
                  <p className="text-sm text-slate-600 uppercase font-semibold">Active Sites</p>
                </Card>
                <Card className="text-center bg-orange-50 border-orange-200">
                  <p className="text-4xl font-bold text-orange-600">
                    {formatHours(filteredLiveData.reduce((sum, e) => {
                      const hours = e.clock_in_time ? (new Date() - new Date(e.clock_in_time)) / 3600000 : 0;
                      return sum + hours;
                    }, 0))}
                  </p>
                  <p className="text-sm text-slate-600 uppercase font-semibold">Total Hours Today</p>
                </Card>
                <Card className="text-center bg-purple-50 border-purple-200">
                  <p className="text-4xl font-bold text-purple-600">
                    {filteredLiveData.filter(e => e.machine_name).length}
                  </p>
                  <p className="text-sm text-slate-600 uppercase font-semibold">Machines Active</p>
                </Card>
              </div>

              {/* Workers by Site */}
              {filteredLiveData.length > 0 ? (
                <div className="space-y-4">
                  {Object.entries(groupByJob(filteredLiveData)).map(([jobTitle, jobData]) => (
                    <Card key={jobTitle} className="border-2 border-slate-200">
                      <div className="flex items-center justify-between mb-4 pb-3 border-b border-slate-100">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                            <MapPin className="w-5 h-5 text-orange-600" />
                          </div>
                          <div>
                            <h3 className="font-bold text-lg">{jobTitle}</h3>
                            {jobData.location && <p className="text-sm text-slate-500">{jobData.location}</p>}
                          </div>
                        </div>
                        <Badge variant="success" className="text-lg px-4 py-1">{jobData.workers.length} workers</Badge>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b-2 border-slate-100">
                              <th className="text-left py-2 px-3 text-xs font-bold text-slate-500 uppercase">Worker</th>
                              <th className="text-left py-2 px-3 text-xs font-bold text-slate-500 uppercase">Role</th>
                              <th className="text-left py-2 px-3 text-xs font-bold text-slate-500 uppercase">Machine</th>
                              <th className="text-left py-2 px-3 text-xs font-bold text-slate-500 uppercase">Clocked In</th>
                              <th className="text-right py-2 px-3 text-xs font-bold text-slate-500 uppercase">Hours</th>
                            </tr>
                          </thead>
                          <tbody>
                            {jobData.workers.map((worker, idx) => (
                              <tr key={idx} className="border-b border-slate-50 hover:bg-green-50">
                                <td className="py-3 px-3">
                                  <div className="flex items-center gap-2">
                                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                                      <User className="w-4 h-4 text-green-600" />
                                    </div>
                                    <span className="font-semibold">{worker.user_name}</span>
                                  </div>
                                </td>
                                <td className="py-3 px-3 text-slate-600 capitalize text-sm">{worker.user_role?.replace('_', ' ')}</td>
                                <td className="py-3 px-3">
                                  {worker.machine_name ? (
                                    <div className="flex items-center gap-1 text-blue-600">
                                      <Truck className="w-4 h-4" />
                                      <span className="text-sm font-medium">{worker.machine_name}</span>
                                    </div>
                                  ) : '-'}
                                </td>
                                <td className="py-3 px-3 text-sm">{formatTime(worker.clock_in_time)}</td>
                                <td className="py-3 px-3 text-right">
                                  <span className="font-bold text-green-600 text-lg">{getHoursOnSite(worker.clock_in_time)}</span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </Card>
                  ))}
                </div>
              ) : (
                <Card className="text-center py-12">
                  <MapPin className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p className="text-slate-500 text-lg">No workers currently on site</p>
                </Card>
              )}
            </>
          )}

          {/* HISTORICAL VIEW */}
          {(viewMode === "date" || viewMode === "range") && data && (
            <>
              {/* Summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="text-center bg-blue-50 border-blue-200">
                  <p className="text-4xl font-bold text-blue-600">{data.summary?.total_entries || 0}</p>
                  <p className="text-sm text-slate-600 uppercase font-semibold">Total Entries</p>
                </Card>
                <Card className="text-center bg-green-50 border-green-200">
                  <p className="text-4xl font-bold text-green-600">{data.summary?.unique_workers || 0}</p>
                  <p className="text-sm text-slate-600 uppercase font-semibold">Unique Workers</p>
                </Card>
                <Card className="text-center bg-orange-50 border-orange-200">
                  <p className="text-4xl font-bold text-orange-600">{formatHours(data.summary?.total_hours || 0)}</p>
                  <p className="text-sm text-slate-600 uppercase font-semibold">Total Hours</p>
                </Card>
                <Card className="text-center bg-purple-50 border-purple-200">
                  <p className="text-4xl font-bold text-purple-600">{data.summary?.unique_sites || 0}</p>
                  <p className="text-sm text-slate-600 uppercase font-semibold">Sites Worked</p>
                </Card>
              </div>

              {/* Entries Table */}
              {data.entries?.length > 0 ? (
                <Card>
                  <h3 className="text-lg font-bold mb-4">Attendance Records</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b-2 border-slate-200">
                          <th className="text-left py-3 px-3 text-xs font-bold text-slate-500 uppercase">Date</th>
                          <th className="text-left py-3 px-3 text-xs font-bold text-slate-500 uppercase">Worker</th>
                          <th className="text-left py-3 px-3 text-xs font-bold text-slate-500 uppercase">Site</th>
                          <th className="text-left py-3 px-3 text-xs font-bold text-slate-500 uppercase">Clock In</th>
                          <th className="text-left py-3 px-3 text-xs font-bold text-slate-500 uppercase">Clock Out</th>
                          <th className="text-right py-3 px-3 text-xs font-bold text-slate-500 uppercase">Hours</th>
                          <th className="text-center py-3 px-3 text-xs font-bold text-slate-500 uppercase">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.entries.map((entry, idx) => (
                          <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50">
                            <td className="py-3 px-3 font-medium">{formatDate(entry.clock_in)}</td>
                            <td className="py-3 px-3">{entry.user_name}</td>
                            <td className="py-3 px-3 text-slate-600">{entry.job_title || entry.job_name || '-'}</td>
                            <td className="py-3 px-3 text-sm">{formatTime(entry.clock_in)}</td>
                            <td className="py-3 px-3 text-sm">{entry.clock_out ? formatTime(entry.clock_out) : '-'}</td>
                            <td className="py-3 px-3 text-right font-bold text-orange-600">{formatHours(entry.hours)}</td>
                            <td className="py-3 px-3 text-center">
                              {entry.alteration_applied && (
                                <Badge variant="warning" className="mr-1">Altered</Badge>
                              )}
                              <Badge variant={entry.clock_out ? 'default' : 'success'}>
                                {entry.clock_out ? 'Done' : 'Active'}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>
              ) : (
                <Card className="text-center py-12">
                  <Calendar className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p className="text-slate-500 text-lg">No attendance records for selected period</p>
                </Card>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

// Time Alterations Approval Page - For supervisors/managers to approve time change requests
const TimeAlterationsPage = () => {
  const { user } = useAuth();
  const [alterations, setAlterations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const [processing, setProcessing] = useState(null);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [selectedAlteration, setSelectedAlteration] = useState(null);
  const [approvalNotes, setApprovalNotes] = useState("");

  useEffect(() => {
    fetchAlterations();
  }, [filter]);

  const fetchAlterations = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/time-alterations?status=${filter !== 'all' ? filter : ''}`);
      setAlterations(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (status) => {
    if (!selectedAlteration) return;
    setProcessing(selectedAlteration.id);
    try {
      await axios.put(`${API}/time-alterations/${selectedAlteration.id}/approve`, {
        status,
        notes: approvalNotes
      });
      toast.success(`Request ${status}`);
      setShowApproveModal(false);
      setSelectedAlteration(null);
      setApprovalNotes("");
      fetchAlterations();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to process request");
    } finally {
      setProcessing(null);
    }
  };

  const formatDate = (dateStr) => dateStr ? new Date(dateStr).toLocaleDateString('en-AU') : '-';
  const formatTime = (dateStr) => dateStr ? new Date(dateStr).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' }) : '-';

  if (!['admin', 'manager', 'supervisor', 'office_staff'].includes(user?.role)) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">You don't have permission to view this page.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="time-alterations-page">
      <div>
        <h1 className="text-3xl font-bold uppercase tracking-tight font-heading">Time Alteration Requests</h1>
        <p className="text-slate-600">Review and approve employee time change requests</p>
      </div>

      {/* Filters */}
      <div className="flex gap-2">
        {['pending', 'approved', 'rejected', 'all'].map(status => (
          <button
            key={status}
            onClick={() => setFilter(status)}
            className={`px-4 py-2 rounded-md font-semibold text-sm uppercase transition-colors ${
              filter === status ? 'bg-orange-500 text-slate-900' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            {status}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Spinner size="lg" /></div>
      ) : alterations.length > 0 ? (
        <div className="space-y-4">
          {alterations.map(alt => (
            <Card key={alt.id} className={alt.status === 'pending' ? 'border-amber-200 bg-amber-50/50' : ''}>
              <div className="flex flex-col md:flex-row md:justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <User className="w-5 h-5 text-slate-400" />
                    <span className="font-bold">{alt.user_name}</span>
                    <Badge variant={alt.status === 'pending' ? 'warning' : alt.status === 'approved' ? 'success' : 'error'}>
                      {alt.status}
                    </Badge>
                  </div>
                  
                  <div className="grid sm:grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-xs text-slate-500 uppercase">Original Times</p>
                      <p className="font-medium">
                        {formatTime(alt.original_clock_in)} - {formatTime(alt.original_clock_out)}
                      </p>
                      <p className="text-slate-500">{formatDate(alt.original_clock_in)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase">Requested Times</p>
                      <p className="font-medium text-orange-600">
                        {alt.requested_clock_in || formatTime(alt.original_clock_in)} - {alt.requested_clock_out || formatTime(alt.original_clock_out)}
                      </p>
                      {alt.requested_job_title && (
                        <p className="text-blue-600">Job: {alt.requested_job_title}</p>
                      )}
                    </div>
                  </div>
                  
                  <div className="mt-3 p-3 bg-slate-100 rounded-lg">
                    <p className="text-xs text-slate-500 uppercase mb-1">Reason</p>
                    <p className="text-sm">{alt.reason}</p>
                  </div>
                  
                  {alt.employee_signature && (
                    <div className="mt-2">
                      <p className="text-xs text-slate-500 uppercase mb-1">Employee Signature</p>
                      <img src={alt.employee_signature} alt="Signature" className="h-12 border rounded bg-white" />
                    </div>
                  )}
                  
                  <p className="text-xs text-slate-400 mt-2">Submitted: {formatDate(alt.created_at)}</p>
                  
                  {alt.approved_by_name && (
                    <p className="text-xs text-slate-500 mt-1">
                      {alt.status === 'approved' ? 'Approved' : 'Rejected'} by {alt.approved_by_name}
                      {alt.approval_notes && ` - "${alt.approval_notes}"`}
                    </p>
                  )}
                </div>
                
                {alt.status === 'pending' && (
                  <div className="flex md:flex-col gap-2">
                    <Button 
                      size="sm"
                      onClick={() => { setSelectedAlteration(alt); setShowApproveModal(true); }}
                      disabled={processing === alt.id}
                    >
                      Review
                    </Button>
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 text-slate-500">
          <Clock className="w-12 h-12 mx-auto mb-4 text-slate-300" />
          <p>No {filter !== 'all' ? filter : ''} alteration requests</p>
        </div>
      )}

      {/* Approval Modal */}
      {showApproveModal && selectedAlteration && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg">
            <h2 className="text-xl font-bold uppercase mb-4">Review Time Alteration</h2>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-slate-500 uppercase">Original</p>
                  <p className="font-medium">{formatTime(selectedAlteration.original_clock_in)} - {formatTime(selectedAlteration.original_clock_out)}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase">Requested</p>
                  <p className="font-medium text-orange-600">
                    {selectedAlteration.requested_clock_in || formatTime(selectedAlteration.original_clock_in)} - {selectedAlteration.requested_clock_out || formatTime(selectedAlteration.original_clock_out)}
                  </p>
                </div>
              </div>
              
              <div className="p-3 bg-slate-100 rounded-lg">
                <p className="text-xs text-slate-500 uppercase mb-1">Employee's Reason</p>
                <p>{selectedAlteration.reason}</p>
              </div>
              
              {selectedAlteration.employee_signature && (
                <div>
                  <p className="text-xs text-slate-500 uppercase mb-1">Signature</p>
                  <img src={selectedAlteration.employee_signature} alt="Signature" className="h-16 border rounded bg-white" />
                </div>
              )}
              
              <div>
                <label className="text-xs text-slate-500 uppercase">Approval Notes (optional)</label>
                <textarea
                  value={approvalNotes}
                  onChange={(e) => setApprovalNotes(e.target.value)}
                  placeholder="Add notes about your decision..."
                  className="w-full mt-1 px-3 py-2 border rounded-md"
                  rows={2}
                />
              </div>
            </div>
            
            <div className="flex gap-2 mt-6">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => { setShowApproveModal(false); setSelectedAlteration(null); setApprovalNotes(""); }}
              >
                Cancel
              </Button>
              <Button 
                variant="outline"
                className="flex-1 border-red-300 text-red-600 hover:bg-red-50"
                onClick={() => handleApprove('rejected')}
                disabled={processing}
              >
                Reject
              </Button>
              <Button 
                className="flex-1"
                onClick={() => handleApprove('approved')}
                disabled={processing}
              >
                {processing ? <Spinner size="sm" /> : 'Approve'}
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

// Job Hours Dashboard - Live hours breakdown by job (Admin/Manager only)
const JobHoursDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [viewMode, setViewMode] = useState("by_job"); // by_job, by_employee, detailed
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/dashboard/job-hours-live`);
      setData(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  const formatHours = (hours) => {
    if (!hours) return "0h";
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Spinner size="lg" /></div>;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="job-hours-page">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold uppercase tracking-tight font-heading">Job Hours Dashboard</h1>
          <p className="text-slate-600">Live breakdown of hours by job and employee</p>
          <p className="text-xs text-slate-400">Week starting {data?.week_start} • Last updated: {new Date(data?.generated_at).toLocaleTimeString()}</p>
        </div>
        <Button onClick={handleRefresh} disabled={refreshing}>
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid md:grid-cols-3 gap-4">
        <Card className="text-center bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
          <p className="text-4xl font-bold text-orange-600">{formatHours(data?.total_hours_this_week)}</p>
          <p className="text-sm text-slate-600 font-semibold uppercase">Total Hours This Week</p>
        </Card>
        <Card className="text-center bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <p className="text-4xl font-bold text-green-600">{data?.currently_clocked_in || 0}</p>
          <p className="text-sm text-slate-600 font-semibold uppercase">Currently Clocked In</p>
        </Card>
        <Card className="text-center bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <p className="text-4xl font-bold text-blue-600">{data?.by_job?.length || 0}</p>
          <p className="text-sm text-slate-600 font-semibold uppercase">Active Jobs</p>
        </Card>
      </div>

      {/* View Mode Toggle */}
      <div className="flex gap-2">
        {[
          { key: 'by_job', label: 'By Job' },
          { key: 'by_employee', label: 'By Employee' },
          { key: 'detailed', label: 'Detailed Breakdown' }
        ].map(mode => (
          <button
            key={mode.key}
            onClick={() => setViewMode(mode.key)}
            className={`px-4 py-2 rounded-md font-semibold text-sm uppercase transition-colors ${
              viewMode === mode.key ? 'bg-orange-500 text-slate-900' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            {mode.label}
          </button>
        ))}
      </div>

      {/* By Job View */}
      {viewMode === "by_job" && (
        <Card>
          <h3 className="font-bold uppercase mb-4">Hours by Job</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-100">
                <tr>
                  <th className="text-left p-3 font-bold uppercase">Job</th>
                  <th className="text-left p-3 font-bold uppercase">Client</th>
                  <th className="text-center p-3 font-bold uppercase">Hours</th>
                  <th className="text-center p-3 font-bold uppercase">Entries</th>
                  <th className="text-center p-3 font-bold uppercase">Employees</th>
                </tr>
              </thead>
              <tbody>
                {data?.by_job?.map((job, idx) => (
                  <tr key={idx} className="border-t hover:bg-slate-50">
                    <td className="p-3 font-medium">{job.job_title}</td>
                    <td className="p-3 text-slate-600">{job.client_name || '-'}</td>
                    <td className="p-3 text-center font-bold text-orange-600">{formatHours(job.total_hours)}</td>
                    <td className="p-3 text-center">{job.entries_count}</td>
                    <td className="p-3 text-center">{job.unique_employees}</td>
                  </tr>
                ))}
                {(!data?.by_job || data.by_job.length === 0) && (
                  <tr><td colSpan="5" className="p-8 text-center text-slate-500">No time entries this week</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* By Employee View */}
      {viewMode === "by_employee" && (
        <Card>
          <h3 className="font-bold uppercase mb-4">Hours by Employee</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-100">
                <tr>
                  <th className="text-left p-3 font-bold uppercase">Employee</th>
                  <th className="text-left p-3 font-bold uppercase">Role</th>
                  <th className="text-center p-3 font-bold uppercase">Hours</th>
                  <th className="text-center p-3 font-bold uppercase">Jobs Worked</th>
                </tr>
              </thead>
              <tbody>
                {data?.by_employee?.map((emp, idx) => (
                  <tr key={idx} className="border-t hover:bg-slate-50">
                    <td className="p-3 font-medium">{emp.user_name}</td>
                    <td className="p-3 text-slate-600 capitalize">{emp.role?.replace('_', ' ')}</td>
                    <td className="p-3 text-center font-bold text-orange-600">{formatHours(emp.total_hours)}</td>
                    <td className="p-3 text-center">{emp.jobs_worked}</td>
                  </tr>
                ))}
                {(!data?.by_employee || data.by_employee.length === 0) && (
                  <tr><td colSpan="4" className="p-8 text-center text-slate-500">No time entries this week</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Detailed Breakdown View */}
      {viewMode === "detailed" && (
        <Card>
          <h3 className="font-bold uppercase mb-4">Detailed Breakdown (Job + Employee)</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-100">
                <tr>
                  <th className="text-left p-3 font-bold uppercase">Job</th>
                  <th className="text-left p-3 font-bold uppercase">Employee</th>
                  <th className="text-center p-3 font-bold uppercase">Hours</th>
                </tr>
              </thead>
              <tbody>
                {data?.detailed_breakdown?.map((item, idx) => (
                  <tr key={idx} className="border-t hover:bg-slate-50">
                    <td className="p-3 font-medium">{item.job_title}</td>
                    <td className="p-3 text-slate-600">{item.user_name}</td>
                    <td className="p-3 text-center font-bold text-orange-600">{formatHours(item.hours)}</td>
                  </tr>
                ))}
                {(!data?.detailed_breakdown || data.detailed_breakdown.length === 0) && (
                  <tr><td colSpan="3" className="p-8 text-center text-slate-500">No time entries this week</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
};


// Settings Page (Admin only)
// SettingsPage extracted to pages/SettingsPage.jsx
const ReportsPage = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [jobs, setJobs] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [reportsRes, jobsRes] = await Promise.all([
        axios.get(`${API}/reports/weekly`),
        axios.get(`${API}/jobs`)
      ]);
      setReports(reportsRes.data);
      setJobs(jobsRes.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="reports-page">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold uppercase tracking-tight font-heading">Weekly Reports</h1>
        <Button onClick={() => setShowModal(true)} data-testid="create-report-btn">
          <Plus className="w-5 h-5 mr-2" />
          New Report
        </Button>
      </div>

      <div className="space-y-4">
        {reports.map(report => (
          <Card key={report.id} data-testid={`report-${report.id}`}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-xl font-bold font-heading uppercase">{report.job_title}</h3>
                <p className="text-sm text-slate-500">Week starting: {report.week_start}</p>
              </div>
              <Badge variant="success">Submitted</Badge>
            </div>
            <p className="text-slate-600 mb-4">{report.summary}</p>
            <div className="text-xs text-slate-400">
              Submitted: {new Date(report.submitted_at).toLocaleString()}
            </div>
          </Card>
        ))}

        {reports.length === 0 && (
          <div className="text-center py-12 text-slate-500">
            <BarChart3 className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <p className="text-lg font-semibold">No reports yet</p>
            <p className="text-sm">Submit your first weekly report</p>
          </div>
        )}
      </div>

      {/* Report Modal */}
      <ReportModal 
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        jobs={jobs}
        onSubmit={fetchData}
      />
    </div>
  );
};

// Report Modal
const ReportModal = ({ isOpen, onClose, jobs, onSubmit }) => {
  const [formData, setFormData] = useState({
    job_id: "",
    week_start: "",
    summary: "",
    accomplishments: "",
    issues: "",
    next_week_plan: ""
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/reports/weekly`, {
        ...formData,
        accomplishments: formData.accomplishments.split('\n').filter(Boolean),
        issues: formData.issues.split('\n').filter(Boolean),
        next_week_plan: formData.next_week_plan.split('\n').filter(Boolean)
      });
      alert("Report submitted successfully!");
      onSubmit();
      onClose();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to submit report");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Submit Weekly Report" size="lg">
      <form onSubmit={handleSubmit} className="space-y-6">
        <Select
          label="Job"
          value={formData.job_id}
          onChange={(e) => setFormData({...formData, job_id: e.target.value})}
          options={[
            { value: "", label: "Select a job..." },
            ...jobs.map(j => ({ value: j.id, label: j.title }))
          ]}
          required
        />

        <Input
          label="Week Starting"
          type="date"
          value={formData.week_start}
          onChange={(e) => setFormData({...formData, week_start: e.target.value})}
          required
        />

        <Textarea
          label="Summary"
          value={formData.summary}
          onChange={(e) => setFormData({...formData, summary: e.target.value})}
          required
        />

        <Textarea
          label="Key Accomplishments (one per line)"
          value={formData.accomplishments}
          onChange={(e) => setFormData({...formData, accomplishments: e.target.value})}
          placeholder="Completed crane assembly&#10;Passed safety inspection"
        />

        <Textarea
          label="Issues/Challenges (one per line)"
          value={formData.issues}
          onChange={(e) => setFormData({...formData, issues: e.target.value})}
        />

        <Textarea
          label="Next Week Plan (one per line)"
          value={formData.next_week_plan}
          onChange={(e) => setFormData({...formData, next_week_plan: e.target.value})}
          required
        />

        <div className="flex gap-4">
          <Button type="submit" disabled={loading} data-testid="submit-report-btn">
            {loading ? <Spinner size="sm" /> : "Submit Report"}
          </Button>
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
        </div>
      </form>
    </Modal>
  );
};

// ==================== SIGNATURE PAD COMPONENT ====================

const SignaturePad = ({ onSave, label, name, setName }) => {
  const canvasRef = React.useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches[0].clientX) - rect.left;
    const y = (e.clientY || e.touches[0].clientY) - rect.top;
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches[0].clientX) - rect.left;
    const y = (e.clientY || e.touches[0].clientY) - rect.top;
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasSignature(true);
  };

  const endDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };

  const saveSignature = () => {
    if (!hasSignature || !name.trim()) {
      alert('Please sign and enter your name');
      return;
    }
    const canvas = canvasRef.current;
    const dataUrl = canvas.toDataURL('image/png');
    onSave(dataUrl, name);
  };

  React.useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#1e293b';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
  }, []);

  return (
    <div className="space-y-3">
      <label className="text-sm font-bold uppercase text-slate-600">{label}</label>
      <div className="border-2 border-slate-200 rounded-lg overflow-hidden bg-white">
        <canvas
          ref={canvasRef}
          width={400}
          height={150}
          className="w-full touch-none cursor-crosshair"
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={endDrawing}
          onMouseLeave={endDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={endDrawing}
        />
      </div>
      <div className="flex gap-3 items-center">
        <Input
          placeholder="Print your full name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="flex-1"
        />
        <Button variant="ghost" size="sm" onClick={clearSignature}>Clear</Button>
        <Button size="sm" onClick={saveSignature}>Confirm</Button>
      </div>
    </div>
  );
};

// ==================== CLIENTS PAGE ====================


// ==================== INVOICING PAGE ====================
// Extracted to /pages/InvoicingPage.jsx


const ClientsPage = () => {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all'); // all | regular | one_off

  const canEdit = ['admin', 'manager', 'office_staff'].includes(user?.role);

  const fetchClients = async () => {
    try {
      const res = await axios.get(`${API}/clients`);
      setClients(res.data);
    } catch (e) {
      console.error('Failed to fetch clients:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchClients(); }, []);

  const filteredClients = clients.filter(c => {
    if (typeFilter === 'one_off' && c.client_type !== 'one_off') return false;
    if (typeFilter === 'regular' && c.client_type === 'one_off') return false;
    return (
      c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.contact_person?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.abn?.includes(searchTerm)
    );
  });

  const oneOffCount = clients.filter(c => c.client_type === 'one_off').length;
  const regularCount = clients.length - oneOffCount;

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold uppercase tracking-tight">Clients</h1>
          <p className="text-slate-600">{clients.length} client(s) in database</p>
        </div>
        <div className="flex gap-3">
          <div className="relative flex-1 md:w-64">
            <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search clients..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 h-12 border-2 border-slate-200 rounded-lg focus:border-orange-500 focus:outline-none"
            />
          </div>
          {canEdit && (
            <Button onClick={() => { setSelectedClient(null); setShowModal(true); }} data-testid="add-client-btn">
              <Plus className="w-5 h-5 mr-2" /> New Client
            </Button>
          )}
        </div>
      </div>

      <div className="flex gap-2 flex-wrap" data-testid="client-type-tabs">
        {[
          { k: 'all', label: `All (${clients.length})` },
          { k: 'regular', label: `Regular (${regularCount})` },
          { k: 'one_off', label: `One-off (${oneOffCount})` }
        ].map(t => (
          <button
            key={t.k}
            onClick={() => setTypeFilter(t.k)}
            className={`px-3 py-1.5 rounded-full text-xs font-bold border ${typeFilter === t.k ? 'bg-orange-500 text-slate-900 border-orange-500' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
            data-testid={`client-type-${t.k}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Spinner /></div>
      ) : filteredClients.length === 0 ? (
        <Card className="text-center py-12">
          <Building2 className="w-12 h-12 mx-auto mb-4 text-slate-300" />
          <h3 className="text-lg font-bold mb-2">No Clients Found</h3>
          <p className="text-slate-600 mb-4">
            {searchTerm ? 'No clients match your search' : 'Get started by adding your first client'}
          </p>
          {canEdit && !searchTerm && (
            <Button onClick={() => setShowModal(true)}>
              <Plus className="w-5 h-5 mr-2" /> Add First Client
            </Button>
          )}
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredClients.map(client => (
            <Card
              key={client.id}
              interactive
              onClick={() => { setSelectedClient(client); setShowModal(true); }}
              className="hover:border-orange-500"
              data-testid={`client-card-${client.id}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-orange-600" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-bold text-lg">{client.name}</h3>
                      {client.client_type === 'one_off' && <Badge variant="warning" data-testid={`one-off-badge-${client.id}`}>One-off</Badge>}
                    </div>
                    {client.contact_person && (
                      <p className="text-sm text-slate-600">{client.contact_person}</p>
                    )}
                  </div>
                </div>
              </div>
              <div className="space-y-1 text-sm text-slate-600">
                {client.phone && <p>{client.phone}</p>}
                {client.email && <p>{client.email}</p>}
                {client.abn && <p className="text-xs">ABN: {client.abn}</p>}
              </div>
              <div className="flex gap-2 mt-3">
                <Badge variant="default">{client.total_jobs || 0} Jobs</Badge>
                <Badge variant="warning">{client.total_hires || 0} Hires</Badge>
              </div>
            </Card>
          ))}
        </div>
      )}

      <ClientModal
        isOpen={showModal}
        onClose={() => { setShowModal(false); setSelectedClient(null); }}
        client={selectedClient}
        onSave={fetchClients}
        canEdit={canEdit}
      />
    </div>
  );
};

const ClientModal = ({ isOpen, onClose, client, onSave, canEdit }) => {
  const [formData, setFormData] = useState({
    name: '', contact_person: '', phone: '', email: '',
    abn: '', billing_address: '', site_addresses: [], notes: '',
    signed_account_form_url: '', signed_account_form_name: ''
  });
  const [loading, setLoading] = useState(false);
  const [newSiteAddress, setNewSiteAddress] = useState('');
  const [uploadingForm, setUploadingForm] = useState(false);

  useEffect(() => {
    if (client) {
      setFormData({
        name: client.name || '',
        contact_person: client.contact_person || '',
        phone: client.phone || '',
        email: client.email || '',
        abn: client.abn || '',
        billing_address: client.billing_address || '',
        site_addresses: client.site_addresses || [],
        notes: client.notes || '',
        signed_account_form_url: client.signed_account_form_url || '',
        signed_account_form_name: client.signed_account_form_name || ''
      });
    } else {
      setFormData({
        name: '', contact_person: '', phone: '', email: '',
        abn: '', billing_address: '', site_addresses: [], notes: '',
        signed_account_form_url: '', signed_account_form_name: ''
      });
    }
  }, [client]);

  const handleAccountFormUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingForm(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      const res = await axios.post(`${API}/upload`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFormData({
        ...formData,
        signed_account_form_url: res.data.url || res.data.file_url,
        signed_account_form_name: file.name
      });
    } catch (e) {
      alert('Failed to upload file');
    } finally {
      setUploadingForm(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      alert('Client name is required');
      return;
    }
    setLoading(true);
    try {
      if (client) {
        await axios.put(`${API}/clients/${client.id}`, formData);
      } else {
        await axios.post(`${API}/clients`, formData);
      }
      onSave();
      onClose();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to save client');
    } finally {
      setLoading(false);
    }
  };

  const addSiteAddress = () => {
    if (newSiteAddress.trim()) {
      setFormData({
        ...formData,
        site_addresses: [...formData.site_addresses, newSiteAddress.trim()]
      });
      setNewSiteAddress('');
    }
  };

  const removeSiteAddress = (index) => {
    setFormData({
      ...formData,
      site_addresses: formData.site_addresses.filter((_, i) => i !== index)
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={client ? 'Client Details' : 'Add New Client'} size="lg">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid md:grid-cols-2 gap-4">
          <Input
            label="Company/Client Name *"
            value={formData.name}
            onChange={(e) => setFormData({...formData, name: e.target.value})}
            required
            disabled={!canEdit}
            data-testid="client-name-input"
          />
          <Input
            label="Contact Person"
            value={formData.contact_person}
            onChange={(e) => setFormData({...formData, contact_person: e.target.value})}
            disabled={!canEdit}
          />
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <Input
            label="Phone"
            value={formData.phone}
            onChange={(e) => setFormData({...formData, phone: e.target.value})}
            disabled={!canEdit}
          />
          <Input
            label="Email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({...formData, email: e.target.value})}
            disabled={!canEdit}
          />
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <Input
            label="ABN"
            value={formData.abn}
            onChange={(e) => setFormData({...formData, abn: e.target.value})}
            placeholder="XX XXX XXX XXX"
            disabled={!canEdit}
          />
          <Input
            label="Billing Address"
            value={formData.billing_address}
            onChange={(e) => setFormData({...formData, billing_address: e.target.value})}
            disabled={!canEdit}
          />
        </div>

        {/* Site Addresses */}
        <div>
          <label className="text-sm font-bold uppercase text-slate-600 mb-2 block">Common Site Addresses</label>
          <div className="space-y-2 mb-3">
            {formData.site_addresses.map((addr, idx) => (
              <div key={idx} className="flex items-center gap-2 p-2 bg-slate-50 rounded">
                <MapPin className="w-4 h-4 text-slate-400" />
                <span className="flex-1 text-sm">{addr}</span>
                {canEdit && (
                  <button type="button" onClick={() => removeSiteAddress(idx)} className="text-red-500 hover:text-red-700">
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
          {canEdit && (
            <div className="flex gap-2">
              <Input
                placeholder="Add site address..."
                value={newSiteAddress}
                onChange={(e) => setNewSiteAddress(e.target.value)}
                className="flex-1"
              />
              <Button type="button" variant="outline" onClick={addSiteAddress}>
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>

        <Textarea
          label="Notes"
          value={formData.notes}
          onChange={(e) => setFormData({...formData, notes: e.target.value})}
          placeholder="Any notes about this client..."
          disabled={!canEdit}
        />

        {/* Signed Account Form Section */}
        <div className="border-t pt-4">
          <h5 className="text-sm font-bold uppercase text-slate-600 mb-3">Signed Account Form</h5>
          {formData.signed_account_form_url ? (
            <div className="flex items-center gap-4 p-3 bg-green-50 border border-green-200 rounded-lg">
              <FileText className="w-8 h-8 text-green-600" />
              <div className="flex-1">
                <p className="font-semibold text-green-800">{formData.signed_account_form_name || 'Account Form'}</p>
                <p className="text-sm text-green-600">Document uploaded</p>
              </div>
              <div className="flex gap-2">
                <a 
                  href={formData.signed_account_form_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="px-3 py-1.5 bg-green-600 text-white rounded-md text-sm hover:bg-green-700"
                >
                  View
                </a>
                {canEdit && (
                  <button
                    type="button"
                    onClick={() => setFormData({...formData, signed_account_form_url: '', signed_account_form_name: ''})}
                    className="px-3 py-1.5 bg-red-100 text-red-600 rounded-md text-sm hover:bg-red-200"
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center">
              <Upload className="w-10 h-10 mx-auto text-slate-400 mb-2" />
              <p className="text-sm text-slate-600 mb-3">Upload signed account form (PDF, JPG, PNG)</p>
              {canEdit && (
                <label className="inline-block px-4 py-2 bg-orange-500 text-slate-900 font-semibold rounded-md cursor-pointer hover:bg-orange-400">
                  {uploadingForm ? <Spinner size="sm" /> : 'Choose File'}
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={handleAccountFormUpload}
                    className="hidden"
                    disabled={uploadingForm}
                  />
                </label>
              )}
            </div>
          )}
        </div>

        {canEdit && client && client.client_type === 'one_off' && (
          <div className="border-t pt-4 bg-amber-50 -mx-4 px-4 -mb-4 pb-4 rounded-b">
            <h5 className="text-sm font-bold uppercase text-amber-900 mb-2">One-off Client</h5>
            <p className="text-xs text-amber-700 mb-3">
              Upload a signed account form above, then promote this client to a regular client so they appear in the main list.
            </p>
            <Button
              type="button"
              disabled={!formData.signed_account_form_url || loading}
              onClick={async () => {
                if (!formData.signed_account_form_url) {
                  alert('Upload a signed account form first');
                  return;
                }
                setLoading(true);
                try {
                  await axios.post(`${API}/clients/${client.id}/promote`, {
                    signed_account_form_url: formData.signed_account_form_url,
                    signed_account_form_name: formData.signed_account_form_name
                  });
                  onSave();
                  onClose();
                } catch (e) {
                  alert(e.response?.data?.detail || 'Failed to promote');
                } finally {
                  setLoading(false);
                }
              }}
              data-testid="promote-client-btn"
            >
              Promote to Regular Client
            </Button>
          </div>
        )}

        {canEdit && (
          <div className="flex gap-3">
            <Button type="submit" disabled={loading} data-testid="save-client-btn">
              {loading ? <Spinner size="sm" /> : (client ? 'Update Client' : 'Add Client')}
            </Button>
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          </div>
        )}
      </form>
    </Modal>
  );
};

// ==================== HIRE OUT PAGE ====================

const HireOutPage = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('general');
  // Dry hire state
  const [hireOuts, setHireOuts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedHire, setSelectedHire] = useState(null);
  const [filterStatus, setFilterStatus] = useState('active');
  const [machines, setMachines] = useState([]);
  const [clients, setClients] = useState([]);
  // General hire state
  const [generalHires, setGeneralHires] = useState([]);
  const [generalFilter, setGeneralFilter] = useState('active');
  const [showGeneralModal, setShowGeneralModal] = useState(false);
  const [selectedGeneral, setSelectedGeneral] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [dailySummary, setDailySummary] = useState(null);

  const canEdit = ['admin', 'manager', 'office_staff'].includes(user?.role);

  const fetchData = async () => {
    try {
      const [hiresRes, machinesRes, clientsRes, usersRes] = await Promise.all([
        axios.get(`${API}/hire-outs${filterStatus === 'active' ? '/active' : ''}`),
        axios.get(`${API}/machines`),
        axios.get(`${API}/clients`),
        axios.get(`${API}/users`).catch(() => ({ data: [] }))
      ]);
      setHireOuts(hiresRes.data);
      setMachines(machinesRes.data);
      setClients(clientsRes.data);
      setAllUsers(usersRes.data || []);
    } catch (e) {
      console.error('Failed to fetch data:', e);
    } finally {
      setLoading(false);
    }
  };

  const fetchGeneralHires = async () => {
    try {
      const url = generalFilter === 'all' ? `${API}/general-hires` : `${API}/general-hires?status=${generalFilter}`;
      const [res, summaryRes] = await Promise.all([
        axios.get(url),
        axios.get(`${API}/general-hires/daily-summary`).catch(() => ({ data: null }))
      ]);
      setGeneralHires(res.data);
      setDailySummary(summaryRes.data);
    } catch (e) {
      console.error('Failed to fetch general hires:', e);
    }
  };

  useEffect(() => { fetchData(); }, [filterStatus]);
  useEffect(() => { fetchGeneralHires(); }, [generalFilter]);

  const getStatusBadge = (status) => {
    const variants = {
      pending_checkout: { variant: 'default', label: 'Pending Checkout' },
      out_on_hire: { variant: 'warning', label: 'Out on Hire' },
      overdue: { variant: 'destructive', label: 'OVERDUE' },
      completed: { variant: 'success', label: 'Completed' },
      cancelled: { variant: 'default', label: 'Cancelled' },
      active: { variant: 'warning', label: 'Active' },
      signed: { variant: 'info', label: 'Client Signed' }
    };
    const config = variants[status] || { variant: 'default', label: status };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  // General Hire handlers
  const [ghForm, setGhForm] = useState({
    machine_id: '', client_id: '', client_name: '', client_contact: '', client_phone: '',
    location: '', description: '', hourly_rate: '', operator_id: '', dogman_ids: [], work_date: ''
  });

  const handleCreateGeneralHire = async () => {
    if (!ghForm.machine_id) { alert("Select a machine"); return; }
    if (!ghForm.client_name && !ghForm.client_id) { alert("Enter a client name or select a client"); return; }
    if (!ghForm.location) { alert("Enter a location"); return; }
    try {
      await axios.post(`${API}/general-hires`, {
        ...ghForm,
        hourly_rate: ghForm.hourly_rate ? parseFloat(ghForm.hourly_rate) : null,
        operator_id: ghForm.operator_id || user?.id
      });
      toast.success("General hire created!");
      setShowGeneralModal(false);
      setGhForm({ machine_id: '', client_id: '', client_name: '', client_contact: '', client_phone: '', location: '', description: '', hourly_rate: '', operator_id: '', dogman_ids: [], work_date: '' });
      fetchGeneralHires();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create hire");
    }
  };

  const handleClockOn = async (hireId) => {
    try {
      await axios.post(`${API}/general-hires/${hireId}/clock-on`, {});
      toast.success("Clocked on!");
      fetchGeneralHires();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d && typeof d === "object" && d.code === "MACHINE_CONFLICT") {
        toast.error(d.message || "You're already clocked on to another machine. Clock that off first.", { duration: 8000 });
      } else {
        toast.error(typeof d === "string" ? d : "Failed to clock on");
      }
    }
  };

  const handleClockOff = async (hireId) => {
    try {
      const res = await axios.post(`${API}/general-hires/${hireId}/clock-off`, {});
      toast.success(`Clocked off! Total: ${res.data.total_hours}h`);
      fetchGeneralHires();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to clock off");
    }
  };

  const [showSignModal, setShowSignModal] = useState(null);
  const [signType, setSignType] = useState('client');

  const handleClientSign = async (hireId, name, sigData) => {
    try {
      await axios.post(`${API}/general-hires/${hireId}/client-sign`, { client_name: name, signature_data: sigData });
      toast.success("Client signature recorded!");
      setShowSignModal(null);
      fetchGeneralHires();
    } catch (e) {
      toast.error("Failed to record signature");
    }
  };

  const handleRepSign = async (hireId, sigData) => {
    try {
      await axios.post(`${API}/general-hires/${hireId}/rep-sign`, { signature_data: sigData });
      toast.success("Rep signature recorded!");
      setShowSignModal(null);
      fetchGeneralHires();
    } catch (e) {
      toast.error("Failed to record signature");
    }
  };

  const handleCompleteHire = async (hireId) => {
    try {
      await axios.post(`${API}/general-hires/${hireId}/complete`);
      toast.success("Hire completed!");
      fetchGeneralHires();
    } catch (e) {
      toast.error("Failed to complete");
    }
  };

  const operators = allUsers.filter(u => (u.capabilities || []).includes('operator'));
  const dogmen = allUsers.filter(u => (u.capabilities || []).some(c => c === 'dogman' || c === 'rigger'));

  return (
    <div className="space-y-6 animate-fade-in" data-testid="hire-out-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold uppercase tracking-tight">Machine Hire</h1>
          <p className="text-slate-600">General hire (crew + machine) and dry hire (machine only)</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b-2 border-slate-200 pb-3">
        {[
          { id: 'general', label: 'General Hire', desc: 'Crew + Machine' },
          { id: 'dry', label: 'Dry Hire', desc: 'Machine Only' }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-5 py-2.5 rounded-lg font-bold text-sm uppercase transition-all ${
              activeTab === tab.id ? 'bg-orange-500 text-white shadow-md' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
            data-testid={`tab-${tab.id}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ==================== GENERAL HIRE TAB ==================== */}
      {activeTab === 'general' && (
        <div className="space-y-4">
          {/* Daily Summary Card */}
          {dailySummary && dailySummary.total_hires > 0 && (
            <Card className="bg-gradient-to-r from-orange-50 to-amber-50 border-2 border-orange-200" data-testid="daily-summary">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold text-sm uppercase text-orange-800">Today's Summary — {dailySummary.date}</h3>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <div className="text-center p-2 bg-white rounded-lg">
                  <p className="text-2xl font-bold text-orange-600">{dailySummary.total_hires}</p>
                  <p className="text-[10px] text-slate-500 uppercase font-bold">Total Hires</p>
                </div>
                <div className="text-center p-2 bg-white rounded-lg">
                  <p className="text-2xl font-bold text-green-600">{dailySummary.active}</p>
                  <p className="text-[10px] text-slate-500 uppercase font-bold">Active</p>
                </div>
                <div className="text-center p-2 bg-white rounded-lg">
                  <p className="text-2xl font-bold text-blue-600">{dailySummary.completed}</p>
                  <p className="text-[10px] text-slate-500 uppercase font-bold">Completed</p>
                </div>
                <div className="text-center p-2 bg-white rounded-lg">
                  <p className="text-2xl font-bold text-slate-700">{dailySummary.total_hours}h</p>
                  <p className="text-[10px] text-slate-500 uppercase font-bold">Total Hours</p>
                </div>
                <div className="text-center p-2 bg-white rounded-lg">
                  <p className="text-2xl font-bold text-green-700">${dailySummary.total_revenue.toLocaleString()}</p>
                  <p className="text-[10px] text-slate-500 uppercase font-bold">Revenue</p>
                </div>
              </div>
            </Card>
          )}

          <div className="flex flex-col md:flex-row gap-3 items-start md:items-center justify-between">
            <div className="flex gap-2">
              {['active', 'signed', 'completed', 'all'].map(f => (
                <button key={f} onClick={() => setGeneralFilter(f)}
                  className={`px-3 py-1.5 rounded-md text-xs font-bold uppercase ${generalFilter === f ? 'bg-orange-100 text-orange-700 border-2 border-orange-300' : 'bg-slate-100 text-slate-500'}`}
                >
                  {f === 'all' ? 'All' : f}
                </button>
              ))}
            </div>
            {canEdit && (
              <Button onClick={() => setShowGeneralModal(true)} data-testid="new-general-hire-btn">
                <Plus className="w-4 h-4 mr-2" /> New General Hire
              </Button>
            )}
          </div>

          {generalHires.length === 0 ? (
            <Card className="text-center py-12">
              <Truck className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <h3 className="font-bold text-lg mb-1">{canEdit ? 'No General Hires' : 'No Hires Assigned'}</h3>
              <p className="text-slate-500 text-sm mb-4">{canEdit ? 'Create a general hire to send a machine + crew to a client' : 'Your assigned hires will appear here'}</p>
              {canEdit && (
                <Button onClick={() => setShowGeneralModal(true)}>
                  <Plus className="w-4 h-4 mr-2" /> New General Hire
                </Button>
              )}
            </Card>
          ) : (
            <div className="space-y-3">
              {generalHires.map(hire => (
                <Card key={hire.id} className={`border-2 transition-colors ${hire.status === 'active' && hire.clock_on_time ? 'border-green-400 bg-green-50/30' : hire.status === 'signed' ? 'border-blue-400 bg-blue-50/30' : ''}`} data-testid={`gh-card-${hire.id}`}>
                  <div className="flex flex-col gap-3">
                    {/* Header Row */}
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${hire.clock_on_time && !hire.clock_off_time ? 'bg-green-100' : 'bg-slate-100'}`}>
                          <Truck className={`w-6 h-6 ${hire.clock_on_time && !hire.clock_off_time ? 'text-green-600' : 'text-slate-500'}`} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-bold text-lg">{hire.machine_name}</h3>
                            {getStatusBadge(hire.status)}
                            {hire.hourly_rate && <span className="text-sm font-bold text-orange-600">${hire.hourly_rate}/hr</span>}
                          </div>
                          <p className="text-sm text-slate-500">{hire.work_date} {hire.machine_type && `- ${hire.machine_type}`} {hire.machine_rego && `(${hire.machine_rego})`}</p>
                        </div>
                      </div>
                      {/* Action Buttons */}
                      <div className="flex flex-wrap gap-2 flex-shrink-0">
                        {!hire.clock_on_time && (
                          <Button size="sm" onClick={() => handleClockOn(hire.id)} data-testid={`clock-on-${hire.id}`} className="py-3 px-5 text-base touch-manipulation" style={{ minHeight: '44px' }}>
                            <Play className="w-4 h-4 mr-1" /> Clock On
                          </Button>
                        )}
                        {hire.clock_on_time && !hire.clock_off_time && (
                          <Button size="sm" variant="destructive" onClick={() => handleClockOff(hire.id)} data-testid={`clock-off-${hire.id}`} className="py-3 px-5 text-base touch-manipulation" style={{ minHeight: '44px' }}>
                            <Square className="w-4 h-4 mr-1" /> Clock Off
                          </Button>
                        )}
                        {hire.clock_on_time && !hire.client_signature && (
                          <Button size="sm" variant="outline" onClick={() => { setShowSignModal(hire.id); setSignType('client'); }} data-testid={`client-sign-${hire.id}`}>
                            <Edit3 className="w-3.5 h-3.5 mr-1" /> Client Sign
                          </Button>
                        )}
                        {hire.clock_on_time && !hire.rep_signature && (
                          <Button size="sm" variant="outline" onClick={() => { setShowSignModal(hire.id); setSignType('rep'); }} data-testid={`rep-sign-${hire.id}`}>
                            <Edit3 className="w-3.5 h-3.5 mr-1" /> Rep Sign
                          </Button>
                        )}
                        {hire.status !== 'completed' && hire.client_signature && hire.clock_off_time && (
                          <Button size="sm" variant="outline" onClick={() => handleCompleteHire(hire.id)}>
                            <CheckCircle className="w-3.5 h-3.5 mr-1" /> Complete
                          </Button>
                        )}
                        {hire.clock_on_time && (
                          <Button size="sm" variant="outline" onClick={async () => {
                            try {
                              const res = await axios.get(`${API}/general-hires/${hire.id}/pdf`, { responseType: 'blob' });
                              const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                              const a = document.createElement('a'); a.href = url; a.download = `docket_${hire.client_name}_${hire.work_date}.pdf`; a.click();
                            } catch (e) { toast.error("Failed to generate docket"); }
                          }} data-testid={`download-docket-${hire.id}`}>
                            <Download className="w-3.5 h-3.5 mr-1" /> Docket PDF
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Job Details - visible to operators */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 text-sm border-t pt-3">
                      <div className="flex gap-2"><span className="text-slate-400 font-medium w-20 flex-shrink-0">Client:</span><span className="font-semibold">{hire.client_name}</span></div>
                      <div className="flex gap-2"><span className="text-slate-400 font-medium w-20 flex-shrink-0">Location:</span><span className="font-semibold">{hire.location}</span></div>
                      {hire.client_contact && <div className="flex gap-2"><span className="text-slate-400 font-medium w-20 flex-shrink-0">Contact:</span><span>{hire.client_contact}</span></div>}
                      {hire.client_phone && <div className="flex gap-2"><span className="text-slate-400 font-medium w-20 flex-shrink-0">Phone:</span><span><a href={`tel:${hire.client_phone}`} className="text-blue-600 underline">{hire.client_phone}</a></span></div>}
                      {hire.operator_name && <div className="flex gap-2"><span className="text-slate-400 font-medium w-20 flex-shrink-0">Operator:</span><span>{hire.operator_name}</span></div>}
                      {hire.dogman_names?.length > 0 && <div className="flex gap-2"><span className="text-slate-400 font-medium w-20 flex-shrink-0">Dogman:</span><span>{hire.dogman_names.map(d => d.name).join(', ')}</span></div>}
                    </div>
                    {hire.description && (
                      <div className="text-sm bg-slate-50 rounded-lg p-3">
                        <span className="text-slate-400 font-medium text-xs uppercase">Work Description: </span>
                        <span className="text-slate-700">{hire.description}</span>
                      </div>
                    )}

                    {/* Clock Times */}
                    {hire.clock_on_time && (
                      <div className="flex flex-wrap gap-4 text-sm border-t pt-2">
                        <span className="text-green-700 font-medium">Clocked On: {new Date(hire.clock_on_time).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })}</span>
                        {hire.clock_off_time && <span className="text-red-600 font-medium">Clocked Off: {new Date(hire.clock_off_time).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })}</span>}
                        {hire.total_hours && <span className="text-slate-800 font-bold">{hire.total_hours}h total</span>}
                        {hire.client_signature && <Badge variant="success">Client Signed</Badge>}
                        {hire.rep_signature && <Badge variant="info">Rep Signed</Badge>}
                      </div>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          )}

          {/* New General Hire Modal */}
          {showGeneralModal && (
            <Modal isOpen={true} onClose={() => setShowGeneralModal(false)} title="New General Hire" size="lg">
              <div className="space-y-4">
                <Select label="Machine *" value={ghForm.machine_id} onChange={(e) => setGhForm({...ghForm, machine_id: e.target.value})}
                  options={[{ value: '', label: '-- Select Machine --' }, ...machines.map(m => ({ value: m.id, label: `${m.name} (${m.type || ''}) ${m.rego || ''}` }))]}
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Select label="Client (existing)" value={ghForm.client_id}
                    onChange={(e) => {
                      const c = clients.find(cl => cl.id === e.target.value);
                      setGhForm({...ghForm, client_id: e.target.value, client_name: c?.name || ghForm.client_name });
                    }}
                    options={[{ value: '', label: '-- Or type below --' }, ...clients.map(c => ({ value: c.id, label: c.name }))]}
                  />
                  <Input label="Client Name *" value={ghForm.client_name} onChange={(e) => setGhForm({...ghForm, client_name: e.target.value})} placeholder="One-off client name" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input label="Client Contact" value={ghForm.client_contact} onChange={(e) => setGhForm({...ghForm, client_contact: e.target.value})} placeholder="Contact person" />
                  <Input label="Client Phone" value={ghForm.client_phone} onChange={(e) => setGhForm({...ghForm, client_phone: e.target.value})} placeholder="Phone number" />
                </div>
                <Input label="Location *" value={ghForm.location} onChange={(e) => setGhForm({...ghForm, location: e.target.value})} placeholder="Job site address" />
                <Textarea label="Description" value={ghForm.description} onChange={(e) => setGhForm({...ghForm, description: e.target.value})} placeholder="What work is being done today?" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input label="Hourly Rate ($)" type="number" step="0.01" value={ghForm.hourly_rate} onChange={(e) => setGhForm({...ghForm, hourly_rate: e.target.value})} placeholder="e.g. 250" />
                  <DatePicker label="Work Date" value={ghForm.work_date} onChange={(v) => setGhForm({...ghForm, work_date: v})} testId="gh-work-date" />
                </div>
                <Select label="Operator" value={ghForm.operator_id} onChange={(e) => setGhForm({...ghForm, operator_id: e.target.value})}
                  options={[{ value: '', label: `-- Default: ${user?.first_name} ${user?.last_name} --` }, ...operators.map(u => ({ value: u.id, label: `${u.first_name} ${u.last_name} (${u.role?.replace('_',' ')})` }))]}
                />
                <div>
                  <label className="block text-xs font-bold uppercase text-slate-600 mb-2">Dogman(s)</label>
                  <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto">
                    {dogmen.map(d => (
                      <label key={d.id} className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer text-sm ${ghForm.dogman_ids.includes(d.id) ? 'bg-blue-100 border-2 border-blue-400' : 'bg-slate-50 border-2 border-transparent'}`}>
                        <input type="checkbox" checked={ghForm.dogman_ids.includes(d.id)}
                          onChange={() => setGhForm({...ghForm, dogman_ids: ghForm.dogman_ids.includes(d.id) ? ghForm.dogman_ids.filter(id => id !== d.id) : [...ghForm.dogman_ids, d.id]})}
                          className="w-4 h-4"
                        />
                        {d.first_name} {d.last_name}
                      </label>
                    ))}
                    {dogmen.length === 0 && <p className="text-sm text-slate-400 col-span-2">No dogmen available</p>}
                  </div>
                </div>
                <div className="flex gap-2 pt-2">
                  <Button onClick={handleCreateGeneralHire} data-testid="submit-general-hire">Create General Hire</Button>
                  <Button variant="ghost" onClick={() => setShowGeneralModal(false)}>Cancel</Button>
                </div>
              </div>
            </Modal>
          )}

          {/* Signature Modal */}
          {showSignModal && (
            <Modal isOpen={true} onClose={() => setShowSignModal(null)} title={signType === 'client' ? 'Client Signature' : 'Rep Signature'}>
              <HireSignaturePad
                signType={signType}
                onSubmit={(name, sigData) => {
                  if (signType === 'client') handleClientSign(showSignModal, name, sigData);
                  else handleRepSign(showSignModal, sigData);
                }}
                onCancel={() => setShowSignModal(null)}
              />
            </Modal>
          )}
        </div>
      )}

      {/* ==================== DRY HIRE TAB ==================== */}
      {activeTab === 'dry' && (
        <div className="space-y-4">
          <div className="flex flex-col md:flex-row gap-3 items-start md:items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">Machine goes out to client with no crew — agreed price for a period of time</p>
            </div>
            <div className="flex gap-3">
              <Select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
                options={[{ value: 'active', label: 'Active Hires' }, { value: 'all', label: 'All Hires' }]}
                className="w-40"
              />
              {canEdit && (
                <Button onClick={() => { setSelectedHire(null); setShowModal(true); }} data-testid="new-dry-hire-btn">
                  <Plus className="w-5 h-5 mr-2" /> New Dry Hire
                </Button>
              )}
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-12"><Spinner /></div>
          ) : hireOuts.length === 0 ? (
            <Card className="text-center py-12">
              <Truck className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <h3 className="text-lg font-bold mb-2">No Dry Hires</h3>
              <p className="text-slate-600 mb-4">Hire out machines to clients (no crew)</p>
              {canEdit && <Button onClick={() => setShowModal(true)}><Plus className="w-5 h-5 mr-2" /> Create Dry Hire</Button>}
            </Card>
          ) : (
            <div className="space-y-4">
              {hireOuts.map(hire => (
                <Card key={hire.id} interactive onClick={() => { setSelectedHire(hire); setShowModal(true); }}
                  className={`hover:border-orange-500 ${hire.status === 'overdue' ? 'border-red-500 bg-red-50' : ''}`}
                  data-testid={`dry-hire-card-${hire.id}`}>
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <div className={`w-14 h-14 rounded-lg flex items-center justify-center ${hire.status === 'overdue' ? 'bg-red-100' : hire.status === 'out_on_hire' ? 'bg-amber-100' : 'bg-slate-100'}`}>
                        <Truck className={`w-7 h-7 ${hire.status === 'overdue' ? 'text-red-600' : hire.status === 'out_on_hire' ? 'text-amber-600' : 'text-slate-600'}`} />
                      </div>
                      <div>
                        <h3 className="font-bold text-lg">{hire.machine_name}</h3>
                        <p className="text-slate-600">{hire.client_name}</p>
                        <p className="text-sm text-slate-500">{hire.hire_location}</p>
                      </div>
                    </div>
                    <div className="flex flex-col md:items-end gap-2">
                      {getStatusBadge(hire.status)}
                      <p className="text-sm text-slate-600">{hire.start_date} → {hire.expected_return_date}</p>
                      <p className="text-sm font-bold text-orange-600">${hire.rate_amount}/{hire.rate_type}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}

          <HireOutModal isOpen={showModal} onClose={() => { setShowModal(false); setSelectedHire(null); }}
            hire={selectedHire} machines={machines} clients={clients} onSave={fetchData} canEdit={canEdit} />
        </div>
      )}
    </div>
  );
};

// Signature Pad mini-component for General Hire
const HireSignaturePad = ({ signType, onSubmit, onCancel }) => {
  const [name, setName] = useState('');
  const canvasRef = useRef(null);
  const [sig, setSig] = useState('');

  const getCoords = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const cx = e.touches ? e.touches[0].clientX : e.clientX;
    const cy = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: (cx - rect.left) * scaleX, y: (cy - rect.top) * scaleY };
  };

  return (
    <div className="space-y-4">
      {signType === 'client' && (
        <Input label="Client Name (printed) *" value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" />
      )}
      <div>
        <label className="text-xs font-bold uppercase text-slate-600 mb-2 block">Signature *</label>
        <canvas ref={canvasRef} width={500} height={120}
          className="w-full border-2 border-slate-200 rounded bg-white cursor-crosshair" style={{ touchAction: 'none' }}
          onMouseDown={(e) => { const c = getCoords(e); const ctx = canvasRef.current.getContext('2d'); ctx.lineWidth = 2; ctx.lineCap = 'round'; ctx.strokeStyle = '#000'; ctx.beginPath(); ctx.moveTo(c.x, c.y); canvasRef.current._drawing = true; }}
          onMouseMove={(e) => { if (!canvasRef.current?._drawing) return; const c = getCoords(e); const ctx = canvasRef.current.getContext('2d'); ctx.lineTo(c.x, c.y); ctx.stroke(); }}
          onMouseUp={() => { if (canvasRef.current) { canvasRef.current._drawing = false; setSig(canvasRef.current.toDataURL()); } }}
          onMouseLeave={() => { if (canvasRef.current) canvasRef.current._drawing = false; }}
          onTouchStart={(e) => { e.preventDefault(); const c = getCoords(e); const ctx = canvasRef.current.getContext('2d'); ctx.lineWidth = 2; ctx.lineCap = 'round'; ctx.strokeStyle = '#000'; ctx.beginPath(); ctx.moveTo(c.x, c.y); canvasRef.current._drawing = true; }}
          onTouchMove={(e) => { e.preventDefault(); if (!canvasRef.current?._drawing) return; const c = getCoords(e); const ctx = canvasRef.current.getContext('2d'); ctx.lineTo(c.x, c.y); ctx.stroke(); }}
          onTouchEnd={() => { if (canvasRef.current) { canvasRef.current._drawing = false; setSig(canvasRef.current.toDataURL()); } }}
        />
        <Button size="sm" variant="outline" className="mt-2" onClick={() => { const ctx = canvasRef.current?.getContext('2d'); ctx?.clearRect(0, 0, 500, 120); setSig(''); }}>Clear</Button>
      </div>
      <div className="flex gap-2">
        <Button onClick={() => { if (signType === 'client' && !name) { alert('Enter client name'); return; } if (!sig) { alert('Please sign'); return; } onSubmit(name, sig); }}
          disabled={!sig || (signType === 'client' && !name)} data-testid="submit-signature">
          Submit Signature
        </Button>
        <Button variant="ghost" onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
};

const HireOutModal = ({ isOpen, onClose, hire, machines, clients, onSave, canEdit }) => {
  const { user } = useAuth();
  const [step, setStep] = useState('details'); // details, checkout, return
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  
  // Form data for new hire
  const [formData, setFormData] = useState({
    machine_id: '', client_id: '',
    start_date: new Date().toISOString().split('T')[0],
    start_time: '07:00',
    expected_return_date: '',
    rate_type: 'daily', rate_amount: '',
    delivery_method: 'client_pickup',
    hire_location: '',
    hours_out: '', km_out: '', fuel_level_out: 100,
    condition_notes_out: '', pre_existing_damage: '',
    client_provides_operator: false,
    operator_name: '', operator_phone: '', operator_ticket_type: '', operator_ticket_number: '',
    insurance_certificate_url: '',
    special_conditions: '', notes: ''
  });

  // Checkout form data
  const [checkoutData, setCheckoutData] = useState({
    condition_photos: [],
    insurance_verified: false,
    terms_accepted: true, condition_accepted: true,
    return_same_condition: true, maintenance_responsibility: true, damage_responsibility: true,
    cleaning_fee_accepted: true, paint_damage_accepted: true,
    client_signature: '', client_name: '',
    employee_signature: '', employee_name: ''
  });

  // Return form data
  const [returnData, setReturnData] = useState({
    return_date: new Date().toISOString().split('T')[0],
    return_time: '',
    hours_in: '', km_in: '', fuel_level_in: 100,
    condition_notes_in: '',
    return_photos: [],
    return_condition: 'good',
    cleanliness_condition: 'clean',
    new_damage_found: false, damage_description: '', damage_photos: [],
    estimated_repair_cost: '',
    fuel_charge: 0, cleaning_charge: 0, damage_charge: 0, other_charges: 0,
    charge_notes: '', returned_acceptable: true, return_notes: '',
    client_signature: '', client_name: '',
    employee_signature: '', employee_name: ''
  });

  useEffect(() => {
    if (hire) {
      if (hire.status === 'pending_checkout') setStep('checkout');
      else if (hire.status === 'out_on_hire' || hire.status === 'overdue') setStep('return');
      else setStep('details');
      
      // Pre-fill return data
      setReturnData(prev => ({
        ...prev,
        hours_in: hire.hours_out || '',
        km_in: hire.km_out || ''
      }));
    } else {
      setStep('details');
      setFormData({
        machine_id: '', client_id: '',
        start_date: new Date().toISOString().split('T')[0],
        start_time: '07:00',
        expected_return_date: '',
        rate_type: 'daily', rate_amount: '',
        delivery_method: 'client_pickup',
        hire_location: '',
        hours_out: '', km_out: '', fuel_level_out: 100,
        condition_notes_out: '', pre_existing_damage: '',
        client_provides_operator: false,
        operator_name: '', operator_phone: '', operator_ticket_type: '', operator_ticket_number: '',
        insurance_certificate_url: '',
        special_conditions: '', notes: ''
      });
    }
  }, [hire]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await axios.get(`${API}/users`);
        setUsers(res.data);
      } catch (e) {}
    };
    if (isOpen) fetchUsers();
  }, [isOpen]);

  const handlePhotoUpload = async (e, type) => {
    const files = Array.from(e.target.files);
    for (const file of files) {
      const reader = new FileReader();
      reader.onload = () => {
        const photo = { photo_url: reader.result, description: '', photo_type: 'condition' };
        if (type === 'checkout') {
          setCheckoutData(prev => ({
            ...prev,
            condition_photos: [...prev.condition_photos, photo]
          }));
        } else if (type === 'return') {
          setReturnData(prev => ({
            ...prev,
            return_photos: [...prev.return_photos, photo]
          }));
        } else if (type === 'damage') {
          setReturnData(prev => ({
            ...prev,
            damage_photos: [...prev.damage_photos, { ...photo, photo_type: 'damage' }]
          }));
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCreateHire = async () => {
    if (!formData.machine_id || !formData.client_id || !formData.expected_return_date || !formData.rate_amount) {
      alert('Please fill in all required fields');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...formData,
        rate_amount: parseFloat(formData.rate_amount),
        hours_out: formData.hours_out ? parseFloat(formData.hours_out) : null,
        km_out: formData.km_out ? parseFloat(formData.km_out) : null,
        fuel_level_out: parseInt(formData.fuel_level_out),
        operator: formData.client_provides_operator ? {
          name: formData.operator_name,
          phone: formData.operator_phone,
          ticket_type: formData.operator_ticket_type,
          ticket_number: formData.operator_ticket_number
        } : null
      };
      await axios.post(`${API}/hire-outs`, payload);
      alert('Hire out created! Now complete the checkout process.');
      onSave();
      onClose();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to create hire out');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckout = async () => {
    const REQUIRED_TYPES = ["front", "back", "left", "right", "odometer", "hour_meter"];
    const provided = new Set(checkoutData.condition_photos.map(p => (p.photo_type || '').toLowerCase()));
    const missing = REQUIRED_TYPES.filter(t => !provided.has(t));
    if (missing.length > 0) {
      alert(`Missing required photos: ${missing.join(', ').replace(/_/g, ' ')}.\n\nYou must capture all 4 sides plus odometer and hour-meter readings before checkout.`);
      return;
    }
    if (!checkoutData.client_signature || !checkoutData.client_name) {
      alert('Client signature and name required');
      return;
    }
    if (!checkoutData.employee_signature || !checkoutData.employee_name) {
      alert('Employee signature and name required');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/hire-outs/${hire.id}/checkout`, checkoutData);
      alert('Machine checked out successfully!');
      onSave();
      onClose();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to complete checkout');
    } finally {
      setLoading(false);
    }
  };

  const handleReturn = async () => {
    const REQUIRED_TYPES = ["front", "back", "left", "right", "odometer", "hour_meter"];
    const provided = new Set(returnData.return_photos.map(p => (p.photo_type || '').toLowerCase()));
    const missing = REQUIRED_TYPES.filter(t => !provided.has(t));
    if (missing.length > 0) {
      alert(`Missing required return photos: ${missing.join(', ').replace(/_/g, ' ')}.\n\nCapture all 4 sides plus odometer and hour-meter readings.`);
      return;
    }
    if (!returnData.client_signature || !returnData.client_name) {
      alert('Client signature and name required');
      return;
    }
    if (!returnData.employee_signature || !returnData.employee_name) {
      alert('Employee signature and name required');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...returnData,
        hours_in: returnData.hours_in ? parseFloat(returnData.hours_in) : null,
        km_in: returnData.km_in ? parseFloat(returnData.km_in) : null,
        fuel_level_in: parseInt(returnData.fuel_level_in),
        fuel_charge: parseFloat(returnData.fuel_charge) || 0,
        cleaning_charge: parseFloat(returnData.cleaning_charge) || 0,
        damage_charge: parseFloat(returnData.damage_charge) || 0,
        other_charges: parseFloat(returnData.other_charges) || 0,
        estimated_repair_cost: returnData.estimated_repair_cost ? parseFloat(returnData.estimated_repair_cost) : null
      };
      const res = await axios.post(`${API}/hire-outs/${hire.id}/return`, payload);
      alert(`Machine returned! Total days: ${res.data.total_days}, Charges: $${res.data.total_charges}`);
      onSave();
      onClose();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to process return');
    } finally {
      setLoading(false);
    }
  };

  const selectedMachine = machines.find(m => m.id === formData.machine_id);
  const selectedClient = clients.find(c => c.id === formData.client_id);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={hire ? `Hire Out - ${hire.machine_name}` : 'New Machine Hire Out'} size="lg">
      {/* Step indicator for existing hire */}
      {hire && (
        <div className="flex gap-2 mb-6">
          <Button
            size="sm"
            variant={step === 'details' ? 'primary' : 'ghost'}
            onClick={() => setStep('details')}
          >
            Details
          </Button>
          {hire.status === 'pending_checkout' && (
            <Button
              size="sm"
              variant={step === 'checkout' ? 'primary' : 'ghost'}
              onClick={() => setStep('checkout')}
            >
              Checkout
            </Button>
          )}
          {(hire.status === 'out_on_hire' || hire.status === 'overdue') && (
            <Button
              size="sm"
              variant={step === 'return' ? 'primary' : 'ghost'}
              onClick={() => setStep('return')}
            >
              Return
            </Button>
          )}
        </div>
      )}

      {/* NEW HIRE FORM */}
      {!hire && step === 'details' && (
        <div className="space-y-6">
          <Card className="bg-slate-50">
            <h4 className="font-bold uppercase text-sm mb-4">Machine & Client</h4>
            <div className="grid md:grid-cols-2 gap-4">
              <Select
                label="Select Machine *"
                value={formData.machine_id}
                onChange={(e) => {
                  const machine = machines.find(m => m.id === e.target.value);
                  setFormData({
                    ...formData,
                    machine_id: e.target.value,
                    hours_out: machine?.current_hours || '',
                    km_out: machine?.current_km || ''
                  });
                }}
                options={[
                  { value: '', label: '-- Select Machine --' },
                  ...machines.filter(m => m.status !== 'hired_out').map(m => ({
                    value: m.id,
                    label: `${m.name} (${m.status || 'available'})`
                  }))
                ]}
              />
              <Select
                label="Select Client *"
                value={formData.client_id}
                onChange={(e) => setFormData({...formData, client_id: e.target.value})}
                options={[
                  { value: '', label: '-- Select Client --' },
                  ...clients.map(c => ({ value: c.id, label: c.name }))
                ]}
              />
            </div>
          </Card>

          <Card className="bg-slate-50">
            <h4 className="font-bold uppercase text-sm mb-4">Hire Period & Rates</h4>
            <div className="grid md:grid-cols-4 gap-4">
              <Input
                label="Start Date *"
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData({...formData, start_date: e.target.value})}
              />
              <Input
                label="Start Time"
                type="time"
                value={formData.start_time}
                onChange={(e) => setFormData({...formData, start_time: e.target.value})}
              />
              <Input
                label="Expected Return *"
                type="date"
                value={formData.expected_return_date}
                onChange={(e) => setFormData({...formData, expected_return_date: e.target.value})}
              />
              <Select
                label="Delivery"
                value={formData.delivery_method}
                onChange={(e) => setFormData({...formData, delivery_method: e.target.value})}
                options={[
                  { value: 'client_pickup', label: 'Client Pickup' },
                  { value: 'company_delivers', label: 'We Deliver' }
                ]}
              />
            </div>
            <div className="grid md:grid-cols-3 gap-4 mt-4">
              <Select
                label="Rate Type"
                value={formData.rate_type}
                onChange={(e) => setFormData({...formData, rate_type: e.target.value})}
                options={[
                  { value: 'hourly', label: 'Hourly' },
                  { value: 'daily', label: 'Daily' },
                  { value: 'weekly', label: 'Weekly' }
                ]}
              />
              <Input
                label="Rate Amount ($) *"
                type="number"
                value={formData.rate_amount}
                onChange={(e) => setFormData({...formData, rate_amount: e.target.value})}
                placeholder="e.g., 850"
              />
              <Input
                label="Hire Location *"
                value={formData.hire_location}
                onChange={(e) => setFormData({...formData, hire_location: e.target.value})}
                placeholder="Site address"
              />
            </div>
          </Card>

          <Card className="bg-slate-50">
            <h4 className="font-bold uppercase text-sm mb-4">Machine Condition (at Hire Out)</h4>
            <div className="grid md:grid-cols-4 gap-4">
              <Input
                label="Hours Reading"
                type="number"
                value={formData.hours_out}
                onChange={(e) => setFormData({...formData, hours_out: e.target.value})}
              />
              <Input
                label="KM Reading"
                type="number"
                value={formData.km_out}
                onChange={(e) => setFormData({...formData, km_out: e.target.value})}
              />
              <Input
                label="Fuel Level (%)"
                type="number"
                min="0"
                max="100"
                value={formData.fuel_level_out}
                onChange={(e) => setFormData({...formData, fuel_level_out: e.target.value})}
              />
            </div>
            <div className="grid md:grid-cols-2 gap-4 mt-4">
              <Textarea
                label="Condition Notes"
                value={formData.condition_notes_out}
                onChange={(e) => setFormData({...formData, condition_notes_out: e.target.value})}
                placeholder="General condition..."
              />
              <Textarea
                label="Pre-existing Damage"
                value={formData.pre_existing_damage}
                onChange={(e) => setFormData({...formData, pre_existing_damage: e.target.value})}
                placeholder="Note any existing damage..."
              />
            </div>
          </Card>

          {/* Operator Section */}
          <Card className="bg-slate-50">
            <div className="flex items-center gap-3 mb-4">
              <input
                type="checkbox"
                id="clientOperator"
                checked={formData.client_provides_operator}
                onChange={(e) => setFormData({...formData, client_provides_operator: e.target.checked})}
                className="w-5 h-5"
              />
              <label htmlFor="clientOperator" className="font-bold uppercase text-sm">Client Provides Operator</label>
            </div>
            {formData.client_provides_operator && (
              <div className="grid md:grid-cols-2 gap-4">
                <Input
                  label="Operator Name"
                  value={formData.operator_name}
                  onChange={(e) => setFormData({...formData, operator_name: e.target.value})}
                />
                <Input
                  label="Operator Phone"
                  value={formData.operator_phone}
                  onChange={(e) => setFormData({...formData, operator_phone: e.target.value})}
                />
                <Input
                  label="Ticket Type"
                  value={formData.operator_ticket_type}
                  onChange={(e) => setFormData({...formData, operator_ticket_type: e.target.value})}
                  placeholder="e.g., Crane Operator"
                />
                <Input
                  label="Ticket Number"
                  value={formData.operator_ticket_number}
                  onChange={(e) => setFormData({...formData, operator_ticket_number: e.target.value})}
                />
              </div>
            )}
          </Card>

          <Textarea
            label="Notes"
            value={formData.notes}
            onChange={(e) => setFormData({...formData, notes: e.target.value})}
            placeholder="Any additional notes..."
          />

          <div className="flex gap-3">
            <Button onClick={handleCreateHire} disabled={loading}>
              {loading ? <Spinner size="sm" /> : 'Create Hire Out'}
            </Button>
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
          </div>
        </div>
      )}

      {/* DETAILS VIEW */}
      {hire && step === 'details' && (
        <div className="space-y-6">
          <div className="grid md:grid-cols-2 gap-4">
            <Card className="bg-blue-50 border-blue-200">
              <h4 className="font-bold uppercase text-sm mb-3">Machine</h4>
              <p className="text-xl font-bold">{hire.machine_name}</p>
              <p className="text-slate-600">{hire.machine_type?.replace('_', ' ')}</p>
              {hire.machine_rego && <p className="text-sm">Rego: {hire.machine_rego}</p>}
            </Card>
            <Card className="bg-green-50 border-green-200">
              <h4 className="font-bold uppercase text-sm mb-3">Client</h4>
              <p className="text-xl font-bold">{hire.client_name}</p>
              {hire.client_contact && <p className="text-slate-600">{hire.client_contact}</p>}
              {hire.client_phone && <p className="text-sm">{hire.client_phone}</p>}
            </Card>
          </div>

          <Card>
            <h4 className="font-bold uppercase text-sm mb-3">Hire Details</h4>
            <div className="grid md:grid-cols-3 gap-4 text-sm">
              <div><span className="text-slate-500">Period:</span> <strong>{hire.start_date} → {hire.expected_return_date}</strong></div>
              <div><span className="text-slate-500">Rate:</span> <strong>${hire.rate_amount}/{hire.rate_type}</strong></div>
              <div><span className="text-slate-500">Location:</span> <strong>{hire.hire_location}</strong></div>
              <div><span className="text-slate-500">Hours Out:</span> <strong>{hire.hours_out || 'N/A'}</strong></div>
              <div><span className="text-slate-500">KM Out:</span> <strong>{hire.km_out || 'N/A'}</strong></div>
              <div><span className="text-slate-500">Fuel:</span> <strong>{hire.fuel_level_out}%</strong></div>
            </div>
            {hire.condition_notes_out && (
              <p className="mt-3 text-sm text-slate-600 bg-slate-50 p-2 rounded">{hire.condition_notes_out}</p>
            )}
          </Card>

          {/* Checkout Photos - Damage Report */}
          {hire.condition_photos_out && hire.condition_photos_out.length > 0 && (
            <Card className="bg-orange-50 border-orange-200">
              <h4 className="font-bold uppercase text-sm mb-3 flex items-center gap-2">
                <Camera className="w-4 h-4" />
                Checkout Photos - Damage & Cleanliness Report ({hire.condition_photos_out.length})
              </h4>
              <p className="text-sm text-slate-600 mb-3">Photos taken when machine was hired out</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {hire.condition_photos_out.map((photo, idx) => (
                  <div key={idx} className="relative group">
                    <img 
                      src={photo.photo_url} 
                      alt={`Checkout photo ${idx+1}`} 
                      className="w-full h-32 object-cover rounded-lg border-2 border-orange-300 cursor-pointer hover:opacity-90"
                      onClick={() => openFile(photo.photo_url, 'photo')}
                    />
                    <div className="absolute bottom-1 left-1 bg-black/70 text-white text-xs px-2 py-1 rounded">
                      Photo {idx+1}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {hire.operator && (
            <Card className="bg-amber-50 border-amber-200">
              <h4 className="font-bold uppercase text-sm mb-3">Client's Operator</h4>
              <div className="grid md:grid-cols-2 gap-2 text-sm">
                <div><span className="text-slate-500">Name:</span> <strong>{hire.operator.name}</strong></div>
                <div><span className="text-slate-500">Phone:</span> <strong>{hire.operator.phone}</strong></div>
                <div><span className="text-slate-500">Ticket:</span> <strong>{hire.operator.ticket_type} - {hire.operator.ticket_number}</strong></div>
              </div>
            </Card>
          )}

          {/* Return Photos & Summary - only show if returned */}
          {hire.status === 'completed' && hire.return_data && (
            <>
              <Card className="bg-green-50 border-green-200">
                <h4 className="font-bold uppercase text-sm mb-3">Return Summary</h4>
                <div className="grid md:grid-cols-3 gap-4 text-sm">
                  <div><span className="text-slate-500">Returned:</span> <strong>{hire.actual_return_date}</strong></div>
                  <div><span className="text-slate-500">Total Days:</span> <strong>{hire.total_days}</strong></div>
                  <div><span className="text-slate-500">Hours Used:</span> <strong>{hire.hours_used || 'N/A'}</strong></div>
                  <div><span className="text-slate-500">KM Used:</span> <strong>{hire.km_used || 'N/A'}</strong></div>
                  <div><span className="text-slate-500">Condition:</span> <Badge variant={hire.return_data.return_condition === 'good' ? 'success' : 'warning'}>{hire.return_data.return_condition}</Badge></div>
                  <div><span className="text-slate-500">Charges:</span> <strong className="text-red-600">${hire.total_charges}</strong></div>
                </div>
                {hire.return_data.condition_notes_in && (
                  <p className="mt-3 text-sm text-slate-600 bg-white p-2 rounded">{hire.return_data.condition_notes_in}</p>
                )}
              </Card>

              {/* Return Photos */}
              {hire.return_data.return_photos && hire.return_data.return_photos.length > 0 && (
                <Card className="bg-blue-50 border-blue-200">
                  <h4 className="font-bold uppercase text-sm mb-3 flex items-center gap-2">
                    <Camera className="w-4 h-4" />
                    Return Photos ({hire.return_data.return_photos.length})
                  </h4>
                  <p className="text-sm text-slate-600 mb-3">Photos taken when machine was returned</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {hire.return_data.return_photos.map((photo, idx) => (
                      <div key={idx} className="relative group">
                        <img 
                          src={photo.photo_url} 
                          alt={`Return photo ${idx+1}`} 
                          className="w-full h-32 object-cover rounded-lg border-2 border-blue-300 cursor-pointer hover:opacity-90"
                          onClick={() => openFile(photo.photo_url, 'photo')}
                        />
                        <div className="absolute bottom-1 left-1 bg-black/70 text-white text-xs px-2 py-1 rounded">
                          Photo {idx+1}
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              {/* Damage Photos if any */}
              {hire.return_data.new_damage_found && hire.return_data.damage_photos && hire.return_data.damage_photos.length > 0 && (
                <Card className="bg-red-50 border-red-200">
                  <h4 className="font-bold uppercase text-sm mb-3 flex items-center gap-2 text-red-800">
                    <AlertCircle className="w-4 h-4" />
                    Damage Photos ({hire.return_data.damage_photos.length})
                  </h4>
                  <p className="text-sm text-red-700 mb-3">{hire.return_data.damage_description}</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {hire.return_data.damage_photos.map((photo, idx) => (
                      <div key={idx} className="relative">
                        <img 
                          src={photo.photo_url} 
                          alt={`Damage photo ${idx+1}`} 
                          className="w-full h-32 object-cover rounded-lg border-2 border-red-300 cursor-pointer hover:opacity-90"
                          onClick={() => openFile(photo.photo_url, 'photo')}
                        />
                        <div className="absolute bottom-1 left-1 bg-red-600 text-white text-xs px-2 py-1 rounded">
                          Damage {idx+1}
                        </div>
                      </div>
                    ))}
                  </div>
                  {hire.return_data.estimated_repair_cost && (
                    <p className="mt-3 text-sm font-bold text-red-800">
                      Estimated Repair Cost: ${hire.return_data.estimated_repair_cost}
                    </p>
                  )}
                </Card>
              )}
            </>
          )}

          {/* Side-by-side Comparison for completed hires */}
          {hire.status === 'completed' && hire.condition_photos_out?.length > 0 && hire.return_data?.return_photos?.length > 0 && (
            <Card>
              <h4 className="font-bold uppercase text-sm mb-3 flex items-center gap-2">
                <Eye className="w-4 h-4" />
                Photo Comparison (Checkout vs Return)
              </h4>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-semibold text-orange-600 mb-2 uppercase">Checkout Photos</p>
                  <div className="grid grid-cols-2 gap-2">
                    {hire.condition_photos_out.slice(0, 4).map((photo, idx) => (
                      <img key={idx} src={photo.photo_url} alt={`Checkout ${idx+1}`} className="w-full h-24 object-cover rounded border-2 border-orange-300" />
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-xs font-semibold text-blue-600 mb-2 uppercase">Return Photos</p>
                  <div className="grid grid-cols-2 gap-2">
                    {hire.return_data.return_photos.slice(0, 4).map((photo, idx) => (
                      <img key={idx} src={photo.photo_url} alt={`Return ${idx+1}`} className="w-full h-24 object-cover rounded border-2 border-blue-300" />
                    ))}
                  </div>
                </div>
              </div>
            </Card>
          )}
        </div>
      )}

      {/* CHECKOUT STEP */}
      {hire && step === 'checkout' && (
        <div className="space-y-6">
          {/* Damage & Cleanliness Photos */}
          <Card className="bg-orange-50 border-orange-200">
            <h4 className="font-bold uppercase text-sm mb-2">Damage & Cleanliness Report Photos (Min 4 Required)</h4>
            <p className="text-sm text-slate-600 mb-3">
              Take clear photos documenting the current condition of the machine. These photos will be used for comparison when the machine is returned.
            </p>
            <div className="bg-white p-3 rounded-lg border border-orange-300 mb-4">
              <p className="text-sm font-semibold text-slate-700 mb-2">Required photos:</p>
              <ul className="text-sm text-slate-600 space-y-1 list-disc list-inside">
                <li><strong>Front view</strong> - Full frontal shot showing overall condition</li>
                <li><strong>Rear view</strong> - Back of machine including any attachments</li>
                <li><strong>Left side</strong> - Full side profile</li>
                <li><strong>Right side</strong> - Full side profile</li>
                <li><strong>Close-ups of any existing damage</strong> - Scratches, dents, paint damage</li>
                <li><strong>Interior/cabin condition</strong> - Cleanliness, controls, seat condition</li>
              </ul>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 text-sm">
              <p className="font-bold mb-2">Required photos (all 6):</p>
              <ul className="list-disc list-inside space-y-1 text-blue-900">
                <li><strong>Front, Back, Left, Right</strong> — all 4 sides of the machine</li>
                <li><strong>Odometer</strong> — current km reading</li>
                <li><strong>Hour meter</strong> — current engine hours</li>
              </ul>
            </div>

            {/* Structured per-type capture slots */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="checkout-photo-slots">
              {[
                { type: 'front', label: 'Front' },
                { type: 'back', label: 'Back' },
                { type: 'left', label: 'Left Side' },
                { type: 'right', label: 'Right Side' },
                { type: 'odometer', label: 'Odometer' },
                { type: 'hour_meter', label: 'Hour Meter' }
              ].map(slot => {
                const existing = checkoutData.condition_photos.find(p => p.photo_type === slot.type);
                return (
                  <div key={slot.type} className={`border-2 rounded-lg p-2 ${existing ? 'border-green-400 bg-green-50' : 'border-dashed border-slate-300'}`} data-testid={`photo-slot-${slot.type}`}>
                    <p className="text-xs font-bold uppercase mb-1">{slot.label}</p>
                    {existing ? (
                      <div className="relative">
                        <img src={existing.photo_url} alt={slot.label} className="w-full h-24 object-cover rounded" />
                        <button
                          type="button"
                          onClick={() => setCheckoutData(prev => ({
                            ...prev,
                            condition_photos: prev.condition_photos.filter(p => p.photo_type !== slot.type)
                          }))}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ) : (
                      <input
                        type="file"
                        accept="image/*"
                        capture="environment"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          const reader = new FileReader();
                          reader.onload = () => {
                            setCheckoutData(prev => ({
                              ...prev,
                              condition_photos: [...prev.condition_photos.filter(p => p.photo_type !== slot.type), { photo_url: reader.result, description: slot.label, photo_type: slot.type }]
                            }));
                          };
                          reader.readAsDataURL(file);
                        }}
                        className="text-xs w-full"
                        data-testid={`photo-input-${slot.type}`}
                      />
                    )}
                  </div>
                );
              })}
            </div>

            {/* Extra free-form condition photos */}
            <div className="mt-4">
              <label className="text-xs font-bold uppercase text-slate-600 block mb-1">Extra condition photos (optional)</label>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => handlePhotoUpload(e, 'checkout')}
                className="mb-3 text-xs"
              />
              <div className="grid grid-cols-4 gap-2">
                {checkoutData.condition_photos.filter(p => !['front','back','left','right','odometer','hour_meter'].includes(p.photo_type)).map((photo, idx) => (
                  <div key={idx} className="relative">
                    <img src={photo.photo_url} alt={`Photo ${idx+1}`} className="w-full h-20 object-cover rounded" />
                    <button
                      type="button"
                      onClick={() => setCheckoutData(prev => ({
                        ...prev,
                        condition_photos: prev.condition_photos.filter((_, i) => {
                          const freeForm = prev.condition_photos.filter(p => !['front','back','left','right','odometer','hour_meter'].includes(p.photo_type));
                          return prev.condition_photos[i] !== freeForm[idx];
                        })
                      }))}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {(() => {
              const required = ['front','back','left','right','odometer','hour_meter'];
              const done = required.filter(t => checkoutData.condition_photos.some(p => p.photo_type === t)).length;
              return (
                <p className={`text-sm mt-3 font-bold ${done === 6 ? 'text-green-600' : 'text-red-600'}`} data-testid="checkout-photo-progress">
                  {done}/6 required photos captured {done === 6 && '✓'}
                </p>
              );
            })()}
          </Card>

          <Card className="bg-slate-50">
            <h4 className="font-bold uppercase text-sm mb-3">Insurance & Compliance</h4>
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="insuranceVerified"
                checked={checkoutData.insurance_verified}
                onChange={(e) => setCheckoutData({...checkoutData, insurance_verified: e.target.checked})}
                className="w-5 h-5"
              />
              <label htmlFor="insuranceVerified" className="font-medium">Insurance certificate received and verified by office</label>
            </div>
          </Card>

          {/* Fee Notice Card */}
          <Card className="bg-red-50 border-red-300">
            <h4 className="font-bold uppercase text-sm mb-3 text-red-800 flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              Important Fee Notice
            </h4>
            <div className="space-y-3 text-sm">
              <div className="flex items-start gap-3 p-2 bg-white rounded border border-red-200">
                <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-red-600 font-bold text-xs">$</span>
                </div>
                <div>
                  <p className="font-bold text-red-800">$200 Cleaning Fee</p>
                  <p className="text-slate-600">A $200 cleaning fee will be charged if the machine is returned in a messy or unclean condition.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-2 bg-white rounded border border-red-200">
                <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-red-600 font-bold text-xs">$</span>
                </div>
                <div>
                  <p className="font-bold text-red-800">Repainting Fee</p>
                  <p className="text-slate-600">If paintwork is badly damaged, a repainting fee will be charged based on the extent of damage.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-2 bg-white rounded border border-red-200">
                <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-red-600 font-bold text-xs">!</span>
                </div>
                <div>
                  <p className="font-bold text-red-800">Damage Liability</p>
                  <p className="text-slate-600">Any damage noticed upon return that is NOT documented in the checkout photos above will be the responsibility of the renter to pay for repairs.</p>
                </div>
              </div>
            </div>
          </Card>

          <Card className="bg-slate-50">
            <h4 className="font-bold uppercase text-sm mb-3">Terms Acceptance</h4>
            <div className="space-y-3">
              {[
                { key: 'condition_accepted', label: 'Machine accepted in current condition as documented in photos above' },
                { key: 'return_same_condition', label: 'Will return in same clean condition including fuel/charge level' },
                { key: 'cleaning_fee_accepted', label: 'I understand a $200 cleaning fee applies if machine is returned messy' },
                { key: 'paint_damage_accepted', label: 'I understand repainting fees apply if paintwork is badly damaged' },
                { key: 'damage_responsibility', label: 'I accept responsibility for ANY damage not shown in checkout photos' },
                { key: 'maintenance_responsibility', label: 'Responsible for proper maintenance and care during hire period' }
              ].map(term => (
                <div key={term.key} className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={checkoutData[term.key]}
                    onChange={(e) => setCheckoutData({...checkoutData, [term.key]: e.target.checked})}
                    className="w-5 h-5 mt-0.5"
                  />
                  <label className="text-sm">{term.label}</label>
                </div>
              ))}
            </div>
          </Card>

          <div className="grid md:grid-cols-2 gap-6">
            <SignaturePad
              label="Client Signature"
              name={checkoutData.client_name}
              setName={(name) => setCheckoutData({...checkoutData, client_name: name})}
              onSave={(sig, name) => setCheckoutData({...checkoutData, client_signature: sig, client_name: name})}
            />
            <SignaturePad
              label="Employee Signature"
              name={checkoutData.employee_name}
              setName={(name) => setCheckoutData({...checkoutData, employee_name: name})}
              onSave={(sig, name) => setCheckoutData({...checkoutData, employee_signature: sig, employee_name: name})}
            />
          </div>

          <div className="flex gap-3">
            <Button onClick={handleCheckout} disabled={loading}>
              {loading ? <Spinner size="sm" /> : 'Complete Checkout'}
            </Button>
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
          </div>
        </div>
      )}

      {/* RETURN STEP */}
      {hire && step === 'return' && (
        <div className="space-y-6">
          <Card className="bg-blue-50 border-blue-200">
            <h4 className="font-bold uppercase text-sm mb-3">Return Details</h4>
            <div className="grid md:grid-cols-3 gap-4">
              <Input
                label="Return Date *"
                type="date"
                value={returnData.return_date}
                onChange={(e) => setReturnData({...returnData, return_date: e.target.value})}
              />
              <Input
                label="Return Time"
                type="time"
                value={returnData.return_time}
                onChange={(e) => setReturnData({...returnData, return_time: e.target.value})}
              />
              <Select
                label="Condition"
                value={returnData.return_condition}
                onChange={(e) => setReturnData({...returnData, return_condition: e.target.value})}
                options={[
                  { value: 'good', label: 'Good' },
                  { value: 'minor_issues', label: 'Minor Issues' },
                  { value: 'damage_found', label: 'Damage Found' }
                ]}
              />
            </div>
            <div className="grid md:grid-cols-3 gap-4 mt-4">
              <Input
                label="Hours Reading"
                type="number"
                value={returnData.hours_in}
                onChange={(e) => setReturnData({...returnData, hours_in: e.target.value})}
                placeholder={`Out: ${hire.hours_out || 'N/A'}`}
              />
              <Input
                label="KM Reading"
                type="number"
                value={returnData.km_in}
                onChange={(e) => setReturnData({...returnData, km_in: e.target.value})}
                placeholder={`Out: ${hire.km_out || 'N/A'}`}
              />
              <Input
                label="Fuel Level (%)"
                type="number"
                min="0"
                max="100"
                value={returnData.fuel_level_in}
                onChange={(e) => setReturnData({...returnData, fuel_level_in: e.target.value})}
                placeholder={`Out: ${hire.fuel_level_out}%`}
              />
            </div>
          </Card>

          {/* Checkout Photos Reference */}
          {hire.condition_photos_out && hire.condition_photos_out.length > 0 && (
            <Card className="bg-blue-50 border-blue-200">
              <h4 className="font-bold uppercase text-sm mb-3 flex items-center gap-2">
                <Camera className="w-4 h-4" />
                Original Checkout Photos (Reference)
              </h4>
              <p className="text-sm text-slate-600 mb-3">
                Compare against these photos to identify any new damage or cleanliness issues.
              </p>
              <div className="grid grid-cols-4 gap-2">
                {hire.condition_photos_out.map((photo, idx) => (
                  <img key={idx} src={photo.photo_url} alt={`Checkout ${idx+1}`} className="w-full h-20 object-cover rounded border-2 border-blue-300" />
                ))}
              </div>
            </Card>
          )}

          {/* Return Photos */}
          <Card className="bg-orange-50 border-orange-200">
            <h4 className="font-bold uppercase text-sm mb-2">Return Condition Photos (All 6 Required)</h4>
            <p className="text-sm text-slate-600 mb-3">
              Match the checkout photos angle-for-angle. Document any damage, dirt, or changes in condition.
            </p>

            {/* Structured per-type return capture */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="return-photo-slots">
              {[
                { type: 'front', label: 'Front' },
                { type: 'back', label: 'Back' },
                { type: 'left', label: 'Left Side' },
                { type: 'right', label: 'Right Side' },
                { type: 'odometer', label: 'Odometer' },
                { type: 'hour_meter', label: 'Hour Meter' }
              ].map(slot => {
                const existing = returnData.return_photos.find(p => p.photo_type === slot.type);
                return (
                  <div key={slot.type} className={`border-2 rounded-lg p-2 ${existing ? 'border-green-400 bg-green-50' : 'border-dashed border-slate-300'}`} data-testid={`return-photo-slot-${slot.type}`}>
                    <p className="text-xs font-bold uppercase mb-1">{slot.label}</p>
                    {existing ? (
                      <div className="relative">
                        <img src={existing.photo_url} alt={slot.label} className="w-full h-24 object-cover rounded" />
                        <button
                          type="button"
                          onClick={() => setReturnData(prev => ({
                            ...prev,
                            return_photos: prev.return_photos.filter(p => p.photo_type !== slot.type)
                          }))}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ) : (
                      <input
                        type="file"
                        accept="image/*"
                        capture="environment"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          const reader = new FileReader();
                          reader.onload = () => {
                            setReturnData(prev => ({
                              ...prev,
                              return_photos: [...prev.return_photos.filter(p => p.photo_type !== slot.type), { photo_url: reader.result, description: slot.label, photo_type: slot.type }]
                            }));
                          };
                          reader.readAsDataURL(file);
                        }}
                        className="text-xs w-full"
                        data-testid={`return-photo-input-${slot.type}`}
                      />
                    )}
                  </div>
                );
              })}
            </div>

            {/* Extra free-form return photos */}
            <div className="mt-4">
              <label className="text-xs font-bold uppercase text-slate-600 block mb-1">Extra photos (damage, interior, etc.)</label>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => handlePhotoUpload(e, 'return')}
                className="mb-3 text-xs"
              />
              <div className="grid grid-cols-4 gap-2">
                {returnData.return_photos.filter(p => !['front','back','left','right','odometer','hour_meter'].includes(p.photo_type)).map((photo, idx) => (
                  <div key={idx} className="relative">
                    <img src={photo.photo_url} alt={`Photo ${idx+1}`} className="w-full h-20 object-cover rounded" />
                    <button
                      type="button"
                      onClick={() => setReturnData(prev => {
                        const freeForm = prev.return_photos.filter(p => !['front','back','left','right','odometer','hour_meter'].includes(p.photo_type));
                        return { ...prev, return_photos: prev.return_photos.filter(p => p !== freeForm[idx]) };
                      })}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {(() => {
              const required = ['front','back','left','right','odometer','hour_meter'];
              const done = required.filter(t => returnData.return_photos.some(p => p.photo_type === t)).length;
              return (
                <p className={`text-sm mt-3 font-bold ${done === 6 ? 'text-green-600' : 'text-red-600'}`} data-testid="return-photo-progress">
                  {done}/6 required photos captured {done === 6 && '✓'}
                </p>
              );
            })()}
          </Card>

          {/* Cleanliness Assessment */}
          <Card className="bg-amber-50 border-amber-200">
            <h4 className="font-bold uppercase text-sm mb-3">Cleanliness Assessment</h4>
            <Select
              label="Machine Cleanliness"
              value={returnData.cleanliness_condition || 'clean'}
              onChange={(e) => setReturnData({...returnData, cleanliness_condition: e.target.value})}
              options={[
                { value: 'clean', label: 'Clean - No cleaning required' },
                { value: 'minor_dirt', label: 'Minor Dirt - Light cleaning needed' },
                { value: 'dirty', label: 'Dirty - Requires thorough cleaning' },
                { value: 'very_dirty', label: 'Very Dirty - Excessive mess ($200 cleaning fee applies)' }
              ]}
            />
            {(returnData.cleanliness_condition === 'dirty' || returnData.cleanliness_condition === 'very_dirty') && (
              <div className="mt-3 p-3 bg-red-100 rounded-lg border border-red-300">
                <p className="text-sm text-red-800 font-semibold flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  {returnData.cleanliness_condition === 'very_dirty' 
                    ? '$200 cleaning fee will be applied' 
                    : 'Cleaning charges may apply'}
                </p>
              </div>
            )}
          </Card>

          {/* Damage Section */}
          <Card className="bg-red-50 border-red-200">
            <div className="flex items-center gap-3 mb-4">
              <input
                type="checkbox"
                id="damageFound"
                checked={returnData.new_damage_found}
                onChange={(e) => setReturnData({...returnData, new_damage_found: e.target.checked})}
                className="w-5 h-5"
              />
              <label htmlFor="damageFound" className="font-bold uppercase text-sm text-red-800">New Damage Found?</label>
            </div>
            {returnData.new_damage_found && (
              <div className="space-y-4">
                <Textarea
                  label="Damage Description"
                  value={returnData.damage_description}
                  onChange={(e) => setReturnData({...returnData, damage_description: e.target.value})}
                  placeholder="Describe the damage..."
                />
                <div>
                  <label className="text-sm font-bold uppercase text-slate-600 mb-2 block">Damage Photos</label>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) => handlePhotoUpload(e, 'damage')}
                  />
                  <div className="grid grid-cols-4 gap-2 mt-2">
                    {returnData.damage_photos.map((photo, idx) => (
                      <img key={idx} src={photo.photo_url} alt={`Damage ${idx+1}`} className="w-full h-20 object-cover rounded" />
                    ))}
                  </div>
                </div>
                <Input
                  label="Estimated Repair Cost ($)"
                  type="number"
                  value={returnData.estimated_repair_cost}
                  onChange={(e) => setReturnData({...returnData, estimated_repair_cost: e.target.value})}
                />
              </div>
            )}
          </Card>

          {/* Charges */}
          <Card className="bg-amber-50 border-amber-200">
            <h4 className="font-bold uppercase text-sm mb-3">Additional Charges</h4>
            {returnData.cleanliness_condition === 'very_dirty' && (
              <div className="mb-4 p-3 bg-red-100 rounded-lg border border-red-300">
                <p className="text-sm text-red-800 font-semibold">
                  $200 cleaning fee auto-applied due to machine being returned very dirty
                </p>
              </div>
            )}
            <div className="grid md:grid-cols-4 gap-4">
              <Input
                label="Fuel Top-up ($)"
                type="number"
                value={returnData.fuel_charge}
                onChange={(e) => setReturnData({...returnData, fuel_charge: e.target.value})}
              />
              <Input
                label="Cleaning ($)"
                type="number"
                value={returnData.cleanliness_condition === 'very_dirty' ? 200 : returnData.cleaning_charge}
                onChange={(e) => setReturnData({...returnData, cleaning_charge: e.target.value})}
                className={returnData.cleanliness_condition === 'very_dirty' ? 'bg-red-50' : ''}
              />
              <Input
                label="Damage/Repairs ($)"
                type="number"
                value={returnData.damage_charge}
                onChange={(e) => setReturnData({...returnData, damage_charge: e.target.value})}
              />
              <Input
                label="Repainting ($)"
                type="number"
                value={returnData.other_charges}
                onChange={(e) => setReturnData({...returnData, other_charges: e.target.value})}
                placeholder="If paint damaged"
              />
            </div>
            <Textarea
              label="Charge Notes"
              value={returnData.charge_notes}
              onChange={(e) => setReturnData({...returnData, charge_notes: e.target.value})}
              placeholder="Explain any charges (e.g., fuel top-up, cleaning, damage repairs, repainting)..."
              className="mt-4"
            />
            {/* Total Charges Summary */}
            <div className="mt-4 p-3 bg-white rounded-lg border">
              <div className="flex justify-between items-center">
                <span className="font-semibold">Total Additional Charges:</span>
                <span className="text-xl font-bold text-red-600">
                  ${(
                    parseFloat(returnData.fuel_charge || 0) +
                    parseFloat(returnData.cleanliness_condition === 'very_dirty' ? 200 : (returnData.cleaning_charge || 0)) +
                    parseFloat(returnData.damage_charge || 0) +
                    parseFloat(returnData.other_charges || 0)
                  ).toFixed(2)}
                </span>
              </div>
            </div>
          </Card>

          <Card className="bg-slate-50">
            <div className="flex items-center gap-3 mb-4">
              <input
                type="checkbox"
                id="returnAcceptable"
                checked={returnData.returned_acceptable}
                onChange={(e) => setReturnData({...returnData, returned_acceptable: e.target.checked})}
                className="w-5 h-5"
              />
              <label htmlFor="returnAcceptable" className="font-bold">Machine returned in acceptable condition</label>
            </div>
            <Textarea
              label="Return Notes"
              value={returnData.return_notes}
              onChange={(e) => setReturnData({...returnData, return_notes: e.target.value})}
              placeholder="Any additional notes..."
            />
          </Card>

          <div className="grid md:grid-cols-2 gap-6">
            <SignaturePad
              label="Client Signature"
              name={returnData.client_name}
              setName={(name) => setReturnData({...returnData, client_name: name})}
              onSave={(sig, name) => setReturnData({...returnData, client_signature: sig, client_name: name})}
            />
            <SignaturePad
              label="Employee Signature"
              name={returnData.employee_name}
              setName={(name) => setReturnData({...returnData, employee_name: name})}
              onSave={(sig, name) => setReturnData({...returnData, employee_signature: sig, employee_name: name})}
            />
          </div>

          <div className="flex gap-3">
            <Button onClick={handleReturn} disabled={loading}>
              {loading ? <Spinner size="sm" /> : 'Complete Return'}
            </Button>
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
          </div>
        </div>
      )}
    </Modal>
  );
};

// ==================== MACHINES PAGE ====================


// ==================== JOB UPLOADS COMPONENT ====================

// Protected Route
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  // Mount the geofence auto-prompt for any logged-in user
  useGeofenceAutoPrompt({ user, enabled: !!user });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Super admins: only force-redirect to /super-admin when no company is being viewed.
  // If they've picked a company to inspect (X-Company-Id set), let them browse anywhere.
  if (user.role === "super_admin") {
    const viewingCompanyId = localStorage.getItem("currentCompanyId");
    if (!viewingCompanyId && !window.location.pathname.startsWith("/super-admin")) {
      return <Navigate to="/super-admin" replace />;
    }
    // skip the allowedRoles check for super_admin — they can view anything
    return (
      <Layout>
        {children}
        <MissedSignoffsPromptModal user={user} />
      </Layout>
    );
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <Layout>
      {children}
      <MissedSignoffsPromptModal user={user} />
    </Layout>
  );
};

// Main App
function App() {
  return (
    <AuthProvider>
      <Toaster position="top-right" richColors closeButton />
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
          <Route path="/jobs" element={<ProtectedRoute><JobsPage /></ProtectedRoute>} />
          <Route path="/clients" element={<ProtectedRoute allowedRoles={["admin", "manager", "office_staff"]}><ClientsPage /></ProtectedRoute>} />
          <Route path="/invoicing" element={<ProtectedRoute allowedRoles={["admin", "office_staff"]}><InvoicingPage /></ProtectedRoute>} />
          <Route path="/hire-out" element={<ProtectedRoute><HireOutPage /></ProtectedRoute>} />
          <Route path="/machines" element={<ProtectedRoute><MachinesPage /></ProtectedRoute>} />
          <Route path="/inventory" element={<ProtectedRoute><InventoryPage /></ProtectedRoute>} />
          <Route path="/time" element={<ProtectedRoute><TimePage /></ProtectedRoute>} />
          <Route path="/timesheets" element={<ProtectedRoute><TimesheetsPage /></ProtectedRoute>} />
          <Route path="/my-records" element={<ProtectedRoute><MyRecordsPage /></ProtectedRoute>} />
          <Route path="/time-alterations" element={<Navigate to="/analytics" replace />} />
          <Route path="/site-attendance" element={<Navigate to="/analytics" replace />} />
          <Route path="/forms" element={<ProtectedRoute><FormsPage /></ProtectedRoute>} />
          <Route path="/daybook" element={<ProtectedRoute><DaybookPage /></ProtectedRoute>} />
          <Route path="/messages" element={<ProtectedRoute><MessagesPage /></ProtectedRoute>} />
          <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
          <Route path="/records" element={<ProtectedRoute allowedRoles={["admin", "manager", "office_staff", "supervisor"]}><RecordsPage /></ProtectedRoute>} />
          <Route path="/employees" element={<ProtectedRoute><EmployeesPage /></ProtectedRoute>} />
          <Route path="/analytics" element={<ProtectedRoute allowedRoles={["admin", "manager", "office_staff", "supervisor"]}><AnalyticsPage /></ProtectedRoute>} />
          <Route path="/job-hours" element={<Navigate to="/analytics" replace />} />
          <Route path="/companies" element={<ProtectedRoute allowedRoles={["admin"]}><CompaniesPage /></ProtectedRoute>} />
          <Route path="/super-admin" element={<ProtectedRoute allowedRoles={["super_admin"]}><SuperAdminPage /></ProtectedRoute>} />
          <Route path="/assistant" element={<ProtectedRoute allowedRoles={["super_admin"]}><AssistantPage /></ProtectedRoute>} />
          <Route path="/tutorials-admin" element={<ProtectedRoute allowedRoles={["super_admin"]}><TutorialsAdminPage /></ProtectedRoute>} />
          <Route path="/qr-codes" element={<ProtectedRoute><QRCodesPage /></ProtectedRoute>} />
          <Route path="/tutorials" element={<ProtectedRoute><WorkerTutorialsPage /></ProtectedRoute>} />
          <Route path="/qr/:token" element={<QRResolverPage />} />
          <Route path="/preview/:token" element={<ClientPreviewPage />} />
          <Route path="/preview/:token/success" element={<ClientPreviewPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/jobs/:jobId/build" element={<ProtectedRoute><BuildWorkflowPage /></ProtectedRoute>} />
          <Route path="/shift-production" element={<ProtectedRoute><ShiftProductionPage /></ProtectedRoute>} />
          <Route path="/shift-equipment" element={<ProtectedRoute><ShiftEquipmentPage /></ProtectedRoute>} />
          <Route path="/fuel-pods" element={<ProtectedRoute><FuelPodsPage /></ProtectedRoute>} />
          <Route path="/incident-log" element={<ProtectedRoute><IncidentLogPage /></ProtectedRoute>} />
          <Route path="/employee-records" element={<ProtectedRoute allowedRoles={["admin", "manager", "office_staff"]}><EmployeeRecordsPage /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute allowedRoles={["admin"]}><SettingsPage /></ProtectedRoute>} />
          <Route path="/reports" element={<Navigate to="/analytics" replace />} />
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
